
import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Agent, Warehouse, Product, Invoice, MatchInvoice, ProductInvoice, User, UserPermissions, UserRole, RoleAppearance, RoleAppearances, Transfer, InvoiceItem, Notification, TransferItem, Sale, Attendance, LeaveRequest, CommissionWithdrawal, DeferredPoint, FlexibleOffer, OfferMember, DashboardDetail, Expense, ExpenseCategory, Feedback, DailyClosing, ActivityLog, MemberPoint, CompanyLetter } from '../types';
import { supabase } from '../supabaseClient';
import { normalizeImageUrl } from '../src/utils/imageUtils';

interface WarehouseLock {
  warehouse_id: string;
  user_id: string;
  user_name: string;
  created_at: string;
}

interface Toast {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface StoreContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  users: User[]; 
  loading: boolean;
  isSyncing: boolean; 
  isProcessing: boolean;
  setIsProcessing: (is: boolean) => void;
  toast: Toast | null;
  notifications: Notification[];
  warehouseLocks: WarehouseLock[];
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  addNotification: (title: string, message: string, type: Notification['type'], invoiceId?: string) => void;
  clearNotifications: () => void;
  markNotificationAsRead: (id: string) => void;
  markChatNotificationsAsRead: (senderNameOrUserId?: string) => Promise<void>;
  sendNotificationToUser: (targetUserId: string, title: string, message: string, type: Notification['type'], invoiceId?: string) => Promise<void>;
  sendNotificationToRoleOrWarehouse: (filter: { role?: UserRole[]; warehouseId?: string }, title: string, message: string, type: Notification['type'], invoiceId?: string) => Promise<void>;
  stopAlarm: () => void;
  viewInvoiceId: string | null;
  setViewInvoiceId: (id: string | null) => void;
  welcomeUser: { name: string } | null;
  setWelcomeUser: (user: { name: string } | null) => void;
  selectedDetail: DashboardDetail | null;
  setSelectedDetail: (detail: DashboardDetail | null) => void;
  login: (email: string, password: string, skipSetUser?: boolean, rememberMe?: boolean) => Promise<{ success: boolean; user?: User; error?: string }>; 
  faceLogin: (descriptor: number[]) => Promise<{ success: boolean; user?: User; error?: string }>;
  logout: () => void;
  addUser: (user: User) => Promise<void>;
  updateUser: (user: User) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  agents: Agent[];
  warehouses: Warehouse[];
  products: Product[];
  invoices: Invoice[];
  ordersWarehouses: Invoice[];
  approvedInvoices: Invoice[]; 
  matchInvoices: MatchInvoice[]; 
  productInvoices: ProductInvoice[];
  transfers: Transfer[];
  sales: Sale[];
  attendance: Attendance[];
  leaveRequests: LeaveRequest[];
  offerMembers: OfferMember[];
  commissionWithdrawals: CommissionWithdrawal[];
  deferredPoints: DeferredPoint[];
  flexibleOffers: FlexibleOffer[];
  expenses: Expense[];
  dailyClosings: DailyClosing[];
  feedbacks: Feedback[];
  activityLogs: ActivityLog[];
  memberPoints: MemberPoint[];
  companyLetters: CompanyLetter[];
  introVideo: string | null;
  systemSettings: Record<string, any>;
  updateSystemSetting: (key: string, value: any, silent?: boolean) => Promise<void>;
  roleAppearances: RoleAppearances;
  addAgent: (agent: Agent) => Promise<void>;
  updateAgent: (agent: Agent) => Promise<void>;
  deleteAgent: (id: string) => Promise<void>; 
  addWarehouse: (warehouse: Warehouse) => Promise<void>;
  updateWarehouse: (warehouse: Warehouse) => Promise<void>; 
  deleteWarehouse: (id: string) => Promise<void>;
  addProduct: (product: Product) => Promise<Product | null>;
  addProductsBatch: (products: Product[]) => Promise<Product[] | null>;
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  addInvoice: (invoice: Invoice) => Promise<void>;
  shipInvoice: (id: string) => Promise<{ success: boolean; error?: string }>; 
  receiveInvoice: (id: string) => Promise<{ success: boolean; error?: string }>;
  cancelInvoice: (id: string) => Promise<{ success: boolean; error?: string }>;
  updateInvoice: (id: string, updatedItems: InvoiceItem[]) => Promise<void>; 
  deleteInvoice: (id: string) => Promise<void>;
  addApprovedInvoice: (invoice: Invoice) => Promise<void>; 
  addMatchInvoice: (invoice: MatchInvoice) => Promise<void>; 
  addProductInvoice: (invoice: ProductInvoice) => Promise<void>;
  addTransfer: (transfer: Transfer) => Promise<void>;
  updateTransfer: (id: string, transfer: Transfer) => Promise<void>;
  approveTransfer: (id: string) => Promise<void>;
  deleteTransfer: (id: string) => Promise<void>;
  addSale: (sale: Sale) => Promise<void>;
  deleteSale: (id: string) => Promise<void>;
  requestCancellation: (id: string, sn: string, reason: string) => Promise<void>;
  confirmCancellation: (id: string) => Promise<void>;
  addAttendance: (attendance: Attendance) => Promise<void>;
  updateAttendance: (attendance: Attendance) => Promise<void>;
  deleteAttendance: (id: string) => Promise<void>;
  addLeaveRequest: (request: LeaveRequest) => Promise<void>;
  updateLeaveRequest: (request: LeaveRequest) => Promise<void>;
  deleteLeaveRequest: (id: string) => Promise<void>;
  registerFace: (userId: string, faceDescriptor: number[], faceImage?: string) => Promise<{ success: boolean; error?: string }>;
  addCommissionWithdrawal: (withdrawal: CommissionWithdrawal) => Promise<void>;
  updateCommissionWithdrawal: (withdrawal: CommissionWithdrawal) => Promise<void>;
  addDeferredPoint: (point: DeferredPoint) => Promise<void>;
  updateDeferredPoint: (point: DeferredPoint) => Promise<void>;
  addFlexibleOffer: (offer: FlexibleOffer) => Promise<void>;
  updateFlexibleOffer: (offer: FlexibleOffer) => Promise<void>;
  deleteFlexibleOffer: (id: string) => Promise<void>;
  addOfferMember: (member: OfferMember) => Promise<void>;
  updateOfferMember: (member: OfferMember) => Promise<void>;
  deleteOfferMember: (id: string) => Promise<void>;
  addExpense: (expense: Expense) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  addDailyClosing: (closing: DailyClosing) => Promise<void>;
  deleteDailyClosing: (id: string) => Promise<void>;
  addFeedback: (feedback: Feedback) => Promise<void>;
  deleteFeedback: (id: string) => Promise<void>;
  addCompanyLetter: (letter: CompanyLetter) => Promise<void>;
  deleteCompanyLetter: (id: string) => Promise<void>;
  updateExpenseStatus: (id: string, status: 'approved' | 'rejected') => Promise<void>;
  updateSaleStatus: (id: string, status: 'approved' | 'rejected') => Promise<void>;
  convertDeferredToCash: (saleId: string, conversionItems: { index: number; quantity: number }[], memberInfo: { id?: string; name: string; membershipNumber: string; phone?: string }) => Promise<string | null>;
  uploadMemberAuthImage: (file: File) => Promise<{ success: boolean; url?: string; error?: string }>;
  toggleFlexibleOffer: (saleId: string, isFlexible: boolean) => Promise<void>;
  updateRoleAppearance: (role: UserRole, appearance: RoleAppearance) => void;
  deleteProductInvoice: (id: string) => Promise<void>;
  deleteMatchInvoice: (id: string) => Promise<void>;
  deleteApprovedInvoice: (id: string) => Promise<void>;
  uploadProductImage: (file: File) => Promise<{ success: boolean; url?: string; error?: string }>;
  uploadIntroVideo: (file: File) => Promise<{ success: boolean; error?: string }>;
  deleteIntroVideo: () => Promise<{ success: boolean; error?: string }>;
  updateExchangeRate: (rate: number, warehouseId?: string) => Promise<void>;
  getExchangeRate: (centerId?: string) => number;
  uploadUserAsset: (file: File, type: 'profile' | 'id_front' | 'id_back') => Promise<{ success: boolean; url?: string; error?: string }>;
  uploadSystemAsset: (file: File) => Promise<{ success: boolean; url?: string; error?: string }>;
  acquireWarehouseLock: (warehouseId: string, moduleName?: string) => Promise<{ success: boolean; error?: string }>;
  releaseWarehouseLock: (warehouseId: string) => Promise<void>;
  forceReleaseLock: (warehouseId: string) => Promise<void>;
  refreshData: () => Promise<void>;
  fetchData: (silent?: boolean, tableToFetch?: string) => Promise<void>;
  printHTML: (htmlContent: string) => void;
  generateInvoiceId: (type: string) => Promise<string>;
  stockPredictions: any[];
  refreshStockPredictions: () => Promise<void>;
  isChatOpen: boolean;
  setIsChatOpen: (open: boolean) => void;
  activeChatUserId: string | null;
  setActiveChatUserId: (id: string | null) => void;
  unreadChatCount: number;
  setUnreadChatCount: (count: number) => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const EMPTY_PERMISSIONS: UserPermissions = {
  viewDashboard: false, viewIntroVideo: false,
  viewUsers: false, addUser: false, editUser: false, deleteUser: false,
  viewAgents: false, addAgent: false, editAgent: false, deleteAgent: false,
  viewWarehouses: false, addWarehouse: false, editWarehouse: false, deleteWarehouse: false,
  viewProducts: false, addProduct: false, editProduct: false, editProductFinance: false, deleteProduct: false, deleteProductInvoice: false,
  viewOrders: false, addOrder: false, editOrder: false, deleteOrder: false, approveOrders: false, confirmReceipt: false, cancelOrder: false,
  viewAllWarehouses: false, createApprovedInvoice: false, viewApprovedInvoices: false, deleteApprovedInvoice: false,
  viewSystemMatch: false, saveSystemMatch: false, viewMatchInvoices: false, deleteMatchInvoice: false,
  viewTransfers: false, addTransfer: false, approveTransfer: false, deleteTransfer: false,
  viewCentersSystem: false, viewUserManagement: false, viewInternalTransfers: false, viewCenterInterface: false, viewCenterUsers: false, viewDistinguishedUsers: false,
  viewOffers: false, addOffer: false, editOffer: false, deleteOffer: false,
  viewEmployees: false, viewAttendance: false, editAttendance: false, deleteAttendance: false,
  viewCommissions: false, addCommission: false, editCommission: false, deleteCommission: false,
  viewPoints: false, addPoint: false, editPoint: false, deletePoint: false,
  manageSettings: false, viewReports: false, viewComplaints: false, addMember: false, editMember: false, deleteMember: false,
  convertDeferred: false, viewLeaveRequests: false, manageLeaveRequests: false, viewActivityLog: false, viewMemberStatement: false,
  viewCommissionDashboard: false, viewStockForecasting: false, addEmployee: false, editEmployee: false, deleteEmployee: false,
  manageDistinguishedUsers: false, manageCentersSystem: false, viewCenterDashboard: false, viewCentersDashboard: false, viewWarehousesDashboard: false,
  viewAccounts: false, addExpense: false, deleteExpense: false, manageBackdatedEntries: false, manageExchangeRate: false,
  canExportExcel: false, canPrintReports: true, canManagePermissions: false, canResetPasswords: false,
  viewPos: false, viewInvoicesCash: false, viewInvoicesOffer: false, viewInvoicesGift: false, viewInvoicesFlexible: false, viewInvoicesDeferred: false,
  viewInvoicesDebt: false, viewInvoicesCancelled: false, addFlexibleSale: false, addDeferredPoint: false, addDebtSale: false, addGiftSale: false,
  applyDiscount: false, deleteSale: false, viewDailyReconciliation: false, canOverrideExchangeRate: false, withdrawCommissions: false,
  withdrawPoints: false, viewDetailedExpenses: false, viewGlobalFinancials: false, viewMemberPrivateDetails: false, adjustMemberPoints: false,
  viewAccountsDashboard: false, deleteComplaint: false, viewWarehouseLocations: false, viewCancelledSales: false, viewUnifiedSales: false,
  viewDailyClosings: false, viewTransferAdd: false, viewMatchAdd: false, viewProductAdd: false, viewOrderAdd: false,
  captureAttendance: false, viewCompanyLetters: false, manageCompanyLetters: false
};

export const BASE_POS_PERMISSIONS: UserPermissions = {
  ...EMPTY_PERMISSIONS,
  viewCenterInterface: true,
  viewPos: true,
  viewInvoicesCash: true,
  viewInvoicesOffer: true,
  viewInvoicesGift: true,
  viewInvoicesFlexible: true,
  viewInvoicesDeferred: true,
  viewInvoicesDebt: true,
  canPrintReports: true,
  addMember: true,
  viewAttendance: true,
  captureAttendance: true
};

export const FULL_PERMISSIONS: UserPermissions = {
  viewDashboard: true, viewIntroVideo: true,
  viewUsers: true, addUser: true, editUser: true, deleteUser: true,
  viewAgents: true, addAgent: true, editAgent: true, deleteAgent: true,
  viewWarehouses: true, addWarehouse: true, editWarehouse: true, deleteWarehouse: true,
  viewProducts: true, addProduct: true, editProduct: true, editProductFinance: true, deleteProduct: true, deleteProductInvoice: true,
  viewOrders: true, addOrder: true, editOrder: true, deleteOrder: true, approveOrders: true, confirmReceipt: true, cancelOrder: true,
  viewAllWarehouses: true, createApprovedInvoice: true, viewApprovedInvoices: true, deleteApprovedInvoice: true,
  viewSystemMatch: true, saveSystemMatch: true, viewMatchInvoices: true, deleteMatchInvoice: true,
  viewTransfers: true, addTransfer: true, approveTransfer: true, deleteTransfer: true,
  viewCentersSystem: true,
  viewUserManagement: true,
  viewInternalTransfers: true,
  viewCenterInterface: true,
  viewCenterUsers: true,
  viewDistinguishedUsers: true,
  viewOffers: true,
  addOffer: true,
  editOffer: true,
  deleteOffer: true,
  viewEmployees: true,
  viewAttendance: true,
  editAttendance: true,
  deleteAttendance: true,
  viewCommissions: true,
  addCommission: true,
  editCommission: true,
  deleteCommission: true,
  viewPoints: true,
  addPoint: true,
  editPoint: true,
  deletePoint: true,
  manageSettings: true,
  viewReports: true,
  addMember: true,
  editMember: true,
  deleteMember: true,
  convertDeferred: true,
  viewLeaveRequests: true,
  manageLeaveRequests: true,
  viewActivityLog: true,
  viewMemberStatement: true,
  viewCommissionDashboard: true,
  viewStockForecasting: true,
  addEmployee: true,
  editEmployee: true,
  deleteEmployee: true,
  manageDistinguishedUsers: true,
  manageCentersSystem: true,
  viewCenterDashboard: true,
  viewCentersDashboard: true,
  viewWarehousesDashboard: true,
  viewComplaints: true,
  viewAccounts: true,
  addExpense: true,
  deleteExpense: true,
  manageBackdatedEntries: true,
  manageExchangeRate: true,
  canExportExcel: true,
  canPrintReports: true,
  canManagePermissions: true,
  canResetPasswords: true,
  viewPos: true,
  viewInvoicesCash: true,
  viewInvoicesOffer: true,
  viewInvoicesGift: true,
  viewInvoicesFlexible: true,
  viewInvoicesDeferred: true,
  viewInvoicesDebt: true,
  viewInvoicesCancelled: true,
  addFlexibleSale: true,
  addDeferredPoint: true,
  addDebtSale: true,
  addGiftSale: true,
  applyDiscount: true,
  deleteSale: true,
  viewDailyReconciliation: true,
  canOverrideExchangeRate: true,
  withdrawCommissions: true,
  withdrawPoints: true,
  viewDetailedExpenses: true,
  viewGlobalFinancials: true,
  viewMemberPrivateDetails: true,
  adjustMemberPoints: true,
  viewAccountsDashboard: true,
  deleteComplaint: true,
  viewWarehouseLocations: true,
  viewCancelledSales: true,
  viewUnifiedSales: true,
  viewDailyClosings: true,
  viewTransferAdd: true,
  viewMatchAdd: true,
  viewProductAdd: true,
  viewOrderAdd: true,
  captureAttendance: true,
  viewCompanyLetters: true,
  manageCompanyLetters: true
};

export const getEmptyPermissions = (): UserPermissions => {
  const empty = {} as UserPermissions;
  Object.keys(FULL_PERMISSIONS).forEach(key => {
    (empty as any)[key] = false;
  });
  return empty;
};

export const calculatePermissions = (role: UserRole, customPermissions?: any): UserPermissions => {
  let base = getEmptyPermissions();
  
  if (role === 'SUPER_ADMIN') {
    base = { ...FULL_PERMISSIONS };
  } else if (role === 'EDITOR') {
    base = { ...FULL_PERMISSIONS };
    // Managers can't delete users or manage settings by default
    base.deleteUser = false;
    base.manageSettings = false;
    base.canManagePermissions = false;
  } else if (role === 'AGENT') {
    base.viewDashboard = true;
    base.viewIntroVideo = true;
    base.viewProducts = true;
    base.viewOrders = true;
    base.addOrder = true;
    base.confirmReceipt = true;
    base.viewCenterInterface = true;
    base.viewPos = true;
    base.viewInvoicesCash = true;
    base.viewInvoicesOffer = true;
    base.viewInvoicesGift = true;
    base.viewInvoicesFlexible = true;
    base.viewInvoicesDeferred = true;
    base.viewInvoicesDebt = true;
    base.viewCenterUsers = true;
    base.viewOffers = true;
    base.viewAttendance = true;
    base.viewDistinguishedUsers = true;
    base.viewPoints = true;
    base.viewCenterDashboard = true;
    base.viewCentersDashboard = true;
  } else {
    // Default for others (VIEWER) - Expanded for better "Viewer" experience
    base.viewDashboard = true;
    base.viewIntroVideo = true;
    base.viewProducts = true;
    base.viewOrders = true;
    base.viewTransfers = true;
    base.viewWarehouses = true;
    base.viewAgents = true;
    base.viewEmployees = true;
    base.viewAttendance = true;
    base.viewReports = true;
    base.viewComplaints = true;
    base.viewInternalTransfers = true;
    base.viewOffers = true;
  }

  let customParsed = customPermissions;
  if (typeof customParsed === 'string') {
    try {
      customParsed = JSON.parse(customParsed);
    } catch (e) {
      console.warn("Failed to parse custom permissions from DB string:", customParsed, e);
      customParsed = {};
    }
  }

  return { ...base, ...(customParsed || {}) };
};

// Optimized Arabic Normalization with Cache
const arabicCache = new Map<string, string>();
export const normalizeArabic = (text: string) => {
  if (!text) return "";
  const cached = arabicCache.get(text);
  if (cached) return cached;

  const normalized = text.trim()
    .replace(/\s+/g, ' ')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/ء/g, '')
    .replace(/[\u064B-\u0652]/g, "") // Remove harakat (diacritics)
    .toLowerCase();
  
  arabicCache.set(text, normalized);
  if (arabicCache.size > 5000) arabicCache.clear(); // Prevent memory leak
  return normalized;
};

export const getWarehouseColor = (name: string | undefined): { bg: string, text: string, border: string, hex: string } => {
  if (!name) return { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-100', hex: '#64748b' };
  
  const normalized = normalizeArabic(name);
  if (normalized.includes('بغداد')) {
    return { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', hex: '#2563eb' };
  }
  if (normalized.includes('اربيل')) { // normalized has already replaced أ with ا
    return { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100', hex: '#ea580c' };
  }
  return { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-100', hex: '#64748b' };
};

// --- FIX: Robust Date Sorting Helper (String-based for cross-browser consistency) ---
export const normalizeExpiryDate = (dateStr: string | null | undefined): string => {
  if (!dateStr || String(dateStr).trim() === "") return "";
  let d = String(dateStr).trim();
  
  // Clean up any extra spaces or common separators
  d = d.replace(/\//g, '-').replace(/\s+/g, '');
  
  // Handle MM-YYYY
  const mmYyyyMatch = d.match(/^(\d{1,2})-(\d{4})$/);
  if (mmYyyyMatch) {
    const month = mmYyyyMatch[1].padStart(2, '0');
    const year = mmYyyyMatch[2];
    return `${year}-${month}`;
  }

  // Handle YYYY-MM
  const yyyyMmMatch = d.match(/^(\d{4})-(\d{1,2})$/);
  if (yyyyMmMatch) {
    const year = yyyyMmMatch[1];
    const month = yyyyMmMatch[2].padStart(2, '0');
    return `${year}-${month}`;
  }
  
  // Handle full dates YYYY-MM-DD
  if (d.length >= 10 && d.includes('-')) {
    const parts = d.split('-');
    if (parts.length >= 2 && parts[0].length === 4) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}`;
    }
  }

  return d;
};

// Helper for consistent display formatting (MM-YYYY)
export const formatExpiryDate = (dateStr: string | null | undefined): string => {
  if (!dateStr || String(dateStr).trim() === '') return '---';
  const normalized = normalizeExpiryDate(dateStr);
  if (!normalized.includes('-')) return normalized;
  
  const [year, month] = normalized.split('-');
  return `${month}-${year}`;
};

export const getBatchSortValue = (expiryDate: string | null | undefined): string => {
  if (!expiryDate || String(expiryDate).trim() === '') {
    return '9999-12-31'; // Far future for items without expiry
  }
  return normalizeExpiryDate(expiryDate);
};

export const printHTML = (htmlContent: string) => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) return;

  doc.write(htmlContent);
  doc.close();
  
  const triggerPrint = () => {
    if (iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    }
  };

  // Wait for fonts/styles
  setTimeout(triggerPrint, 500);
};

const mapDBToProduct = (p: any): Product => ({
  id: String(p.id || ''),
  warehouseId: String(p.warehouse_id || p.warehouseid || p.warehouseId || ''),
  name: p.name || '',
  code: p.code || '',
  points: Number(p.points || 0),
  priceUSD: Number(p.price_usd || p.priceUSD || 0),
  expiryDate: String(p.expiry || p.expiry_date || p.expiryDate || ''),
  quantity: Number(p.quantity || 0),
  piecesPerCarton: Number(p.pieces_per_carton || p.pieces_carton || p.piecesPerCarton || 1),
  notes: p.notes || '',
  imageUrl: normalizeImageUrl(p.image_url || p.imageUrl),
  type: p.type || 'normal',
  category: p.category || '',
  unit: p.unit || '',
  userId: p.user_id || p.userId || '',
  userName: p.user_name || p.userName || ''
});

const mapProductToDB = (p: any, currentUser?: User | null) => ({
  id: String(p.id || crypto.randomUUID()),
  warehouse_id: String(p.warehouseId || p.warehouse_id || p.warehouseid || ''),
  name: String(p.name || '').trim(),
  code: String(p.code || ''),
  points: Number(p.points || 0),
  price_usd: Number(p.priceUSD || p.price_usd || 0),
  expiry: normalizeExpiryDate(p.expiryDate || p.expiry || p.expiry_date || ''),
  quantity: Number(p.quantity || 0),
  pieces_per_carton: Number(p.piecesPerCarton || p.pieces_per_carton || p.pieces_carton || 1),
  notes: String(p.notes || ''),
  image_url: normalizeImageUrl(p.imageUrl || p.image_url || ''),
  type: p.type || 'normal',
  category: String(p.category || ''),
  unit: String(p.unit || ''),
  user_id: p.userId || p.user_id || currentUser?.id || null,
  user_name: p.userName || p.user_name || currentUser?.name || null
});

// Global audio reference for looping alarm
let globalAlarmAudio: HTMLAudioElement | null = null;

const playNotificationSound = () => {
  try {
    if (!globalAlarmAudio) {
      globalAlarmAudio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
      globalAlarmAudio.loop = true;
      globalAlarmAudio.volume = 0.5;
    }
    globalAlarmAudio.play().catch(e => console.log('Audio play failed (user interaction required)', e));
  } catch (e) {
    console.error("Audio error", e);
  }
};

const stopAlarmSound = () => {
  if (globalAlarmAudio) {
    globalAlarmAudio.pause();
    globalAlarmAudio.currentTime = 0;
  }
};

const mapDBToFeedback = (f: any): Feedback => ({
  id: String(f.id),
  centerId: String(f.center_id),
  centerName: f.center_name || '',
  userId: String(f.user_id),
  userName: f.user_name || '',
  type: f.type || 'complaint',
  customerName: f.customer_name || '',
  customerPhone: f.customer_phone || '',
  details: f.details || '',
  date: f.date || new Date().toISOString()
});

const mapFeedbackToDB = (f: Feedback) => ({
  // id will be generated by Supabase
  center_id: f.centerId,
  center_name: f.centerName,
  user_id: f.userId,
  user_name: f.userName,
  type: f.type,
  customer_name: f.customerName,
  customer_phone: f.customerPhone,
  details: f.details,
  date: f.date
});

const safeParse = (str: string | null | undefined, defaultValue: any = null) => {
  if (!str) return defaultValue;
  try {
    return JSON.parse(str);
  } catch (e) {
    return defaultValue;
  }
};

const mapDBToUser = (profile: any): User => {
  const role = profile.role || 'VIEWER';
  return { 
    id: String(profile.id), 
    name: profile.name || '', 
    email: profile.email || '', 
    password: profile.password, 
    role, 
    permissions: calculatePermissions(role, profile.permissions), 
    agentId: profile.agent_id ? String(profile.agent_id) : undefined, 
    centerId: profile.center_id ? String(profile.center_id) : undefined,
    createdAt: profile.created_at,
    faceDescriptor: safeParse(profile.face_descriptor),
    profileImage: profile.profile_image,
    idCardFront: profile.id_card_front,
    idCardBack: profile.id_card_back,
    phone: profile.phone,
    emergencyContactName: profile.emergency_contact_name,
    emergencyContactPhone: profile.emergency_contact_phone,
    age: profile.age,
    education: profile.education,
    maritalStatus: profile.marital_status,
    childrenCount: profile.children_count,
    currentAddress: profile.current_address,
    previousAddress: profile.previous_address
  };
};

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeChatUserId, setActiveChatUserId] = useState<string | null>(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // --- UTILS ---

  const [isProcessing, setIsProcessing] = useState(false);
  const [stockPredictions, setStockPredictions] = useState<any[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [lowStockNotified, setLowStockNotified] = useState<Set<string>>(new Set());
  const [users, setUsers] = useState<User[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [ordersWarehouses, setOrdersWarehouses] = useState<Invoice[]>([]);
  const [productInvoices, setProductInvoices] = useState<ProductInvoice[]>([]);
  const [matchInvoices, setMatchInvoices] = useState<MatchInvoice[]>([]);
  const [approvedInvoices, setApprovedInvoices] = useState<Invoice[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [offerMembers, setOfferMembers] = useState<OfferMember[]>([]);
  const [commissionWithdrawals, setCommissionWithdrawals] = useState<CommissionWithdrawal[]>([]);
  const [deferredPoints, setDeferredPoints] = useState<DeferredPoint[]>([]);
  const [flexibleOffers, setFlexibleOffers] = useState<FlexibleOffer[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [dailyClosings, setDailyClosings] = useState<DailyClosing[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [memberPoints, setMemberPoints] = useState<MemberPoint[]>([]);
  const [companyLetters, setCompanyLetters] = useState<CompanyLetter[]>([]);

  // Cleanup old attendance photos (Monthly)
  useEffect(() => {
    const cleanupOldPhotos = async () => {
      try {
        const lastCleanupDate = localStorage.getItem('last_photo_cleanup');
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        
        if (lastCleanupDate !== currentMonth) {
          console.log('Running monthly photo cleanup...');
          
          // List all files in storage (ideally we should only list previous months)
          const { data: buckets, error: listError } = await supabase.storage.from('attendance-photos').list('', { limit: 100 });
          
          if (listError) throw listError;
          
          if (buckets && buckets.length > 0) {
            for (const bucket of buckets) {
              // If it's a folder-like object and not the current month
              if (bucket.name !== currentMonth) {
                const { data: files, error: filesError } = await supabase.storage.from('attendance-photos').list(bucket.name);
                if (!filesError && files && files.length > 0) {
                  const paths = files.map(f => `${bucket.name}/${f.name}`);
                  await supabase.storage.from('attendance-photos').remove(paths);
                }
              }
            }
          }
          
          localStorage.setItem('last_photo_cleanup', currentMonth);
        }
      } catch (err) {
        console.error('Photo cleanup error:', err);
      }
    };

    if (currentUser?.role === 'SUPER_ADMIN') {
      cleanupOldPhotos();
    }
  }, [currentUser]);

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [introVideo, setIntroVideo] = useState<string | null>(null);
  const [systemSettings, setSystemSettings] = useState<Record<string, any>>(() => {
    try {
      const cached = localStorage.getItem('system_settings');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    if (Object.keys(systemSettings).length > 0) {
      localStorage.setItem('system_settings', JSON.stringify(systemSettings));
    }
  }, [systemSettings]);

  const [warehouseLocks, setWarehouseLocks] = useState<WarehouseLock[]>([]);
  const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
  const [welcomeUser, setWelcomeUser] = useState<{ name: string } | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<DashboardDetail | null>(null);

  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toastTimeoutRef = useRef<any>(null);
  const lastFetchTimeRef = useRef<number>(0);
  const lastTableFetchTimeRef = useRef<Record<string, number>>({});
  const fetchDebounceTimerRef = useRef<Record<string, any>>({});
  const fetchResolversRef = useRef<Record<string, (() => void)[]>>({});
  const isFetchingRef = useRef<Record<string, boolean>>({});
  const sessionCheckActiveRef = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData(true);
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // event.persisted is true if the page is loaded from cache (e.g. Back button)
      if (event.persisted) {
        fetchData(true);
      }
    };

    const handlePopState = () => fetchData(true);

    // Wake from sleep detection
    let lastTime = Date.now();
    const sleepDetector = setInterval(() => {
      const currentTime = Date.now();
      // If interval took way longer than expected (10s diff for a 2s interval) -> probably device slept
      if (currentTime - lastTime > 6000) {
        console.log("Wake from sleep detected. Refreshing data.");
        fetchData(true);
      }
      lastTime = currentTime;
    }, 2000);

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleVisibilityChange);
    window.addEventListener('popstate', handlePopState);

    return () => {
      clearInterval(sleepDetector);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const [roleAppearances, setRoleAppearances] = useState<RoleAppearances>({
    'SUPER_ADMIN': { label: 'مدير النظام', colorClass: 'bg-purple-100 text-purple-700' },
    'EDITOR': { label: 'مشرف', colorClass: 'bg-blue-100 text-blue-700' },
    'VIEWER': { label: 'مشاهد', colorClass: 'bg-gray-100 text-gray-700' },
    'AGENT': { label: 'وكيل', colorClass: 'bg-emerald-100 text-emerald-700' },
    'EMPLOYEE': { label: 'موظف', colorClass: 'bg-indigo-100 text-indigo-700' },
    'CUSTOM': { label: 'مخصص', colorClass: 'bg-orange-100 text-orange-700' },
  });

  const updateRoleAppearance = (role: UserRole, appearance: RoleAppearance) => {
    setRoleAppearances(prev => ({ ...prev, [role]: appearance }));
  };

  const currentUserRef = useRef<User | null>(currentUser);

  useEffect(() => {
    setIsAuthenticated(!!currentUser);
    currentUserRef.current = currentUser;
  }, [currentUser]);
  const warehousesRef = useRef(warehouses);
  const systemSettingsRef = useRef(systemSettings);
  const invoicesRef = useRef<Invoice[]>([]);
  const ordersWarehousesRef = useRef<Invoice[]>([]);
  const notificationsRef = useRef<Notification[]>([]);

  useEffect(() => {
    currentUserRef.current = currentUser;
    warehousesRef.current = warehouses;
    systemSettingsRef.current = systemSettings;
    invoicesRef.current = invoices;
    ordersWarehousesRef.current = ordersWarehouses;
  }, [currentUser, warehouses, systemSettings, invoices, ordersWarehouses]);

  // Fetch user-specific notifications when current user changes or logs in
  useEffect(() => {
    if (currentUser) {
      fetchData(true, 'notifications').catch(err => console.error("Error fetching initial notifications:", err));
    } else {
      setNotifications([]);
      notificationsRef.current = [];
    }
  }, [currentUser]);

  useEffect(() => {
    if (isProcessing) {
      const timer = setTimeout(() => {
        setIsProcessing(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isProcessing]);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    let finalMessage = message;
    if (finalMessage) {
      if (finalMessage === 'Failed to fetch' || finalMessage.includes('Failed to fetch')) {
        finalMessage = 'مشكلة في الاتصال بالانترنت أو السيرفر، يرجى المحاولة مرة أخرى';
      }
    } else {
      finalMessage = 'حدث خطأ غير معروف';
    }

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    setToast({ message: finalMessage, type });

    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 4000);
  };

  const stopAlarm = () => stopAlarmSound();

  const sendNotificationToUser = async (targetUserId: string, title: string, message: string, type: Notification['type'], invoiceId?: string) => {
    try {
      const newNote: Notification = {
        id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9),
        title,
        message,
        type,
        time: new Date().toLocaleTimeString('ar-IQ', { timeZone: 'Asia/Baghdad' }),
        isRead: false,
        invoiceId
      };

      const dbPayload = mapNotificationToDB(newNote, targetUserId);
      await supabase.from('notifications').insert([dbPayload]);
      
      if (currentUser?.id === targetUserId) {
        const updated = [newNote, ...notificationsRef.current].slice(0, 50);
        setNotifications(updated);
        notificationsRef.current = updated;
      }
    } catch (err) {
      console.error("[StoreContext] sendNotificationToUser error:", err);
    }
  };

  const sendNotificationToRoleOrWarehouse = async (filter: { role?: UserRole[]; warehouseId?: string }, title: string, message: string, type: Notification['type'], invoiceId?: string) => {
    try {
      let query = supabase.from('profiles').select('id, role, center_id, permissions');
      const { data: profiles, error } = await query;
      if (error || !profiles) return;

      const targetUsers = profiles.filter(p => {
        if (filter.role && !filter.role.includes(p.role as UserRole)) {
          return false;
        }
        if (filter.warehouseId) {
          const wh = warehousesRef.current.find(w => String(w.id) === String(filter.warehouseId));
          const isManager = wh && String(wh.managerUserId) === String(p.id);
          const isLinkedCenter = wh && String(wh.id) === String(p.center_id);
          const isAdmin = p.role === 'SUPER_ADMIN' || p.role === 'EDITOR';

          return isManager || isLinkedCenter || isAdmin;
        }
        return true;
      });

      for (const u of targetUsers) {
        await sendNotificationToUser(u.id, title, message, type, invoiceId);
      }
    } catch (err) {
      console.error("[StoreContext] sendNotificationToRoleOrWarehouse error:", err);
    }
  };

  const addNotification = async (title: string, message: string, type: Notification['type'], invoiceId?: string) => {
    if (!currentUser) return;
    await sendNotificationToUser(currentUser.id, title, message, type, invoiceId);
  };

  const clearNotifications = async () => {
    if (!currentUser) return;
    setNotifications([]);
    notificationsRef.current = [];
    try {
      await supabase.from('notifications').delete().eq('user_id', currentUser.id);
    } catch (err) {
      console.error("[StoreContext] clearNotifications error:", err);
    }
  };

  const markNotificationAsRead = async (id: string) => {
    if (!currentUser) return;
    const updated = notificationsRef.current.map(n => n.id === id ? { ...n, isRead: true } : n);
    setNotifications(updated);
    notificationsRef.current = updated;
    try {
      await supabase.from('notifications').update({ is_read: 1 }).eq('id', id);
    } catch (err) {
      console.error("[StoreContext] markNotificationAsRead error:", err);
    }
  };

  const markChatNotificationsAsRead = async (senderNameOrUserId?: string) => {
    if (!currentUser) return;
    const toMark = notificationsRef.current.filter(n => {
      if (n.isRead) return false;
      const isChatNotif = (n.title && (n.title.includes('رسالة') || n.title.includes('💬'))) ||
                          (n.message && (n.message.includes('رسالة') || n.message.includes('💬')));
      if (!isChatNotif) return false;
      if (senderNameOrUserId) {
        return n.title.includes(senderNameOrUserId) || n.message.includes(senderNameOrUserId);
      }
      return true;
    });

    if (toMark.length === 0) return;

    const idsToMark = new Set(toMark.map(n => n.id));
    const updated = notificationsRef.current.map(n => idsToMark.has(n.id) ? { ...n, isRead: true } : n);
    setNotifications(updated);
    notificationsRef.current = updated;

    try {
      const idsArray = Array.from(idsToMark);
      for (const id of idsArray) {
        await supabase.from('notifications').update({ is_read: 1 }).eq('id', id);
      }
    } catch (err) {
      console.error("[StoreContext] markChatNotificationsAsRead error:", err);
    }
  };

  const checkLowStock = (currentProducts: Product[], settings: Record<string, any>) => {
    const threshold = Number(settings.low_stock_threshold || 10);
    const groups: Record<string, { name: string; total: number; warehouseId: string }> = {};

    currentProducts.forEach(p => {
      const key = `${normalizeArabic(p.name)}_${p.warehouseId}`;
      if (!groups[key]) {
        groups[key] = { name: p.name, total: 0, warehouseId: p.warehouseId };
      }
      groups[key].total += Number(p.quantity || 0);
    });

    const newNotified = new Set(lowStockNotified);
    let hasNewNotification = false;

    Object.entries(groups).forEach(([key, data]) => {
      if (data.total <= threshold && data.total > 0 && !lowStockNotified.has(key)) {
        newNotified.add(key);
        hasNewNotification = true;
      } else if (data.total > threshold && lowStockNotified.has(key)) {
        newNotified.delete(key);
      }
    });

    if (hasNewNotification) {
      setLowStockNotified(newNotified);
    }
  };

  // --- IDLE TIMER ---
  useEffect(() => {
    if (!currentUser) return;

    let idleTimer: NodeJS.Timeout;
    // Default to 15 minutes of inactivity for auto-logout
    const idleMinutes = Number(systemSettings?.idle_timeout_minutes) || 15;
    const idleTimeout = idleMinutes * 60 * 1000;

    const handleIdleLogout = () => {
      console.log(`[StoreContext] User idle for ${idleMinutes} minutes, logging out...`);
      sessionStorage.setItem('logout_reason', 'idle');
      logout();
    };

    const resetTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(handleIdleLogout, idleTimeout);
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Throttled reset to avoid performance issues on mousemove
    let lastReset = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 1000) { // Reset at most once per second
        lastReset = now;
        resetTimer();
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledReset, { passive: true });
    });

    resetTimer();

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledReset);
      });
    };
  }, [currentUser, systemSettings?.idle_timeout_minutes]);

  // Helpers for mapping notifications
  const mapDBToNotification = (n: any): Notification => {
    return {
      id: String(n.id),
      title: n.title || '',
      message: n.message || '',
      time: n.created_at ? new Date(n.created_at).toLocaleTimeString('ar-IQ', { timeZone: 'Asia/Baghdad' }) : '',
      type: n.type || 'system',
      isRead: Boolean(n.is_read !== undefined ? n.is_read === 1 || n.is_read === true : (n.isRead || false)),
      invoiceId: n.invoice_id || n.invoiceId
    };
  };

  const mapNotificationToDB = (n: Notification, targetUserId: string) => {
    return {
      id: n.id,
      user_id: targetUserId,
      title: n.title,
      message: n.message,
      type: n.type,
      is_read: n.isRead ? 1 : 0,
      invoice_id: n.invoiceId || null
    };
  };

  // Helper for consistent mapping
  const mapDBToInvoice = (inv: any): Invoice => {
    let items = [];
    try {
      items = Array.isArray(inv.items) ? inv.items : (typeof inv.items === 'string' && inv.items.trim() ? JSON.parse(inv.items) : []);
    } catch (e) {
      console.error("Error parsing invoice items:", e);
      items = [];
    }

    const notes = inv.notes || '';
    let extractedType = inv.type || '';
    
    // Parse type from notes if not present or to ensure consistency for legacy data
    if (!extractedType || extractedType === 'cash' || extractedType === 'sale') {
      if (notes.includes('TYPE:offer')) extractedType = 'offer';
      else if (notes.includes('TYPE:gift')) extractedType = 'gift';
      else if (notes.includes('TYPE:deferred')) extractedType = 'deferred';
      else if (notes.includes('TYPE:debt')) extractedType = 'debt';
      else if (notes.includes('TYPE:flexible') || notes.includes('IS_FLEXIBLE_OFFER')) extractedType = 'flexible';
      else if (!extractedType || extractedType === 'sale') extractedType = 'cash';
    }

    const agentId = String(inv.agent_id || inv.agentid || '');
    const warehouseId = String(inv.warehouse_id || inv.warehouseid || '');

    return { 
      ...inv, 
      id: String(inv.id), 
      agentId: agentId, 
      warehouseId: warehouseId, 
      centerId: warehouseId || agentId,
      items: items,
      totalPoints: Number(inv.total_points || inv.totalPoints || 0), 
      status: (inv.status || 'pending').toLowerCase(),
      notes: notes,
      type: extractedType,
      customerName: inv.customer_name || inv.customerName || '',
      customerPhone: inv.customer_phone || inv.customerPhone || '',
      customerMembership: inv.customer_membership || inv.customerMembership || '',
      totalPriceUSD: Number(inv.total_price_usd || inv.totalPriceUSD || 0),
      exchangeRate: Number(inv.exchange_rate || inv.exchangeRate || 1500),
      memberId: inv.member_id || inv.memberId || '',
      createdBy: inv.created_by || inv.createdBy || '',
      userName: inv.user_name || inv.userName || '',
      centerName: inv.center_name || inv.centerName || ''
    };
  };

  const calculateSalesFromInvoices = (mappedInvoices: Invoice[], currentWarehouses: Warehouse[]) => {
    const whMap = new Map<string, Warehouse>(currentWarehouses.map(w => [String(w.id), w]));
    const agentWhMap = new Map<string, Warehouse>(
      currentWarehouses.filter(w => w.agentId).map(w => [String(w.agentId), w])
    );

    return mappedInvoices
      .filter(inv => {
        const wh = whMap.get(String(inv.warehouseId)) || agentWhMap.get(String(inv.agentId));
        const isSaleType = ['sale', 'cash', 'debt', 'deferred', 'offer', 'flexible', 'gift', 'offers', 'gifts', 'offer_flexible'].includes(inv.type || '');
        const isWarehouseOrder = inv.type === 'warehouse';
        const isFinished = ['approved', 'received', 'completed', 'paid', 'sale', 'shipped', 'pending_cancellation', 'cancelled'].includes(inv.status);
        return wh?.type === 'AGENT' && !isWarehouseOrder && isFinished;
      })
      .map(inv => {
        let saleType = inv.type || 'cash';
        if (saleType === 'sale') saleType = 'cash';
        if (saleType === 'offers') saleType = 'offer';
        if (saleType === 'gifts') saleType = 'gift';
        if (saleType === 'offer_flexible') saleType = 'flexible';

        return {
          id: inv.id, 
          agentId: inv.agentId, 
          centerId: inv.centerId, 
          warehouseId: inv.warehouseId, 
          date: inv.date,
          items: (inv.items || []).map((item: any) => ({ productId: item.productId, productName: item.productName, quantity: item.quantity, priceUSD: Number(item.priceUSD || 0), points: item.points })),
          totalPoints: Number(inv.totalPoints || 0), totalPointsUSD: Number(inv.totalPoints || 0), // Adding totalPointsUSD for consistency if needed
          totalPriceUSD: Number(inv.totalPriceUSD || 0), exchangeRate: Number(inv.exchangeRate || 1500),
          customerName: inv.customerName || '', customerPhone: inv.customerPhone || '', customerMembership: inv.customerMembership || '', 
          isFlexibleOffer: inv.type === 'flexible' || inv.type === 'offer_flexible', 
          type: saleType as any,
          memberId: inv.memberId,
          userId: inv.createdBy,
          status: inv.status as any,
          cancellationReason: inv.cancellation_reason,
          userName: inv.userName || '',
          centerName: inv.centerName || ''
        };
      });
  };

  const fetchData = async (silent = false, tableToFetch?: string) => {
    const tableKey = tableToFetch || 'all';
    if (!fetchResolversRef.current[tableKey]) fetchResolversRef.current[tableKey] = [];
    
    return new Promise<void>((resolve) => {
      fetchResolversRef.current[tableKey].push(resolve);
      
      if (fetchDebounceTimerRef.current[tableKey]) {
        clearTimeout(fetchDebounceTimerRef.current[tableKey]);
      }

      const debounceTime = tableToFetch ? 50 : (silent ? 500 : 200);
      
      fetchDebounceTimerRef.current[tableKey] = setTimeout(async () => {
        if (isFetchingRef.current[tableKey]) {
          const resolvers = fetchResolversRef.current[tableKey];
          fetchResolversRef.current[tableKey] = [];
          resolvers.forEach(r => r());
          return;
        }

        // Allow public fetching of agents and warehouses for registration
        const isPublicAllowed = ['agents', 'warehouses', 'system_settings'].includes(tableKey);
        
        // --- IMPROVED USER CHECK ---
        // If we are still loading, wait a bit to give checkSession/onAuthStateChange a chance to finish
        let hasUser = !!currentUserRef.current;
        let retryCount = 0;
        
        // While loading, if we don't have a user, wait up to 3 seconds (in small steps)
        while (!hasUser && loading && retryCount < 15 && !isPublicAllowed) {
            await new Promise(r => setTimeout(r, 200));
            hasUser = !!currentUserRef.current;
            retryCount++;
        }

        // Final check before skipping
        if (!hasUser && !isPublicAllowed && !loading) {
          if (!silent) console.warn(`[StoreContext] Skipping ${tableKey} fetch: No user and not loading.`);
          const resolvers = fetchResolversRef.current[tableKey];
          fetchResolversRef.current[tableKey] = [];
          resolvers.forEach(r => r());
          return;
        }

        // --- CRITICAL FIX FOR SILENT SESSION LOSS (ANON RLS ARRAY WIPES) ---
        // If Supabase loses the session token in the background, a query runs as 'anon'.
        
        // Wait briefly if session validation is currently running in background to avoid race conditions
        let sessionWaitCount = 0;
        while (sessionCheckActiveRef.current && sessionWaitCount < 10) {
            await new Promise(r => setTimeout(r, 500));
            sessionWaitCount++;
        }

        if (hasUser && !isPublicAllowed) {
           const { data: sessionData } = await supabase.auth.getSession();
           if (!sessionData?.session) {
             const { error: refreshErr, data: refreshData } = await supabase.auth.refreshSession();
             if (refreshErr || !refreshData?.session) {
                // If refresh failed, it might be a manual login or truly expired.
                // We only block the fetch if we AREN'T in an optimistic manual mode.
                const isManualFallback = !!localStorage.getItem('manual_user');
                
                if (!isManualFallback) {
                  if (!silent) console.error("[StoreContext] Session inactive and no manual fallback. Aborting background fetch to protect UI data.");
                  const resolvers = fetchResolversRef.current[tableKey];
                  fetchResolversRef.current[tableKey] = [];
                  isFetchingRef.current[tableKey] = false;
                  if (loading) setLoading(false);
                  resolvers.forEach(r => r());
                  return;
                } else {
                  if (!silent) console.warn("[StoreContext] Supabase Auth session inactive, but manual fallback found. Proceeding with fetch.");
                }
             }
           }
        }

        const actualNow = Date.now();
        if (actualNow - (lastTableFetchTimeRef.current[tableKey] || 0) < debounceTime && silent && !tableToFetch && hasUser) {
          const resolvers = fetchResolversRef.current[tableKey];
          fetchResolversRef.current[tableKey] = [];
          resolvers.forEach(r => r());
          return;
        }
        
        lastFetchTimeRef.current = actualNow;
        lastTableFetchTimeRef.current[tableKey] = actualNow;
        isFetchingRef.current[tableKey] = true;
        
        if (!silent) setIsSyncing(true);
        try {
          const fetchTable = async (table: string, limit = 5000, retries = 2): Promise<any[] | null> => {
            // Safety check: Don't poll private tables if we lost the user during the timeout
            if (!['agents', 'warehouses', 'system_settings'].includes(table) && !currentUserRef.current && !loading) {
               return null;
            }
            try {
              let query = supabase.from(table).select('*');
              if (table === 'notifications' && currentUserRef.current) {
                query = query.eq('user_id', currentUserRef.current.id);
              }
              
              // Apply limits and ordering to potentially large tables
              if (['invoices', 'orders_warehouses', 'attendance', 'deferred_points', 'product_invoices', 'agent_orders', 'approved_invoices', 'transfers'].includes(table)) {
                let orderField = 'date';
                if (table === 'attendance') orderField = 'login_time';
                else if (table === 'deferred_points') orderField = 'release_date';
                
                // Do not apply 'ORDER BY' at SQL level for orders_warehouses because its remote schema 
                // is structurally mapped (COL 1, COL 2...) and would cause a 500 error in dev environment.
                if (table !== 'orders_warehouses') {
                  query = query.order(orderField, { ascending: false });
                  
                  // Secondary sort for better precision on same-day entries
                  if (['invoices', 'transfers', 'agent_orders'].includes(table)) {
                    query = query.order('id', { ascending: false });
                  }
                }
                
                query = query.limit(limit);
              } else if (table === 'products') {
                query = query.limit(10000);
              }

              const { data, error } = await query;
              if (error) {
                const isAbortError = error.message?.includes('AbortError') || error.hint?.includes('aborted');
                const isNetworkError = error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError');
                
                // Return gracefully without 500 error logs if it's just a network/ad-blocker issue to prevent noise
                if (isNetworkError) {
                  console.warn(`[StoreContext] Network or AdBlocker blocked fetch for ${table}.`);
                } else if (isAbortError) {
                  // Silent abort - common during navigation
                  return null;
                } else {
                  console.error(`[StoreContext] Fetch error for ${table}:`, error);
                }
                
                // Attempt to auto-recover if JWT token is expired
                if (error.message?.toLowerCase().includes('jwt') || error.details?.toLowerCase().includes('jwt')) {
                   console.log("[StoreContext] JWT error detected in fetch, attempting to refresh session...");
                   await supabase.auth.refreshSession();
                }

                // Fast-fail if the DB is completely unreachable
                if (error?.code === 'ECONNREFUSED' || error?.code === 'ENOTFOUND' || error?.code === 'ETIMEDOUT') {
                   console.log(`[StoreContext] DB refused connection unconditionally for ${table}. Skipping retries.`);
                   return null;
                }
                
                if (retries > 0 && error?.code !== 'SERVER_STARTING' && error?.code !== 'INVALID_JSON') {
                   await new Promise(resolve => setTimeout(resolve, 1500)); // Delay before retry to avoid rate limits
                   return await fetchTable(table, limit, retries - 1);
                }
                return null;
              }
              lastTableFetchTimeRef.current[table] = Date.now();
              return data;
            } catch (err: any) {
              const isAbortError = err.name === 'AbortError' || err.message?.includes('AbortError') || err.message?.includes('aborted');
              if (isAbortError) {
                // Silent catch for unexpected aborts
                return null;
              }
              console.error(`[StoreContext] Fetch exception for ${table}:`, err);
              if (retries > 1) return await fetchTable(table, limit, retries - 1);
              return null;
            }
          };

      if (tableToFetch) {
        let limit = 5000;
        if (tableToFetch === 'attendance') limit = 10000;
        if (tableToFetch === 'products') limit = 10000;

        const data = await fetchTable(tableToFetch, limit);
        if (data) {
          switch (tableToFetch) {
          case 'agents': setAgents(data.map((a: any) => ({ ...a, name: a.name || '', id: String(a.id), warehouseId: a.warehouse_id ? String(a.warehouse_id) : undefined }))); break;
          case 'warehouses': {
            const mapped = data.map((w: any) => ({ 
              ...w, 
              name: w.name || '',
              manager: w.manager || '',
              id: String(w.id), 
              managerUserId: w.manager_user_id ? String(w.manager_user_id) : undefined, 
              agentId: w.agent_id ? String(w.agent_id) : undefined, 
              exchangeRate: Number(w.exchange_rate || 1500) 
            }));
            setWarehouses(mapped);
            warehousesRef.current = mapped;
            break;
          }
          case 'products': {
            const mapped = data.map(mapDBToProduct);
            setProducts(mapped);
            checkLowStock(mapped, systemSettings);
            break;
          }
          case 'invoices': {
            const mapped = data.map(mapDBToInvoice);
            setInvoices(mapped);
            invoicesRef.current = mapped;
            setSales(calculateSalesFromInvoices(mapped, warehousesRef.current));
            break;
          }
          case 'orders_warehouses': {
            const mapped = data.map(mapDBToInvoice);
            setOrdersWarehouses(mapped);
            break;
          }
          case 'product_invoices': {
            const mapped = data.map((inv: any) => {
              const rawItems = Array.isArray(inv.items) ? inv.items : safeParse(inv.items, []);
              const mappedItems = rawItems
                .filter((it: any) => it && ((it.name && it.name.trim() !== '') || (it.quantity && Number(it.quantity) > 0)))
                .map(mapDBToProduct);
              return {
                id: String(inv.id),
                warehouseId: (inv.warehouse_id || inv.warehouseid) ? String(inv.warehouse_id || inv.warehouseid) : undefined,
                date: inv.date,
                items: mappedItems,
                totalPoints: Number(inv.total_points || 0),
                totalQuantity: Number(inv.total_quantity || 0),
                totalCartons: Number(inv.total_cartons || 0)
              };
            });
            setProductInvoices(mapped);
            break;
          }
          case 'match_invoices': setMatchInvoices(data.map((m: any) => ({ 
            id: String(m.id), 
            date: m.date, 
            items: Array.isArray(m.items) ? m.items : safeParse(m.items, []), 
            totalDifference: Number(m.total_difference || 0),
            userId: m.user_id || '',
            userName: m.user_name || ''
          }))); break;
          case 'approved_invoices': {
            const mapped = data.map((a: any) => ({ id: String(a.id), date: a.date, items: Array.isArray(a.items) ? a.items : safeParse(a.items, []), warehouseId: (a.warehouse_id || a.warehouseid) ? String(a.warehouse_id || a.warehouseid) : undefined, agentId: (a.agent_id || a.agentid) ? String(a.agent_id || a.agentid) : undefined, totalPoints: Number(a.total_points || 0), status: 'approved' as any }));
            setApprovedInvoices(mapped);
            break;
          }
          case 'agent_orders': {
            const mapped = data.map((a: any) => ({ id: String(a.id), date: a.date, items: Array.isArray(a.items) ? a.items : safeParse(a.items, []), warehouseId: (a.warehouse_id || a.warehouseid) ? String(a.warehouse_id || a.warehouseid) : undefined, agentId: (a.agent_id || a.agentid) ? String(a.agent_id || a.agentid) : undefined, totalPoints: Number(a.total_points || 0), status: 'approved' }));
            // We fetch withdrawals from this table
            const withdrawals = data.filter((a: any) => {
              const notes = (a.notes || '').toUpperCase();
              return notes.includes('COMMISSION_WITHDRAWAL') || notes.includes('سحب عمولة');
            }).map((a: any) => {
              const parsedItems = Array.isArray(a.items) ? a.items : safeParse(a.items, []);
              const item = parsedItems[0] || {};
              return { 
                id: String(a.id), 
                centerId: (a.agent_id || a.agentid) ? String(a.agent_id || a.agentid) : undefined, 
                warehouseId: (a.warehouse_id || a.warehouseid) ? String(a.warehouse_id || a.warehouseid) : undefined, 
                date: a.date, 
                amountUSD: Number(item.amountUSD || 0), 
                exchangeRate: Number(item.exchangeRate || 1500), 
                membershipNumber: item.membershipNumber || '', 
                memberName: item.memberName || '', 
                authImageUrl: item.authImageUrl || '',
                receiverName: a.receiver_name || item.receiverName || '',
                userName: a.user_name || '',
                centerName: a.center_name || '',
                status: 'approved' as any 
              };
            });
            setCommissionWithdrawals(withdrawals);
            break;
          }
          case 'profiles': {
            const mappedUsers = data.map((p: any) => ({ 
              id: String(p.id), 
              name: p.name || '', 
              email: p.email || '', 
              password: p.password, 
              role: p.role || 'VIEWER', 
              permissions: calculatePermissions(p.role || 'VIEWER', p.permissions), 
              agentId: p.agent_id ? String(p.agent_id) : undefined, 
              centerId: p.center_id ? String(p.center_id) : undefined,
              linkedCenterIds: safeParse(p.linked_center_ids) || safeParse((systemSettingsRef.current || {})[`usr_lc_${p.id}`]) || [],
              phone: p.phone,
              emergencyContactName: p.emergency_contact_name,
              emergencyContactPhone: p.emergency_contact_phone,
              createdAt: p.created_at,
              faceDescriptor: safeParse(p.face_descriptor),
              faceImage: normalizeImageUrl(p.face_image) || undefined,
              profileImage: normalizeImageUrl(p.profile_image) || undefined,
              idCardFront: normalizeImageUrl(p.id_card_front) || undefined,
              idCardBack: normalizeImageUrl(p.id_card_back) || undefined,
              age: p.age,
              education: p.education,
              maritalStatus: p.marital_status,
              childrenCount: p.children_count,
              currentAddress: p.current_address,
              previousAddress: p.previous_address
            }));
            setUsers(mappedUsers);
            if (currentUserRef.current) {
              const updatedMe = mappedUsers.find(u => u.id === currentUserRef.current?.id);
              if (updatedMe) {
                setCurrentUser(updatedMe);
                currentUserRef.current = updatedMe;
              }
            }
            break;
          }
          case 'attendance': setAttendance(data.map((a: any) => ({
            id: String(a.id),
            userId: String(a.user_id),
            userName: a.user_name,
            centerId: a.center_id ? String(a.center_id) : undefined,
            centerName: a.center_name,
            loginTime: a.login_time ? (a.login_time.includes('Z') ? a.login_time : a.login_time.replace(' ', 'T') + 'Z') : '',
            logoutTime: a.logout_time ? (a.logout_time.includes('Z') ? a.logout_time : a.logout_time.replace(' ', 'T') + 'Z') : undefined,
            date: a.date ? String(a.date).substring(0, 10) : '',
            lateReason: a.late_reason,
            logoutReason: a.logout_reason,
            notes: a.notes,
            photoImage: normalizeImageUrl(a.photo_image) || undefined,
            logoutPhotoImage: normalizeImageUrl(a.logout_photo_image) || undefined
          }))); break;
          case 'leave_requests': setLeaveRequests(data.map((l: any) => ({
            id: String(l.id),
            userId: String(l.user_id),
            userName: l.user_name,
            centerId: l.center_id ? String(l.center_id) : undefined,
            centerName: l.center_name,
            type: l.type,
            category: l.category || 'regular',
            startDate: l.start_date,
            endDate: l.end_date,
            startTime: l.start_time,
            endTime: l.end_time,
            reason: l.reason,
            attachments: (Array.isArray(l.attachments) ? l.attachments : safeParse(l.attachments, [])).map((url: string) => typeof url === 'string' ? url.replace('https://rkstore.pages.dev/uploads/', 'https://www.basseraa.com/uploads/').replace('https://warehouse-manager.pages.dev/uploads/', 'https://www.basseraa.com/uploads/') : url),
            status: l.status,
            approvedBy: l.approved_by,
            approvedByName: l.approved_by_name,
            processedByName: l.processed_by_name,
            createdAt: l.created_at,
            messages: Array.isArray(l.messages) ? l.messages : safeParse(l.messages, [])
          }))); break;
          case 'transfers': setTransfers(data.map((t: any) => ({ 
            id: String(t.id), 
            sourceWarehouseId: String(t.source_warehouse_id), 
            destinationWarehouseId: String(t.destination_warehouse_id), 
            date: t.date, 
            items: Array.isArray(t.items) ? t.items : safeParse(t.items, []), 
            status: t.status || 'pending', 
            notes: t.notes,
            userId: t.user_id || '',
            userName: t.user_name || ''
          }))); break;
          case 'deferred_points': setDeferredPoints(data.map((p: any) => ({ 
            ...p, 
            id: String(p.id), 
            centerId: String(p.center_id), 
            memberId: String(p.member_id || ''),
            saleId: String(p.sale_id || ''),
            productId: String(p.product_id), 
            productName: p.product_name, 
            points: Number(p.points || 0),
            quantity: Number(p.quantity || 0),
            releaseDate: p.release_date,
            userName: p.user_name || '',
            centerName: p.center_name || '',
            memberName: p.member_name || '',
            membershipNumber: p.membership_number || ''
          }))); break;
          case 'flexible_offers': setFlexibleOffers(data.map((o: any) => ({ 
            ...o, 
            id: String(o.id), 
            startDate: o.start_date, 
            endDate: o.end_date, 
            isActive: o.is_active, 
            discountPercentage: o.discount_percentage, 
            bonusPoints: o.bonus_points, 
            productIds: Array.isArray(o.product_ids) ? o.product_ids : safeParse(o.product_ids, []),
            offerCategory: o.offer_category,
            isGlobal: o.is_global,
            centerId: o.center_id,
            userId: o.user_id || '',
            userName: o.user_name || ''
          }))); break;
          case 'offer_members': setOfferMembers(data.map((m: any) => ({ 
            ...m, 
            id: String(m.id), 
            offerId: m.offer_id ? String(m.offer_id) : undefined, 
            membershipNumber: m.membership_number, 
            createdAt: m.created_at, 
            phone: m.phone, 
            authImageUrl: normalizeImageUrl(m.auth_image_url) || undefined,
            type: m.type || 'offer', 
            centerId: m.center_id ? String(m.center_id) : undefined, 
            centerName: m.center_name,
            userId: m.user_id || '',
            userName: m.user_name || ''
          }))); break;
          case 'user_feedback':
          case 'app_feedbacks':
          case 'feedbacks': 
            console.log(`[StoreContext] Feedbacks sync for table ${tableToFetch || 'ALL'}:`, data);
            setFeedbacks(data.map(mapDBToFeedback)); 
            break;
          case 'daily_closings':
            setDailyClosings(data.map((c: any) => {
              const summaryObj = typeof c.summary === 'string' ? safeParse(c.summary, {}) : (c.summary || {});
              return {
                id: String(c.id),
                centerId: String(c.center_id),
                centerName: c.center_name,
                userId: String(c.user_id),
                userName: c.user_name,
                date: c.date,
                totalCashUSD: Number(c.total_cash_usd || 0),
                totalFlexibleUSD: Number(c.total_flexible_usd || 0),
                totalExpensesUSD: Number(c.total_expenses_usd || 0),
                totalWithdrawalsUSD: Number(c.total_withdrawals_usd || 0),
                netCashUSD: Number(c.net_cash_usd || 0),
                dailySafeUSD: Number(c.daily_safe_usd || summaryObj?.dailySafeUSD || 0),
                totalPoints: Number(c.total_points || 0),
                exchangeRate: Number(c.exchange_rate || 1500),
                status: c.status,
                closedAt: c.closed_at,
                summary: summaryObj
              };
            }));
            break;
          case 'warehouse_locks': setWarehouseLocks(data); break;
            case 'activity_logs': setActivityLogs(data.map((l: any) => ({ ...l, id: String(l.id) }))); break;
          case 'member_points': setMemberPoints(data.map((p: any) => ({ ...p, id: String(p.id) }))); break;
          case 'system_settings': {
              const settings: Record<string, any> = {};
              data.forEach((s: any) => {
                settings[s.key] = s.value;
              });
              setSystemSettings(settings);
              const videoSetting = data.find((s: any) => s.key === 'intro_video_url');
              if (videoSetting) setIntroVideo(videoSetting.value);
              break;
            }
          case 'notifications': {
              const mapped = data.map(mapDBToNotification);
              
              if (notificationsRef.current.length > 0) {
                const currentIds = new Set(notificationsRef.current.map(n => n.id));
                const hasNewUnread = mapped.some((n: any) => !currentIds.has(n.id) && !n.isRead);
                if (hasNewUnread) {
                  playNotificationSound();
                  const latest = mapped.find((n: any) => !currentIds.has(n.id) && !n.isRead);
                  if (latest) {
                    setToast({ message: `🔔 ${latest.title}: ${latest.message}`, type: 'info' });
                    setTimeout(() => setToast(null), 5000);
                  }
                }
              }
              
              setNotifications(mapped);
              notificationsRef.current = mapped;
              break;
            }
          }
        }
        return;
      }

          // Phase 1: Essential Data (Staggered loading for better perceived performance)
          const [
            agentsData, warehousesData, productsData, usersData, settingsData, locksData
          ] = await Promise.all([
            fetchTable('agents'), 
            fetchTable('warehouses'), 
            fetchTable('products', 10000), 
            fetchTable('profiles'), 
            fetchTable('system_settings'), 
            fetchTable('warehouse_locks')
          ]);

          if (agentsData) setAgents(agentsData.map(a => ({ ...a, name: a.name || '', id: String(a.id), warehouseId: a.warehouse_id ? String(a.warehouse_id) : undefined })));
          if (warehousesData) {
            const mappedWarehouses = warehousesData.map(w => ({ 
              ...w, 
              name: w.name || '',
              manager: w.manager || '',
              id: String(w.id), 
              managerUserId: w.manager_user_id ? String(w.manager_user_id) : undefined, 
              agentId: w.agent_id ? String(w.agent_id) : undefined,
              latitude: w.latitude ? Number(w.latitude) : undefined,
              longitude: w.longitude ? Number(w.longitude) : undefined,
              allowedRadius: w.allowed_radius ? Number(w.allowed_radius) : undefined,
              location: w.location
            }));
            setWarehouses(mappedWarehouses);
            warehousesRef.current = mappedWarehouses; 
          }
          if (productsData) {
            const mappedProducts = productsData.map(mapDBToProduct);
            setProducts(mappedProducts);
            // Check low stock after products are loaded
            const currentSettings = settingsData ? (() => {
              const s: Record<string, any> = {};
              settingsData.forEach((item: any) => { s[item.key] = item.value; });
              return s;
            })() : systemSettings;
            checkLowStock(mappedProducts, currentSettings);
          }
          if (usersData) {
            const mappedUsers = usersData.map((p: any) => ({ 
              id: String(p.id), 
              name: p.name || '', 
              email: p.email || '', 
              password: p.password, 
              role: p.role || 'VIEWER', 
              permissions: calculatePermissions(p.role || 'VIEWER', p.permissions), 
              agentId: p.agent_id ? String(p.agent_id) : undefined, 
              centerId: p.center_id ? String(p.center_id) : undefined,
              phone: p.phone,
              emergencyContactName: p.emergency_contact_name,
              emergencyContactPhone: p.emergency_contact_phone,
              age: p.age,
              education: p.education,
              maritalStatus: p.marital_status,
              childrenCount: p.children_count,
              currentAddress: p.current_address,
              previousAddress: p.previous_address,
              profileImage: normalizeImageUrl(p.profile_image),
              idCardFront: normalizeImageUrl(p.id_card_front),
              idCardBack: normalizeImageUrl(p.id_card_back),
              createdAt: p.created_at,
              leaveBalance: p.leave_balance,
              faceDescriptor: typeof p.face_descriptor === 'string' ? safeParse(p.face_descriptor, undefined) : p.face_descriptor
            }));
            setUsers(mappedUsers);
            if (currentUserRef.current) {
              const updatedMe = mappedUsers.find(u => u.id === currentUserRef.current?.id);
              if (updatedMe) {
                setCurrentUser(updatedMe);
                currentUserRef.current = updatedMe;
              }
            }
          }
          if (settingsData) {
            const settings: Record<string, any> = {};
            settingsData.forEach((s: any) => { 
              let val = s.value;
              if (typeof val === 'string' && (s.key.includes('image') || s.key.includes('logo') || s.key.includes('stamp') || s.key.includes('video') || s.key.includes('photo') || s.key.includes('url'))) {
                val = normalizeImageUrl(val);
              }
              settings[s.key] = val; 
            });
            setSystemSettings(settings);
            const videoSetting = settingsData.find((s: any) => s.key === 'intro_video_url');
            if (videoSetting) setIntroVideo(normalizeImageUrl(videoSetting.value));
          }
          if (locksData) setWarehouseLocks(locksData);

          // Phase 2: Secondary Data (Background loading)
          const [invoicesData, ordersWarehousesData, pInvoicesData, matchData] = await Promise.all([
            fetchTable('invoices', 2000), 
            fetchTable('orders_warehouses', 2000),
            fetchTable('product_invoices', 1000), 
            fetchTable('match_invoices', 500)
          ]);

          if (invoicesData) {
            const mapped = invoicesData.map(mapDBToInvoice);
            setInvoices(mapped);
            invoicesRef.current = mapped;
            setSales(calculateSalesFromInvoices(mapped, warehousesRef.current));
          }
          if (ordersWarehousesData) {
            setOrdersWarehouses(ordersWarehousesData.map(mapDBToInvoice));
          }
          if (pInvoicesData) setProductInvoices(pInvoicesData.map((inv: any) => ({ id: String(inv.id), warehouseId: (inv.warehouse_id || inv.warehouseid) ? String(inv.warehouse_id || inv.warehouseid) : undefined, date: inv.date, items: (Array.isArray(inv.items) ? inv.items : safeParse(inv.items, [])).map(mapDBToProduct), totalPoints: Number(inv.total_points || 0), totalQuantity: Number(inv.total_quantity || 0), totalCartons: Number(inv.total_cartons || 0) })));
          if (matchData) setMatchInvoices(matchData.map((m: any) => ({ 
            id: String(m.id), 
            date: m.date, 
            items: Array.isArray(m.items) ? m.items : safeParse(m.items, []), 
            totalDifference: Number(m.total_difference || 0),
            userId: m.user_id || '',
            userName: m.user_name || ''
          })));

          // After essential data and first batch of secondary data is loaded, we can show the app
          setLoading(false);
          setIsSyncing(false);

          const [approvedData, ordersData, transfersData, flexibleOffersData] = await Promise.all([
            fetchTable('approved_invoices', 1000),
            fetchTable('agent_orders', 1000), 
            fetchTable('transfers', 500),
            fetchTable('flexible_offers')
          ]);

          const [deferredPointsData, offerMembersData, attendanceData, expensesData, dailyClosingsData, feedbacksData, leaveRequestsData, lettersData, notificationsData] = await Promise.all([
            fetchTable('deferred_points', 1000), 
            fetchTable('offer_members'),
            fetchTable('attendance', 10000),
            fetchTable('expenses', 5000),
            fetchTable('daily_closings', 2000),
            fetchTable('user_feedback'),
            fetchTable('leave_requests', 2000),
            fetchTable('company_letters'),
            fetchTable('notifications', 500)
          ]);

          if (flexibleOffersData) setFlexibleOffers(flexibleOffersData.map((o: any) => ({ 
            ...o, 
            id: String(o.id), 
            startDate: o.start_date, 
            endDate: o.end_date, 
            isActive: o.is_active, 
            discountPercentage: o.discount_percentage, 
            bonusPoints: o.bonus_points,
            productIds: Array.isArray(o.product_ids) ? o.product_ids : safeParse(o.product_ids, []),
            userId: o.user_id || '',
            userName: o.user_name || ''
          })));
          if (deferredPointsData) setDeferredPoints(deferredPointsData.map((p: any) => ({ 
            ...p, 
            id: String(p.id), 
            centerId: String(p.center_id), 
            memberId: String(p.member_id || ''),
            saleId: String(p.sale_id || ''),
            productId: String(p.product_id), 
            productName: p.product_name, 
            points: Number(p.points || 0),
            quantity: Number(p.quantity || 0),
            releaseDate: p.release_date,
            userId: p.user_id || '',
            userName: p.user_name || '',
            centerName: p.center_name || '',
            memberName: p.member_name || '',
            membershipNumber: p.membership_number || ''
          })));
          if (offerMembersData) setOfferMembers(offerMembersData.map((m: any) => ({ 
            ...m, 
            id: String(m.id), 
            offerId: String(m.offer_id), 
            membershipNumber: m.membership_number, 
            authImageUrl: normalizeImageUrl(m.auth_image_url) || undefined,
            createdAt: m.created_at,
            userId: m.user_id || '',
            userName: m.user_name || ''
          })));
          if (attendanceData) setAttendance(attendanceData.map((a: any) => ({
            id: String(a.id),
            userId: String(a.user_id),
            userName: a.user_name,
            centerId: a.center_id ? String(a.center_id) : undefined,
            centerName: a.center_name,
            loginTime: a.login_time ? (a.login_time.includes('Z') ? a.login_time : a.login_time.replace(' ', 'T') + 'Z') : '',
            logoutTime: a.logout_time ? (a.logout_time.includes('Z') ? a.logout_time : a.logout_time.replace(' ', 'T') + 'Z') : undefined,
            date: a.date ? String(a.date).substring(0, 10) : '',
            lateReason: a.late_reason,
            logoutReason: a.logout_reason,
            notes: a.notes,
            photoImage: normalizeImageUrl(a.photo_image) || undefined,
            logoutPhotoImage: normalizeImageUrl(a.logout_photo_image) || undefined
          })));

          if (leaveRequestsData) {
            setLeaveRequests(leaveRequestsData.map((l: any) => ({
              id: String(l.id),
              userId: String(l.user_id),
              userName: l.user_name,
              centerId: l.center_id ? String(l.center_id) : undefined,
              centerName: l.center_name,
              type: l.type,
              category: l.category || 'regular',
              startDate: l.start_date,
              endDate: l.end_date,
              startTime: l.start_time,
              endTime: l.end_time,
              reason: l.reason,
              attachments: (Array.isArray(l.attachments) ? l.attachments : safeParse(l.attachments, [])).map((url: string) => typeof url === 'string' ? url.replace('https://rkstore.pages.dev/uploads/', 'https://www.basseraa.com/uploads/').replace('https://warehouse-manager.pages.dev/uploads/', 'https://www.basseraa.com/uploads/') : url),
              status: l.status,
              createdAt: l.created_at,
              approvedBy: l.approved_by,
              approvedByName: l.approved_by_name,
              processedByName: l.processed_by_name || l.approved_by_name,
              messages: Array.isArray(l.messages) ? l.messages : safeParse(l.messages, [])
            })));
          }

          if (expensesData) {
            setExpenses(expensesData.map((e: any) => ({
              id: String(e.id),
              centerId: String(e.center_id),
              centerName: e.center_name,
              userId: String(e.user_id),
              userName: e.user_name,
              category: e.category,
              amount: Number(e.amount || 0),
              currency: e.currency,
              exchangeRate: Number(e.exchange_rate || 1500),
              description: e.description,
              date: e.date
            })));
          }

          if (dailyClosingsData) {
            setDailyClosings(dailyClosingsData.map((c: any) => {
              const summaryObj = typeof c.summary === 'string' ? safeParse(c.summary, {}) : (c.summary || {});
              return {
                id: String(c.id),
                centerId: String(c.center_id),
                centerName: c.center_name,
                userId: String(c.user_id),
                userName: c.user_name,
                date: c.date,
                totalCashUSD: Number(c.total_cash_usd || 0),
                totalFlexibleUSD: Number(c.total_flexible_usd || 0),
                totalExpensesUSD: Number(c.total_expenses_usd || 0),
                totalWithdrawalsUSD: Number(c.total_withdrawals_usd || 0),
                netCashUSD: Number(c.net_cash_usd || 0),
                dailySafeUSD: Number(c.daily_safe_usd || summaryObj?.dailySafeUSD || 0),
                totalPoints: Number(c.total_points || 0),
                exchangeRate: Number(c.exchange_rate || 1500),
                status: c.status,
                closedAt: c.closed_at,
                summary: summaryObj
              };
            }));
          }

          if (feedbacksData) {
            setFeedbacks(feedbacksData.map(mapDBToFeedback));
          }

          if (lettersData) {
            setCompanyLetters(lettersData.map((l: any) => ({
              id: String(l.id),
              letterNumber: l.letter_number,
              formattedNumber: l.formatted_number,
              date: l.date,
              content: l.content,
              textBlocks: typeof l.text_blocks_json === 'string' ? safeParse(l.text_blocks_json, undefined) : l.text_blocks_json,
              backgroundImage: l.background_image,
              stampImage: l.stamp_image,
              layout: typeof l.layout_json === 'string' ? safeParse(l.layout_json, undefined) : l.layout_json,
              userId: l.user_id,
              userName: l.user_name,
              centerId: l.center_id,
              centerName: l.center_name,
              createdAt: l.created_at
            })));
          }

          if (notificationsData) {
            const mapped = notificationsData.map(mapDBToNotification);
            setNotifications(mapped);
            notificationsRef.current = mapped;
          }

          if (invoicesData) {
            console.log(`[StoreContext] Fetched ${invoicesData.length} invoices`);
            const mappedInvoices = invoicesData.map(mapDBToInvoice);
            console.log(`[StoreContext] Mapped ${mappedInvoices.length} invoices`);
            
            // Detect new invoices for notifications
            if (invoicesRef.current.length > 0) {
               const oldIds = new Set(invoicesRef.current.map(i => i.id));
               const newInvoices = mappedInvoices.filter(i => !oldIds.has(i.id));
               
               newInvoices.forEach(newInvoice => {
                  const user = currentUserRef.current;
                  const currentWarehouses = warehousesRef.current;
                  if (user && newInvoice.status === 'pending') {
                    const targetWh = currentWarehouses.find(w => String(w.id) === String(newInvoice.warehouseId));
                    const whName = targetWh ? targetWh.name : 'مخزن';
                    const isAdmin = user.role === 'SUPER_ADMIN' || user.role === 'EDITOR';
                    const isTargetWarehouseManager = targetWh && String(targetWh.managerUserId) === String(user.id);

                    if (isAdmin || isTargetWarehouseManager) {
                        const notifMessage = `وصل طلب جديد برقم ${newInvoice.id} إلى ${whName}. يرجى المراجعة.`;
                        playNotificationSound();
                        addNotification("طلب جديد وارد", notifMessage, "order", newInvoice.id);
                        setToast({ message: `🔔 ${notifMessage}`, type: 'info' });
                        setTimeout(() => setToast(null), 5000);
                    }
                  }
               });
            }
            
            setInvoices(mappedInvoices);
            invoicesRef.current = mappedInvoices;
            setSales(calculateSalesFromInvoices(mappedInvoices, warehousesRef.current));
          }
          if (pInvoicesData) setProductInvoices(pInvoicesData.map(inv => ({ id: String(inv.id), warehouseId: String(inv.warehouse_id || inv.warehouseid), date: inv.date, items: (Array.isArray(inv.items) ? inv.items : safeParse(inv.items, [])).map(mapDBToProduct), totalPoints: Number(inv.total_points || 0), totalQuantity: Number(inv.total_quantity || 0), totalCartons: Number(inv.total_cartons || 0) })));
          if (matchData) setMatchInvoices(matchData.map(m => ({ id: String(m.id), date: m.date, items: Array.isArray(m.items) ? m.items : safeParse(m.items, []), totalDifference: Number(m.total_difference || 0), userId: m.user_id || '', userName: m.user_name || '' })));
          if (approvedData) {
            setApprovedInvoices(approvedData.map(a => ({ id: String(a.id), date: a.date, items: Array.isArray(a.items) ? a.items : safeParse(a.items, []), warehouseId: String(a.warehouse_id || a.warehouseid), agentId: String(a.agent_id || a.agentid), totalPoints: Number(a.total_points || 0), status: 'approved' as any })));
          }
          if (ordersData) {
            const withdrawals = ordersData.filter(a => {
              const notes = (a.notes || '').toUpperCase();
              return notes.includes('COMMISSION_WITHDRAWAL') || notes.includes('سحب عمولة');
            }).map(a => {
              const parsedItems = Array.isArray(a.items) ? a.items : safeParse(a.items, []);
              const item = parsedItems[0] || {};
              return { 
                id: String(a.id), 
                centerId: String(a.agent_id || a.agentid), 
                warehouseId: String(a.warehouse_id || a.warehouseid), 
                date: a.date, 
                amountUSD: Number(item.amountUSD || 0), 
                exchangeRate: Number(item.exchangeRate || 1500), 
                membershipNumber: item.membershipNumber || '', 
                memberName: item.memberName || '', 
                authImageUrl: item.authImageUrl || '',
                receiverName: a.receiver_name || item.receiverName || '',
                userName: a.user_name || '',
                centerName: a.center_name || '',
                status: 'approved' as any 
              };
            });
            setCommissionWithdrawals(withdrawals);
          }
          if (transfersData) setTransfers(transfersData.map(t => ({ 
            id: String(t.id), 
            sourceWarehouseId: String(t.source_warehouse_id), 
            destinationWarehouseId: String(t.destination_warehouse_id), 
            date: t.date, 
            items: Array.isArray(t.items) ? t.items : safeParse(t.items, []), 
            status: t.status || 'pending', 
            notes: t.notes,
            userId: t.user_id || '',
            userName: t.user_name || ''
          })));
        } catch (e) {
          console.error("Fetch Data Error:", e);
        } finally {
          isFetchingRef.current[tableKey] = false;
          const resolvers = fetchResolversRef.current[tableKey];
          fetchResolversRef.current[tableKey] = [];
          resolvers.forEach(r => r());
          setLoading(false);
          setIsDataLoaded(true);
          setIsSyncing(false);
        }
      }, tableToFetch ? 100 : debounceTime); // Shorter debounce for specific tables
    });
  };

  const refreshData = async () => {
    await fetchData();
  };

  useEffect(() => {
    const APP_VERSION = '1.0.6';
    
    // Perform aggressive cleanup immediately if flagged from a previous logout
    if (localStorage.getItem('strict_logout_active') === 'true' || sessionStorage.getItem('strict_logout_active') === 'true') {
      console.warn("[StoreContext] Strict logout cleanup on boot. Purging storage.");
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem('app_version', APP_VERSION);
    }

    if (localStorage.getItem('app_version') !== APP_VERSION) {
      console.log('App version updated, clearing caches...');
      localStorage.clear();
      sessionStorage.clear();
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      localStorage.setItem('app_version', APP_VERSION);
      // Small delay to allow react to mount before full reload
      setTimeout(() => {
        window.location.reload();
      }, 500);
      return;
    }

    const channel = supabase.channel('system-sync-all')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'invoices' }, async (payload) => {
          const newInvoice = payload.new;
          const user = currentUserRef.current;
          const currentWarehouses = warehousesRef.current;

          if (user) {
            const targetWh = currentWarehouses.find(w => String(w.id) === String(newInvoice.warehouse_id));
            const whName = targetWh ? targetWh.name : 'مخزن';
            const isAdmin = user.role === 'SUPER_ADMIN' || user.role === 'EDITOR';
            const isTargetWarehouseManager = targetWh && String(targetWh.managerUserId) === String(user.id);

            if (isAdmin || isTargetWarehouseManager) {
                const notifMessage = `وصل طلب جديد برقم ${newInvoice.id} إلى ${whName}. يرجى المراجعة.`;
                playNotificationSound();
                await addNotification("طلب جديد وارد", notifMessage, "order", newInvoice.id);
                setToast({ message: `🔔 ${notifMessage}`, type: 'info' });
                setTimeout(() => setToast(null), 5000);
            }
          }
          fetchData(true, 'invoices');
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'orders_warehouses' }, async (payload) => {
          const newInvoice = payload.new;
          const user = currentUserRef.current;
          const currentWarehouses = warehousesRef.current;

          if (user) {
            const targetWh = currentWarehouses.find(w => String(w.id) === String(newInvoice.warehouse_id));
            const whName = targetWh ? targetWh.name : 'مخزن';
            const isAdmin = user.role === 'SUPER_ADMIN' || user.role === 'EDITOR';
            const isTargetWarehouseManager = targetWh && String(targetWh.managerUserId) === String(user.id);

            if (isAdmin || isTargetWarehouseManager) {
                const notifMessage = `وصلت طلبية جديدة برقم ${newInvoice.id} إلى ${whName}. يرجى المراجعة.`;
                playNotificationSound();
                await addNotification("طلبية جديدة واردة", notifMessage, "order", newInvoice.id);
                setToast({ message: `🔔 ${notifMessage}`, type: 'info' });
                setTimeout(() => setToast(null), 5000);
            }
          }
          fetchData(true, 'orders_warehouses');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'system_settings' }, async (payload: any) => {
         if (payload.new && payload.new.key) {
           const key = payload.new.key;
           const val = payload.new.value;
           
           setSystemSettings(prev => ({ ...prev, [key]: val }));
           if (key.startsWith('chat_history_') || key.startsWith('last_seen_')) {
             return; // Do not reload entire settings table over HTTP for chat messages
           }
         }
         fetchData(true, 'system_settings');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, async (payload: any) => {
         const activeUser = currentUserRef.current;
         if (activeUser && payload.new && payload.new.user_id === activeUser.id) {
           const newNotif = mapDBToNotification(payload.new);
           const currentIds = new Set(notificationsRef.current.map(n => n.id));
           
           if (!currentIds.has(newNotif.id)) {
              if (!newNotif.isRead) {
                playNotificationSound();
                setToast({ message: `🔔 ${newNotif.title}: ${newNotif.message}`, type: 'info' });
                setTimeout(() => setToast(null), 5000);
              }
              const updated = [newNotif, ...notificationsRef.current].slice(0, 50);
              setNotifications(updated);
              notificationsRef.current = updated;
           } else {
              const updated = notificationsRef.current.map(n => n.id === newNotif.id ? newNotif : n);
              setNotifications(updated);
              notificationsRef.current = updated;
           }
         }
         fetchData(true, 'notifications');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders_warehouses' }, (payload) => {
         if(payload.eventType !== 'INSERT') fetchData(true, 'orders_warehouses');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices' }, (payload) => {
         if(payload.eventType !== 'INSERT') fetchData(true, 'invoices');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => fetchData(true, 'products'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'transfers' }, () => fetchData(true, 'transfers'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agents' }, () => fetchData(true, 'agents'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'warehouses' }, () => fetchData(true, 'warehouses'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'product_invoices' }, () => fetchData(true, 'product_invoices'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'approved_invoices' }, () => fetchData(true, 'approved_invoices'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_orders' }, () => fetchData(true, 'agent_orders'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => fetchData(true, 'profiles'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leave_requests' }, () => fetchData(true, 'leave_requests'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'attendance' }, () => fetchData(true, 'attendance'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_feedback' }, () => fetchData(true, 'user_feedback'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'company_letters' }, () => fetchData(true, 'company_letters'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'warehouse_locks' }, (payload) => {
        if (payload.eventType === 'INSERT') setWarehouseLocks(prev => [...prev, payload.new as WarehouseLock]);
        if (payload.eventType === 'DELETE') setWarehouseLocks(prev => prev.filter(l => l.warehouse_id !== payload.old.warehouse_id));
      })
      .subscribe();

    const fetchProfileData = async (userId: string) => {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();
        
        if (profile && !profileError) { 
          const role = profile.role || 'VIEWER';
          const userData: User = { 
            id: String(profile.id), 
            name: profile.name || '', 
            email: profile.email || '', 
            password: profile.password, 
            role, 
            permissions: calculatePermissions(role, profile.permissions), 
            agentId: String(profile.agent_id || ''), 
            centerId: String(profile.center_id || ''),
            createdAt: profile.created_at,
            faceDescriptor: safeParse(profile.face_descriptor),
            profileImage: normalizeImageUrl(profile.profile_image),
            idCardFront: normalizeImageUrl(profile.id_card_front),
            idCardBack: normalizeImageUrl(profile.id_card_back),
            phone: profile.phone,
            emergencyContactName: profile.emergency_contact_name,
            emergencyContactPhone: profile.emergency_contact_phone,
            age: profile.age,
            education: profile.education,
            maritalStatus: profile.marital_status,
            childrenCount: profile.children_count,
            currentAddress: profile.current_address,
            previousAddress: profile.previous_address
          };
          return userData;
        }
      } catch (err) {
        console.error("[StoreContext] fetchProfileData error:", err);
      }
      return null;
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const ts = Date.now();
      console.log(`[StoreContext] Auth Event: ${event} (${ts})`);

      if (localStorage.getItem('strict_logout_active') === 'true' || sessionStorage.getItem('strict_logout_active') === 'true') {
        if (event === 'SIGNED_OUT') {
           setCurrentUser(null);
           setIsAuthenticated(false);
        }
        return;
      }
      
      if (event === 'SIGNED_OUT') {
        // Clear secure cookies
        fetch('/api/auth/session', { method: 'DELETE', keepalive: true }).catch(e => console.error("Error clearing cookie on signout event:", e));

        console.log("[StoreContext] SIGNED_OUT detected. Transitioning to guest state.");
        setCurrentUser(null);
        currentUserRef.current = null;
        localStorage.removeItem('manual_user');
        setIsAuthenticated(false);
      } else if (['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event as any)) {
        // Sync cookies on sign-in or refresh
        if (session) {
          fetch('/api/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              access_token: session.access_token,
              refresh_token: session.refresh_token
            }),
            keepalive: true
          }).catch(e => console.error("Error syncing cookie on auth event:", e));
        }

        if (session?.user) {
          setIsAuthenticated(true);
          // If the profile fetch was already done by checkSession or login, skip it
          if (currentUserRef.current?.id === session.user.id) {
            return;
          }

          const userData = await fetchProfileData(session.user.id);
          if (userData) {
            setCurrentUser(userData);
            currentUserRef.current = userData;
            localStorage.setItem('manual_user', JSON.stringify(userData));
            fetchData(true);
          }
        }
        setLoading(false);
      }
    });

    const checkSession = async () => {
      if (sessionCheckActiveRef.current) return;
      
      const params = new URLSearchParams(window.location.search);
      const isLoggingOut = params.has('logout_ts') || params.has('logout_t') || params.has('logout') || sessionStorage.getItem('strict_logout_active') === 'true' || localStorage.getItem('strict_logout_active') === 'true';

      if (isLoggingOut) {
         console.log("[StoreContext] Logout intent detected. Preventing auto-login.");
         setLoading(false);
         sessionCheckActiveRef.current = false;
         // Cleanup flags after a delay if we are on the logout page
         setTimeout(() => {
           sessionStorage.removeItem('strict_logout_active');
           localStorage.removeItem('strict_logout_active');
         }, 3000);
         return;
      }
      
      sessionCheckActiveRef.current = true;
      
      const startTime = Date.now();
      console.log(`[StoreContext] Initializing session check sequence (${startTime})`);

      // 1. ABSOLUTE PRIORITY: Persistent Restore
      const manualUserStr = localStorage.getItem('manual_user');
      let isOptimistic = false;

      if (manualUserStr) {
        try {
          const userData = safeParse(manualUserStr);
          if (userData && userData.id) {
            console.log(`[StoreContext] 🚀 Optimistic Warm Start: ${userData.name}`);
            setCurrentUser(userData);
            currentUserRef.current = userData;
            isOptimistic = true;
            setLoading(false); // Enable UI immediately
            fetchData(true).catch(() => {});
          }
        } catch (e) {
          localStorage.removeItem('manual_user');
        }
      }

      const createTimeout = (ms: number, name: string) => {
        let timeoutId: any;
        const promise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error(name)), ms);
        });
        return { promise, cancel: () => clearTimeout(timeoutId) };
      };

      try {
        // 2. BACKGROUND VALIDATION: Only block if we failed optimistic start
        // Limit wait time. If we have no cache (not optimistic), we should show login fast.
        const waitTime = isOptimistic ? 3000 : 2000; 
        const sessionTimeout = createTimeout(waitTime, 'SESSION_TIMEOUT');
        
        let sessionResult;
        try {
          console.log("[StoreContext] Validating Supabase session in background...");
          // We use getSession first as it is much faster (local)
          sessionResult = await Promise.race([
            supabase.auth.getSession(),
            sessionTimeout.promise
          ]) as any;
          
          sessionTimeout.cancel();
        } catch (e: any) {
          sessionTimeout.cancel();
          console.warn("[StoreContext] Background validation timed out or failed. Proceeding with state.", e.message);
          // If we are at this point and not optimistic, we must let the user login
          setLoading(false);
          sessionCheckActiveRef.current = false;
          return;
        }

        const session = sessionResult.data?.session;
        if (session?.user) {
          // If we have a session, ensure profile matches
          if (!currentUserRef.current || currentUserRef.current.id !== session.user.id) {
            console.log("[StoreContext] Remote session found. Syncing profile...");
            const profileData = await fetchProfileData(session.user.id);
            if (profileData) {
              setCurrentUser(profileData);
              currentUserRef.current = profileData;
              localStorage.setItem('manual_user', JSON.stringify(profileData));
              fetchData(true).catch(err => console.error("[StoreContext] Initial fetchData failed:", err));
            }
          }
          
          // Trigger a silent refresh in background to keep it alive without blocking
          supabase.auth.refreshSession();
        } else if (!isOptimistic) {
           // Truly no session and no cache - force logout state
           console.log("[StoreContext] No session found. Setting to guest state.");
           setCurrentUser(null);
           currentUserRef.current = null;
           localStorage.removeItem('manual_user');
        }
      } catch (err) {
        console.error("[StoreContext] checkSession critical failure:", err);
      } finally {
        sessionCheckActiveRef.current = false;
        setLoading(false);
      }
    };
    checkSession();
    
    // Background Recovery for Chrome suspended tabs
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log("[StoreContext] App became visible. Ensuring session active.");
        if (currentUserRef.current) {
          // Force a token refresh if the tab was asleep to prevent 401s
          await supabase.auth.refreshSession();
          // If we lost RT connection, checking it will nudge supersonic
          fetchData(true).catch(() => {});
        } else {
          // If logged out but suddenly visible, check session just in case
          checkSession();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Always trigger an initial fetch in the background to support public views (like registration)
    // The fetchData function now allows fetching agents/warehouses even if not logged in.
    setTimeout(() => {
      fetchData(true).catch(e => console.log("[StoreContext] Initial background fetch:", e));
    }, 500);

    // Safety timeout to ensure loading screen eventually disappears
    const safetyTimeout = setTimeout(() => {
      // Only force end loading if we actually have some data or it's been a long time
      setLoading(false);
    }, 20000);

    // Lightning-Fast Background Change Detection (Delta Synchronization)
    const intervalId = setInterval(async () => {
      if (!currentUserRef.current) return;
      try {
        const response = await fetch('/api/db/changes-check');
        if (!response.ok) return;
        
        const serverTimestamps: Record<string, number> = await response.json();
        
        // List of synchronized database tables in the application
        const tablesSyncList = [
          'agents', 'warehouses', 'products', 'profiles', 'system_settings', 'warehouse_locks',
          'invoices', 'orders_warehouses', 'product_invoices', 'match_invoices', 'approved_invoices',
          'agent_orders', 'transfers', 'flexible_offers', 'deferred_points', 'offer_members',
          'attendance', 'expenses', 'daily_closings', 'user_feedback', 'leave_requests', 'company_letters',
          'notifications'
        ];
        
        for (const table of tablesSyncList) {
          const serverTime = serverTimestamps[table];
          if (serverTime) {
            const localTime = lastTableFetchTimeRef.current[table] || 0;
            // If the table was updated on the server more recently than our last sync, fetch it
            if (serverTime > localTime) {
              console.log(`⚡ Lightning Sync: Table "${table}" updated on server. Syncing...`);
              fetchData(true, table).catch(err => console.warn(`[Lightning Sync] Failed to sync ${table}:`, err));
            }
          }
        }
      } catch (err) {
        // Silently catch connection glitches to avoid console noise during background polling 
      }
    }, 1500); // Check for server modifications every 1.5 seconds for instant updates!

    return () => { 
      try {
        if (channel) {
          supabase.removeChannel(channel).catch(e => console.warn("[StoreContext] Channel removal non-critical error:", e));
        }
      } catch (e) {
        console.warn("[StoreContext] Channel cleanup failed:", e);
      }
      subscription.unsubscribe();
      clearInterval(intervalId);
      clearTimeout(safetyTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const acquireWarehouseLock = async (warehouseId: string, moduleName?: string) => {
    try {
      if (!currentUser) return { success: false };
      const { data: existing } = await supabase.from('warehouse_locks').select('*').eq('warehouse_id', warehouseId).maybeSingle();
      if (existing) {
        if (existing.user_id === currentUser.id) return { success: true };
        const lockTime = new Date(existing.created_at).getTime();
        const now = new Date().getTime();
        if ((now - lockTime) / (1000 * 60) > 15) {
           await supabase.from('warehouse_locks').delete().eq('warehouse_id', warehouseId);
        } else {
           return { success: false, error: `المخزن مشغول حالياً بواسطة ${existing.user_name}` };
        }
      }
      
      const displayName = moduleName ? `${currentUser.name} (${moduleName})` : currentUser.name;
      const { error } = await supabase.from('warehouse_locks').insert([{ 
        warehouse_id: warehouseId, 
        user_id: currentUser.id, 
        user_name: displayName 
      }]);
      if (error) return { success: false, error: "فشل في تأمين المخزن" };
      return { success: true };
    } catch (e) {
      console.error("Lock error:", e);
      return { success: false, error: "خطأ في تأمين المخزن" };
    }
  };

  const releaseWarehouseLock = async (warehouseId: string) => {
    if (!currentUser) return;
    await supabase.from('warehouse_locks').delete().eq('warehouse_id', warehouseId).eq('user_id', currentUser.id);
  };

  const forceReleaseLock = async (warehouseId: string) => {
     setIsProcessing(true);
     try {
       setWarehouseLocks(prev => prev.filter(l => l.warehouse_id !== warehouseId));
       await supabase.from('warehouse_locks').delete().eq('warehouse_id', warehouseId);
       showToast("تم فك حجز المخزن يدوياً");
       await fetchData(true);
     } finally {
       setIsProcessing(false);
     }
  };

  const login = useCallback(async (email: string, password: string, skipSetUser = false, rememberMe = false) => {
    console.log(`[StoreContext] login starting for: ${email}`);

    if (rememberMe) {
      localStorage.setItem('remember_me', 'true');
    } else {
      localStorage.removeItem('remember_me');
    }

    try {
      // Clear any previous logout flags since we are attempting login
      localStorage.removeItem('strict_logout_active');
      sessionStorage.removeItem('strict_logout_active');

      // 1. Primary: Supabase Auth with Race Timeout
      console.log("[StoreContext] Attempting Supabase Auth.signInWithPassword...");
      
      const { data: authData, error: authError } = await (async () => {
        const p = supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password: password
        });
        const timeout = new Promise<{data: any, error: any}>((_, reject) => setTimeout(() => reject(new Error("AUTH_TIMEOUT")), 20000));
        try {
          return await Promise.race([p, timeout]) as any;
        } catch (e: any) {
          return { data: null, error: { message: e.message === "AUTH_TIMEOUT" ? "عذراً، الاتصال بخدمة الهوية بطيء جداً، يرجى المحاولة مرة أخرى" : e.message } };
        }
      })();

      if (!authError && authData?.user) {
        console.log("[StoreContext] Auth success, fetching profile...");
        // Sync session (non-blocking)
        if (authData.session) {
          fetch('/api/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              access_token: authData.session.access_token,
              refresh_token: authData.session.refresh_token
            }),
            keepalive: true
          }).catch(e => console.warn("[StoreContext] Session sync failed:", e));
        }

        const { data: profile, error: profileError } = await (async () => {
          const p = supabase.from('profiles').select('*').eq('id', authData.user.id).single();
          const timeout = new Promise<{data: any, error: any}>((_, reject) => setTimeout(() => reject(new Error("PROFILE_TIMEOUT")), 15000));
          try {
            return await Promise.race([p, timeout]) as any;
          } catch (e: any) {
            return { data: null, error: { message: e.message === "PROFILE_TIMEOUT" ? "تأخر جلب بيانات الملف الشخصي، يرجى المحاولة ثانية" : e.message } };
          }
        })();

        if (profile && !profileError) {
          console.log("[StoreContext] Profile found, mapping user...");
          try {
            const userData = mapDBToUser(profile);
            if (!skipSetUser) {
              setCurrentUser(userData);
              currentUserRef.current = userData;
              setIsAuthenticated(true);
              localStorage.setItem('manual_user', JSON.stringify(userData));
              // Small delay to ensure state is set before fetching
              setTimeout(() => {
                fetchData(true).catch(e => console.error("[StoreContext] Post-login fetchData error:", e));
              }, 200);
              showToast('تم تسجيل الدخول بنجاح');
            }
            return { success: true, user: userData };
          } catch (mappingErr) {
            console.error("[StoreContext] User mapping failed:", mappingErr);
          }
        } else {
           console.warn("[StoreContext] Profile fetch error or timeout:", profileError);
        }
      } else if (authError) {
        console.warn("[StoreContext] Auth error or timeout:", authError.message);
      }

      // 3. Fallback: Manual Profile Auth
      console.log("[StoreContext] Auth failed or profile missing, trying manual profile lookup...");
      const { data: profiles, error: manualError } = await (async () => {
        const p = supabase.from('profiles').select('*').eq('email', email.trim().toLowerCase()).eq('password', password);
        const timeout = new Promise<{data: any, error: any}>((_, reject) => setTimeout(() => reject(new Error("MANUAL_AUTH_TIMEOUT")), 20000));
        try {
          return await Promise.race([p, timeout]) as any;
        } catch (e: any) {
          return { data: null, error: { message: e.message === "MANUAL_AUTH_TIMEOUT" ? "فشل البحث المباشر بسبب بطء الاتصال" : e.message } };
        }
      })();

      if (profiles && profiles.length > 0) {
        console.log("[StoreContext] Manual profile auth success!");
        const userData = mapDBToUser(profiles[0]);
        if (!skipSetUser) {
          setCurrentUser(userData);
          currentUserRef.current = userData;
          setIsAuthenticated(true);
          localStorage.setItem('manual_user', JSON.stringify(userData));
          setTimeout(() => {
            fetchData(true).catch(e => console.error("[StoreContext] Post-manual-login fetchData error:", e));
          }, 200);
          showToast('تم تسجيل الدخول بنجاح');
        }
        return { success: true, user: userData };
      }

      let finalError = authError?.message || manualError?.message || 'بيانات الدخول غير صحيحة.';
      if (finalError === 'Invalid login credentials') {
        finalError = 'البريد الإلكتروني أو كلمة المرور غير صحيحة';
      }
      console.warn("[StoreContext] Login failed for both methods:", finalError);
      return { success: false, error: finalError };

    } catch (err: any) {
      console.error("[StoreContext] CRITICAL Login Exception:", err);
      const errMsg = err?.message || 'فشل الاتصال بالخادم';
      return { success: false, error: errMsg };
    }
  }, [fetchData, showToast]);

  const logout = useCallback(async () => { 
    console.log("[StoreContext] Logout Initiated...");
    
    // 1. Clear state immediately for UI responsiveness
    setCurrentUser(null);
    currentUserRef.current = null;
    setIsAuthenticated(false);
    
    // 2. Stop all background processes
    if ((window as any).refreshInterval) clearInterval((window as any).refreshInterval);
    if ((window as any).alarmInterval) clearInterval((window as any).alarmInterval);
    stopAlarmSound();
    
    // 3. Set strict logout flags to prevent auto-login on reload
    sessionStorage.setItem('strict_logout_active', 'true');
    localStorage.setItem('strict_logout_active', 'true');
    
    try {
      // 4. Supabase SignOut
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("SIGNOUT_TIMEOUT")), 1000))
      ]).catch(e => console.warn("[StoreContext] SignOut error or timeout:", e));

      // 5. Clear all data arrays to prevent any stale data being visible
      setUsers([]);
      setAgents([]);
      setWarehouses([]);
      setProducts([]);
      setInvoices([]);
      setSales([]);
      setNotifications([]);
      setActivityLogs([]);
      setAttendance([]);
      setLeaveRequests([]);
      setExpenses([]);
      setDailyClosings([]);
      setFeedbacks([]);
      setMemberPoints([]);
      setDeferredPoints([]);

      // 6. Wipe Storage completely
      localStorage.clear();
      sessionStorage.clear();
      
      // Re-set flag AFTER clearing so it persists for the next load
      localStorage.setItem('strict_logout_active', 'true');
      
      // 7. Wipe Cookies
      document.cookie.split(";").forEach(c => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });

      // 8. Update URL cleanly
      window.history.replaceState(null, '', '/');

    } catch (err) {
      console.warn("[StoreContext] Logout minor failure:", err);
    }
  }, []);

  const addUser = async (u: User) => { 
    setIsProcessing(true);
    try {
      const payload: any = { 
        id: u.id, 
        name: u.name, 
        email: u.email, 
        password: u.password, 
        role: u.role, 
        permissions: u.permissions, 
        agent_id: u.agentId, 
        center_id: u.centerId,
        created_at: u.createdAt || new Date().toISOString(),
        face_descriptor: u.faceDescriptor ? JSON.stringify(u.faceDescriptor) : null,
        profile_image: u.profileImage || null,
        id_card_front: u.idCardFront || null,
        id_card_back: u.idCardBack || null,
        phone: u.phone || null,
        emergency_contact_name: u.emergencyContactName || null,
        emergency_contact_phone: u.emergencyContactPhone || null,
        age: u.age || null,
        education: u.education || null,
        marital_status: u.maritalStatus || null,
        children_count: u.childrenCount || 0,
        current_address: u.currentAddress || null,
        previous_address: u.previousAddress || null
      };

      // Ensure face_descriptor is set if it exists in the user object
      if (u.faceDescriptor) {
        payload.face_descriptor = JSON.stringify(u.faceDescriptor);
      }

      // Check for face_image carefully - only include if user specifically has it
      // Note: face_image column must exist in DB for this to work
      if (u.faceImage) {
        payload.face_image = u.faceImage;
      }

      const { data, error } = await supabase.from('profiles').insert([payload]).select().single(); 
      if (error) throw error; 
      
      if (u.linkedCenterIds && u.linkedCenterIds.length > 0) {
        await updateSystemSetting(`usr_lc_${data.id}`, JSON.stringify(u.linkedCenterIds));
      }

      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };

  const updateUser = async (u: User) => { 
    setIsProcessing(true);
    try {
      const updatePayload: any = { 
        name: u.name, 
        email: u.email, 
        password: u.password, 
        role: u.role, 
        permissions: u.permissions, 
        agent_id: u.agentId,
        center_id: u.centerId,
        face_descriptor: u.faceDescriptor ? JSON.stringify(u.faceDescriptor) : null,
        profile_image: u.profileImage || null,
        id_card_front: u.idCardFront || null,
        id_card_back: u.idCardBack || null,
        phone: u.phone || null,
        emergency_contact_name: u.emergencyContactName || null,
        emergency_contact_phone: u.emergencyContactPhone || null,
        age: u.age || null,
        education: u.education || null,
        marital_status: u.maritalStatus || null,
        children_count: u.childrenCount || 0,
        current_address: u.currentAddress || null,
        previous_address: u.previousAddress || null
      };

      if (u.faceDescriptor) {
        updatePayload.face_descriptor = JSON.stringify(u.faceDescriptor);
      }

      if (u.faceImage) {
        updatePayload.face_image = u.faceImage;
      }

      const { error } = await supabase.from('profiles').update(updatePayload).eq('id', u.id); 
      
      if (error) throw error; 

      if (u.linkedCenterIds !== undefined) {
        await updateSystemSetting(`usr_lc_${u.id}`, JSON.stringify(u.linkedCenterIds));
      } 

      // Update local storage and current user if it's the same person
      if (currentUser?.id === u.id) {
        const freshUser = { ...currentUser, ...u };
        setCurrentUser(freshUser);
        currentUserRef.current = freshUser;
        localStorage.setItem('manual_user', JSON.stringify(freshUser));
      }

      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };

  const faceLogin = async (descriptor: number[]) => {
    setIsProcessing(true);
    try {
      console.log("[StoreContext] Attempting Face Login...");
      const { data: profiles, error: fetchError } = await supabase.from('profiles').select('*').not('face_descriptor', 'is', null);
      if (fetchError) throw fetchError;
      
      let bestMatchProfile = null;
      let minDistance = 0.5; // Adjusted threshold for smarter matching to avoid false negative
      
      const inputDescriptor = new Float32Array(descriptor);
      
      for (const profile of profiles || []) {
        try {
          const storedDescData = safeParse(profile.face_descriptor);
          const storedDescriptor = new Float32Array(storedDescData);
          
          let distSq = 0;
          for (let i = 0; i < 128; i++) {
            distSq += Math.pow(inputDescriptor[i] - (storedDescriptor[i] || 0), 2);
          }
          const dist = Math.sqrt(distSq);
          
          if (dist < minDistance) {
            minDistance = dist;
            bestMatchProfile = profile;
          }
        } catch (e) {
          console.error("Error comparing face for user:", profile.id, e);
        }
      }
      
      if (bestMatchProfile) {
        console.log(`[StoreContext] Face matched! Logging in as: ${bestMatchProfile.email}`);
        return await login(bestMatchProfile.email, bestMatchProfile.password, false, true);
      }
      
      return { success: false, error: "لم يتم التعرف على الوجه. يرجى التأكد من أنك المستخدم الصحيح ووجهك مسجل مسبقاً." };
    } catch (err: any) {
      console.error("[StoreContext] Face login error:", err);
      return { success: false, error: "حدث خطأ أثناء معالجة تسجيل الدخول بالوجه" };
    } finally {
      setIsProcessing(false);
    }
  };

  const registerFace = async (userId: string, faceDescriptor: number[], faceImage?: string) => {
    try {
      console.log("[StoreContext] Registering Face...");
      
      const { data: otherProfiles, error: fetchError } = await supabase.from('profiles').select('id, name, face_descriptor').not('face_descriptor', 'is', null);
      if (fetchError) throw fetchError;
      
      const inputDescriptor = new Float32Array(faceDescriptor);
      for (const profile of otherProfiles || []) {
        if (profile.id === userId) continue;
        try {
          const storedDescData = typeof profile.face_descriptor === 'string' ? JSON.parse(profile.face_descriptor) : profile.face_descriptor;
          if (!storedDescData) continue;
          const storedDescriptor = new Float32Array(storedDescData);
          
          let distSq = 0;
          for (let i = 0; i < 128; i++) {
            distSq += Math.pow(inputDescriptor[i] - (storedDescriptor[i] || 0), 2);
          }
          const dist = Math.sqrt(distSq);
          
          if (dist < 0.45) { // Uniqueness check
            return { success: false, error: `هذا الوجه مسجل مسبقاً للمستخدم: ${profile.name}. لا يمكن تكرار البصمة.` };
          }
        } catch (e) {
          continue;
        }
      }

      const updateData: any = {
        face_descriptor: JSON.stringify(faceDescriptor)
      };

      if (faceImage) {
        updateData.face_image = faceImage;
      }

      const { error } = await supabase.from('profiles').update(updateData).eq('id', userId);
      
      if (error) throw error;
      
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, faceDescriptor: faceDescriptor, faceImage: faceImage || u.faceImage } : u));
      if (currentUser?.id === userId) {
        setCurrentUser(prev => prev ? { ...prev, faceDescriptor: faceDescriptor, faceImage: faceImage || prev.faceImage } : prev);
      }
      
      fetchData(true); // update background
      return { success: true };
    } catch (err: any) {
      console.error("[StoreContext] Face registration error:", err);
      return { success: false, error: err.message || 'فشل تسجيل بصمة الوجه' };
    }
  };

  const isValidUUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  const addAttendance = async (a: Attendance) => {
    try {
      setAttendance(prev => [...prev, a]); // optimistic update
      const dbCenterId = warehouses.some(w => String(w.id) === String(a.centerId)) ? a.centerId : null;
      const { error } = await supabase.from('attendance').insert([{
        id: a.id,
        user_id: a.userId,
        user_name: a.userName,
        center_id: dbCenterId ? String(dbCenterId) : null,
        center_name: a.centerName,
        login_time: a.loginTime,
        logout_time: a.logoutTime,
        date: a.date,
        late_reason: a.lateReason,
        logout_reason: a.logoutReason,
        notes: a.notes,
        photo_image: a.photoImage,
        logout_photo_image: a.logoutPhotoImage
      }]);
      if (error) {
        console.error('Error adding attendance:', error);
        throw error;
      }
      fetchData(true, 'attendance'); // background update
    } catch (err: any) {
      console.error('Add attendance failed:', err);
      fetchData(true, 'attendance'); // rollback if failed
      throw err;
    }
  };

  const updateAttendance = async (a: Attendance) => {
    try {
      setAttendance(prev => prev.map(item => item.id === a.id ? a : item)); // optimistic update
      const { error } = await supabase.from('attendance').update({
        login_time: a.loginTime,
        logout_time: a.logoutTime,
        date: a.date,
        late_reason: a.lateReason,
        logout_reason: a.logoutReason,
        notes: a.notes,
        photo_image: a.photoImage,
        logout_photo_image: a.logoutPhotoImage
      }).eq('id', a.id);
      if (error) {
        console.error('Error updating attendance:', error);
        throw error;
      }
      fetchData(true, 'attendance');
    } catch (err: any) {
      console.error('Update attendance failed:', err);
      fetchData(true, 'attendance');
      throw err;
    }
  };

  const deleteAttendance = async (id: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('attendance').delete().eq('id', id);
      if (error) throw error;
      await fetchData(true, 'attendance');
    } finally {
      setIsProcessing(false);
    }
  };

  const addLeaveRequest = async (r: LeaveRequest) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('leave_requests').insert([{
        id: r.id,
        user_id: r.userId,
        user_name: r.userName,
        center_id: r.centerId ? String(r.centerId) : null,
        center_name: r.centerName,
        type: r.type,
        category: r.category || 'regular',
        start_date: r.startDate,
        end_date: r.endDate,
        start_time: r.startTime,
        end_time: r.endTime,
        reason: r.reason,
        attachments: JSON.stringify(r.attachments || []),
        status: r.status,
        created_at: r.createdAt || new Date().toISOString(),
        messages: JSON.stringify(r.messages || [])
      }]);
      if (error) throw error;
      setLeaveRequests(prev => [r, ...prev.filter(item => item.id !== r.id)]);
      await fetchData(true, 'leave_requests');
    } finally {
      setIsProcessing(false);
    }
  };

  const updateLeaveRequest = async (r: LeaveRequest) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('leave_requests').update({
        status: r.status,
        approved_by: r.approvedBy,
        approved_by_name: r.approvedByName,
        processed_by_name: currentUser?.name || r.processedByName,
        messages: JSON.stringify(r.messages || [])
      }).eq('id', r.id);
      if (error) throw error;
      
      // Optimistic update
      setLeaveRequests(prev => prev.map(item => item.id === r.id ? { 
        ...item, 
        status: r.status, 
        approvedBy: r.approvedBy,
        approvedByName: r.approvedByName,
        processedByName: currentUser?.name || r.processedByName,
        messages: r.messages || []
      } : item));

      await fetchData(true, 'leave_requests');
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteLeaveRequest = async (id: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('leave_requests').delete().eq('id', id);
      if (error) throw error;
      setLeaveRequests(prev => prev.filter(item => item.id !== id));
      await fetchData(true, 'leave_requests');
    } finally {
      setIsProcessing(false);
    }
  };
  const deleteUser = async (id: string) => { 
    setIsProcessing(true);
    try {
      await supabase.from('profiles').delete().eq('id', id); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  const addAgent = async (agent: Agent) => { 
    setIsProcessing(true);
    try {
      const agentPayload = {
        name: agent.name,
        phone: agent.phone,
        address: agent.address,
        status: agent.status || 'active',
        region: agent.region || '',
        warehouse_id: agent.warehouseId || null,
        user_id: currentUser?.id,
        user_name: currentUser?.name
      };
      const { data: agentData, error } = await supabase.from('agents').insert([agentPayload]).select().single(); 
      if (error) throw error; 
      
      const { data: whData, error: whError } = await supabase.from('warehouses').insert([{ 
        name: `مخزن الوكيل: ${agentData.name}`, 
        manager: agentData.name, 
        address: agentData.address, 
        phone: agentData.phone, 
        type: 'AGENT', 
        agent_id: agentData.id,
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }]).select().single(); 
      
      if (whError) throw whError;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };
  const updateAgent = async (agent: Agent) => { 
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('agents').update({ 
        name: agent.name, 
        phone: agent.phone, 
        address: agent.address,
        status: agent.status || 'active',
        region: agent.region || '',
        warehouse_id: agent.warehouseId || null,
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }).eq('id', agent.id); 
      if (error) throw error; 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  const deleteAgent = async (id: string) => { 
    setIsProcessing(true);
    try {
      await supabase.from('agents').delete().eq('id', id); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  const addWarehouse = async (w: Warehouse) => { 
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('warehouses').insert([{ 
        name: w.name, 
        manager: w.manager || 'بدون مسؤول', 
        manager_user_id: w.managerUserId || null, 
        phone: w.phone || '', 
        address: w.address || '', 
        type: w.type || 'GENERAL', 
        agent_id: w.agentId || null,
        latitude: w.latitude || null,
        longitude: w.longitude || null,
        allowed_radius: w.allowedRadius || 500,
        location: w.location || '',
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }]); 
      if (error) {
        console.error("Supabase Error adding warehouse:", error);
        throw error;
      }
      await fetchData(true); 
      showToast('تم إضافة المخزن بنجاح');
    } catch (error: any) {
      console.error("Critical Error adding warehouse:", error);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      showToast('خطأ في إضافة المخزن: ' + errorMessage, 'error');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };
  const updateWarehouse = async (w: Warehouse) => { 
    setIsProcessing(true);
    try {
      await supabase.from('warehouses').update({ 
        name: w.name, 
        manager: w.manager || 'بدون مسؤول', 
        manager_user_id: w.managerUserId || null, 
        phone: w.phone || '', 
        address: w.address || '', 
        agent_id: w.agentId || null,
        latitude: w.latitude || null,
        longitude: w.longitude || null,
        allowed_radius: w.allowedRadius || 500,
        location: w.location || '',
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }).eq('id', w.id); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  const deleteWarehouse = async (id: string) => { 
    setIsProcessing(true);
    try {
      await supabase.from('warehouses').delete().eq('id', id); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  const addProduct = async (p: Product) => { 
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.from('products').insert([mapProductToDB(p, currentUser)]).select().single(); 
      if (error) throw error; 
      await fetchData(true); 
      return data ? mapDBToProduct(data) : null; 
    } finally {
      setIsProcessing(false);
    }
  };
  const addProductsBatch = async (products: Product[]) => { 
    setIsProcessing(true);
    try {
      const validProducts = products.filter(p => p && p.name && p.name.trim() !== '' && Number(p.quantity) > 0);
      if (validProducts.length === 0) return [];
      
      const addedProducts: Product[] = [];
      
      for (const p of validProducts) {
        const normalizedName = normalizeArabic(p.name || '');
        const normalizedExp = normalizeExpiryDate(p.expiryDate || '');
        const targetWarehouseId = p.warehouseId || '';
        
        // Check for existing product in target warehouse
        const { data: existingProducts, error: fetchErr } = await supabase
          .from('products')
          .select('*')
          .eq('warehouse_id', targetWarehouseId);

        if (fetchErr) {
          console.error("Error fetching products for batch addition:", fetchErr);
        }

        const existing = (existingProducts || []).find((ep: any) => 
          normalizeArabic(ep.name || '') === normalizedName && 
          normalizeExpiryDate(ep.expiry || ep.expiry_date || ep.expiryDate || '') === normalizedExp
        );

        if (existing) {
          const newQty = Number(existing.quantity || 0) + Number(p.quantity || 0);
          const updatePayload: any = { 
            quantity: newQty,
            warehouse_id: targetWarehouseId,
            user_id: currentUser?.id || existing.user_id,
            user_name: currentUser?.name || existing.user_name
          };
          if (p.code) updatePayload.code = p.code;
          if (p.points !== undefined && p.points !== null) updatePayload.points = p.points;
          if (p.piecesPerCarton) updatePayload.pieces_per_carton = p.piecesPerCarton;
          if (p.imageUrl) updatePayload.image_url = p.imageUrl;

          const { error: updErr } = await supabase
            .from('products')
            .update(updatePayload)
            .eq('id', existing.id);
            
          if (updErr) throw updErr;
          addedProducts.push({ ...p, id: String(existing.id) });
        } else {
          // New product record
          const prodId = p.id || crypto.randomUUID();
          const insertPayload: any = {
            id: prodId,
            warehouse_id: targetWarehouseId,
            name: p.name.trim(),
            code: p.code || 'AUTO',
            points: p.points || 0,
            price_usd: p.priceUSD || 0,
            expiry: normalizedExp,
            quantity: p.quantity || 0,
            pieces_per_carton: p.piecesPerCarton || 1,
            notes: p.notes || '',
            image_url: p.imageUrl || '',
            type: p.type || 'normal',
            category: p.category || '',
            unit: p.unit || '',
            user_id: p.userId || currentUser?.id || null,
            user_name: p.userName || currentUser?.name || null
          };

          const { error: insErr } = await supabase
            .from('products')
            .insert([insertPayload]);

          if (insErr) {
            console.error("Error inserting product in batch:", insErr);
            throw insErr;
          }

          addedProducts.push({ ...p, id: prodId });
        }
      }

      await fetchData(true); 
      return addedProducts; 
    } finally {
      setIsProcessing(false);
    }
  };
  const updateProduct = async (p: Product) => { 
    setIsProcessing(true);
    try {
      await supabase.from('products').update(mapProductToDB(p, currentUser)).eq('id', p.id); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  const deleteProduct = async (id: string) => { 
    setIsProcessing(true);
    try {
      await supabase.from('products').delete().eq('id', id); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  const addProductInvoice = async (inv: ProductInvoice) => { 
    setIsProcessing(true);
    try {
      const invoiceDate = inv.date 
        ? (inv.date.length === 10 ? `${inv.date} 00:00:00` : inv.date) 
        : new Date().toISOString().slice(0, 19).replace('T', ' ');

      const validItems = (inv.items || [])
        .filter(p => p && p.name && p.name.trim() !== '' && Number(p.quantity) > 0)
        .map(p => mapProductToDB(p, currentUser));

      const totalQty = validItems.reduce((s, it) => s + Number(it.quantity || 0), 0);
      const totalPts = validItems.reduce((s, it) => s + (Number(it.points || 0) * Number(it.quantity || 0)), 0);
      const totalCrt = validItems.reduce((s, it) => s + (Number(it.pieces_per_carton || 1) > 0 ? (Number(it.quantity || 0) / Number(it.pieces_per_carton || 1)) : Number(it.quantity || 0)), 0);

      const payload: any = { 
        id: inv.id, 
        warehouse_id: inv.warehouseId || '', 
        date: invoiceDate, 
        items: validItems, 
        total_points: totalPts, 
        total_quantity: totalQty, 
        total_cartons: totalCrt,
        user_id: currentUser?.id || null,
        user_name: currentUser?.name || null
      };

      let { error } = await supabase.from('product_invoices').insert([payload]); 
      if (error) {
        console.warn("Product invoice insert warning, retrying...", error);
        const res2 = await supabase.from('product_invoices').insert([payload]);
        if (res2.error) throw error;
      }
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  
  const addInvoice = async (inv: Invoice) => {
    setIsProcessing(true);
    let insertedId = inv.id;
    const isWarehouseOrder = String(inv.id).startsWith('RKORD');
    const targetTable = isWarehouseOrder ? 'orders_warehouses' : 'invoices';
    
    try {
      const { data: allWarehouseProducts, error: fetchErr } = await supabase
        .from('products')
        .select('*')
        .eq('warehouse_id', inv.warehouseId)
        .gt('quantity', 0);

      if (fetchErr) throw fetchErr;
      if (!allWarehouseProducts || allWarehouseProducts.length === 0) {
        throw new Error("المخزن فارغ حالياً أو لا تتوفر كميات كافية");
      }

      let localProducts = allWarehouseProducts.map(p => ({ ...p, originalQty: Number(p.quantity || 0) }));
      const updatedItems: InvoiceItem[] = [];
      const productUpdates: { id: string; oldQty: number; newQty: number }[] = [];

      for (const item of inv.items) {
        const normalizedTarget = normalizeArabic(item.productName);
        const matchedBatches = localProducts
          .filter(p => normalizeArabic(p.name) === normalizedTarget)
          .sort((a, b) => {
            const valA = getBatchSortValue(a.expiry);
            const valB = getBatchSortValue(b.expiry);
            if (valA !== valB) return valA.localeCompare(valB);
            return String(a.id).localeCompare(String(b.id));
          });

        if (matchedBatches.length === 0) throw new Error(`عجز مادة ${item.productName}`);

        const totalAvailable = matchedBatches.reduce((sum, b) => sum + Number(b.quantity || 0), 0);
        const requiredQty = Number(item.quantity); 

        if (totalAvailable < requiredQty) throw new Error(`عجز ${item.productName}. المتوفر: ${totalAvailable}`);

        let remainingToTake = requiredQty;
        const allocations: { batchId: string; expiryDate: string; quantity: number }[] = [];
        for (const b of matchedBatches) {
          if (remainingToTake <= 0) break;
          const currentQty = Number(b.quantity);
          if (currentQty <= 0) continue; 
          const take = Math.min(currentQty, remainingToTake);
          allocations.push({ batchId: String(b.id), expiryDate: b.expiry || '', quantity: take });
          
          remainingToTake -= take;
          b.quantity = currentQty - take;
          
          productUpdates.push({ id: String(b.id), oldQty: b.originalQty, newQty: b.quantity });
        }
        
        if (remainingToTake > 0) throw new Error(`فشل مادة ${item.productName}`);
        allocations.sort((a, b) => getBatchSortValue(a.expiryDate).localeCompare(getBatchSortValue(b.expiryDate)));
        updatedItems.push({ ...item, quantity: requiredQty, allocatedBatches: allocations });
      }
      
      // Perform stock updates first
      const completedUpdates: typeof productUpdates = [];
      try {
        for (const update of productUpdates) {
          const { error: updErr } = await supabase.from('products').update({ quantity: update.newQty }).eq('id', update.id);
          if (updErr) throw updErr;
          completedUpdates.push(update);
        }

        // Now insert invoice
        let success = false;
        let attempts = 0;
        let finalId = inv.id;

        while (!success && attempts < 10) {
          const { error: invErr } = await supabase
            .from(targetTable)
            .insert([{ 
              id: finalId, 
              agent_id: String(inv.agentId), 
              warehouse_id: inv.warehouseId, 
              date: inv.date, 
              items: updatedItems, 
              total_points: Number(inv.totalPoints || 0), 
              status: 'pending', 
              created_by: currentUser?.id,
              user_name: currentUser?.name
            }]);

          if (!invErr) {
            success = true;
            insertedId = finalId;
          } else if (invErr.code === '23505' || (invErr.message && invErr.message.includes('unique constraint'))) {
            attempts++;
            await new Promise(r => setTimeout(r, Math.random() * 300 + 100));
            finalId = await generateInvoiceId(isWarehouseOrder ? 'order' : 'cash');
          } else {
            throw invErr;
          }
        }

        if (!success) throw new Error("فشل توليد رقم فاتورة فريد");

      } catch (innerErr) {
        // Rollback stock if anything fails
        for (const r of completedUpdates) {
          await supabase.from('products').update({ quantity: r.oldQty }).eq('id', r.id);
        }
        throw innerErr;
      }
      
      await fetchData(true);

      // Trigger Order notifications
      if (isWarehouseOrder) {
        const wh = warehousesRef.current.find(w => String(w.id) === String(inv.warehouseId));
        const whName = wh ? wh.name : "المخزن";
        const title = "طلب جديد وارد 📦";
        const message = `وصل طلبية جديدة برقم ${insertedId} إلى ${whName} من قبل ${currentUser?.name || "المركز"}.`;
        
        if (wh && wh.managerUserId) {
          await sendNotificationToUser(wh.managerUserId, title, message, 'order', insertedId);
        }
        await sendNotificationToRoleOrWarehouse({ role: ['SUPER_ADMIN', 'EDITOR'] }, title, message, 'order', insertedId);
      }
    } catch (err: any) { 
      console.error("Add Invoice Error:", err);
      showToast(err.message || "خطأ في تسجيل الطلبية", "error");
      throw err; 
    } finally {
      setIsProcessing(false);
    }

  };

  const shipInvoice = async (id: string) => { 
    setIsProcessing(true);
    try {
      const isWarehouseOrder = String(id).startsWith('RKORD');
      const targetTable = isWarehouseOrder ? 'orders_warehouses' : 'invoices';
      const { error } = await supabase.from(targetTable).update({ 
        status: 'shipped',
        user_id: currentUser?.id,
        user_name: currentUser?.name 
      }).eq('id', id); 
      if (error) return { success: false, error: error.message }; 

      // Send Shipment Notifications
      try {
        const { data: orderDetails } = await supabase.from(targetTable).select('*').eq('id', id).maybeSingle();
        if (orderDetails) {
          const creatorId = orderDetails.created_by;
          const agentId = orderDetails.agent_id;
          const title = "تم شحن طلبيتك 🚚";
          const message = `خبر سار! لقد تم شحن طلبك رقم ${id} بنجاح من قبل ${currentUser?.name || "المستودع"}.`;

          if (creatorId) {
            await sendNotificationToUser(creatorId, title, message, 'shipment', id);
          }
          if (agentId) {
            await sendNotificationToRoleOrWarehouse({ role: ['AGENT', 'CUSTOM', 'VIEWER'], warehouseId: agentId }, title, message, 'shipment', id);
          }
        }
      } catch (notifErr) {
        console.error("Tracking shipment notification error:", notifErr);
      }

      await fetchData(true); 
      return { success: true }; 
    } finally {
      setIsProcessing(false);
    }
  };
  
  const receiveInvoice = async (id: string) => {
    setIsProcessing(true);
    try {
      const isWarehouseOrder = String(id).startsWith('RKORD');
      const targetTable = isWarehouseOrder ? 'orders_warehouses' : 'invoices';
      
      const inv = isWarehouseOrder ? ordersWarehouses.find(i => i.id === id) : invoices.find(i => i.id === id);
      if (!inv) return { success: false, error: "الفاتورة غير موجودة" };
      
      const agent = agents.find(a => String(a.id) === String(inv.agentId));
      let targetWarehouse = warehouses.find(w => w.type === 'AGENT' && String(w.agentId) === String(inv.agentId));
      
      if (!targetWarehouse && agent) {
        const { data: newWh, error: whError } = await supabase.from('warehouses').insert([{ 
          name: `مركز: ${agent.name}`, 
          manager: agent.name, 
          address: agent.address, 
          phone: agent.phone, 
          type: 'AGENT', 
          agent_id: agent.id
        }]).select().single();
        
        if (whError) {
          console.error("Error creating warehouse on receipt:", whError);
          return { success: false, error: "فشل في إنشاء مخزن الوكيل" };
        }
        
        targetWarehouse = { 
          ...newWh, 
          id: String(newWh.id),
          managerUserId: newWh.manager_user_id,
          agentId: String(newWh.agent_id)
        };
        await supabase.from('agents').update({ warehouse_id: newWh.id }).eq('id', agent.id);
      }
      
      if (!targetWarehouse) return { success: false, error: "لم يتم العثور على مركز لاستلام المواد" };

      for (const item of inv.items) {
        if (!item.allocatedBatches) continue;
        
        const { data: baseProductTemplate } = await supabase
          .from('products')
          .select('*')
          .eq('name', item.productName)
          .limit(1)
          .maybeSingle();

        for (const alloc of item.allocatedBatches) {
          const { data: existingInAgentCenter } = await supabase
            .from('products')
            .select('*')
            .eq('warehouse_id', targetWarehouse.id)
            .eq('name', item.productName)
            .eq('expiry', alloc.expiryDate)
            .maybeSingle();
          
          if (existingInAgentCenter) {
            await supabase
              .from('products')
              .update({ quantity: Number(existingInAgentCenter.quantity || 0) + Number(alloc.quantity) })
              .eq('id', existingInAgentCenter.id);
          } else {
            await supabase.from('products').insert([{ 
              warehouse_id: targetWarehouse.id, 
              name: item.productName, 
              code: baseProductTemplate?.code || 'AUTO', 
              points: baseProductTemplate?.points || item.points, 
              expiry: alloc.expiryDate, 
              quantity: alloc.quantity, 
              pieces_per_carton: baseProductTemplate?.pieces_per_carton || 1 
            }]);
          }
        }
      }
      
      await supabase.from(targetTable).update({ 
        status: 'received',
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }).eq('id', id);
      await fetchData(true);
      return { success: true }; 
    } catch (err: any) { 
      console.error("Receive Invoice Critical Error:", err);
      return { success: false, error: err.message }; 
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelInvoice = async (id: string) => {
    setIsProcessing(true);
    try {
      const isWarehouseOrder = String(id).startsWith('RKORD');
      const targetTable = isWarehouseOrder ? 'orders_warehouses' : 'invoices';
      
      const inv = isWarehouseOrder ? ordersWarehouses.find(i => i.id === id) : invoices.find(i => i.id === id);
      if (!inv) return { success: false, error: "الطلبية غير موجودة" };
      
      if (inv.status === 'received') {
        // 1. Remove items from agent warehouse
        const agentWh = warehouses.find(w => w.type === 'AGENT' && String(w.agentId) === String(inv.agentId));
        if (agentWh) {
          for (const item of inv.items) {
            if (!item.allocatedBatches) continue;
            for (const alloc of item.allocatedBatches) {
              const { data: pInAgentCenter } = await supabase
                .from('products')
                .select('*')
                .eq('warehouse_id', agentWh.id)
                .eq('name', item.productName)
                .eq('expiry', alloc.expiryDate)
                .maybeSingle();
                
              if (pInAgentCenter) {
                await supabase
                  .from('products')
                  .update({ quantity: Math.max(0, Number(pInAgentCenter.quantity || 0) - Number(alloc.quantity)) })
                  .eq('id', pInAgentCenter.id);
              }
            }
          }
        }

        // 2. Return items to source warehouse
        for (const item of inv.items) {
          if (!item.allocatedBatches) continue;
          for (const alloc of item.allocatedBatches) {
            const { data: sourceBatch } = await supabase
              .from('products')
              .select('*')
              .eq('id', alloc.batchId)
              .maybeSingle();
            
            if (sourceBatch) {
              await supabase
                .from('products')
                .update({ quantity: Number(sourceBatch.quantity || 0) + Number(alloc.quantity) })
                .eq('id', sourceBatch.id);
            } else {
              // If batch was deleted, recreate it in source warehouse
              const { data: baseTemplate } = await supabase
                .from('products')
                .select('*')
                .eq('name', item.productName)
                .limit(1)
                .maybeSingle();

              await supabase.from('products').insert([{
                id: alloc.batchId,
                warehouse_id: inv.warehouseId,
                name: item.productName,
                code: baseTemplate?.code || 'AUTO',
                points: baseTemplate?.points || item.points,
                expiry: alloc.expiryDate,
                quantity: alloc.quantity,
                pieces_per_carton: baseTemplate?.pieces_per_carton || 1
              }]);
            }
          }
        }
      } else {
        // Just return to stock (for pending/shipped/approved statuses)
        for (const item of inv.items) {
          if (!item.allocatedBatches) continue;
          for (const alloc of item.allocatedBatches) {
            const { data: sourceBatch } = await supabase
              .from('products')
              .select('*')
              .eq('id', alloc.batchId)
              .maybeSingle();
            
            if (sourceBatch) {
              await supabase
                .from('products')
                .update({ quantity: Number(sourceBatch.quantity || 0) + Number(alloc.quantity) })
                .eq('id', sourceBatch.id);
            } else {
               // Recreate if missing
               const { data: baseTemplate } = await supabase
                .from('products')
                .select('*')
                .eq('name', item.productName)
                .limit(1)
                .maybeSingle();

              await supabase.from('products').insert([{
                id: alloc.batchId,
                warehouse_id: inv.warehouseId,
                name: item.productName,
                code: baseTemplate?.code || 'AUTO',
                points: baseTemplate?.points || item.points,
                expiry: alloc.expiryDate,
                quantity: alloc.quantity,
                pieces_per_carton: baseTemplate?.pieces_per_carton || 1
              }]);
            }
          }
        }
      }
      
      await supabase.from(targetTable).update({ 
        status: 'cancelled',
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }).eq('id', id);
      await fetchData(true);
      return { success: true };
    } catch (err: any) { 
      console.error("Cancel Invoice Critical Error:", err);
      return { success: false, error: err.message }; 
    } finally {
      setIsProcessing(false);
    }
  };

  const updateInvoice = async (id: string, updatedItems: InvoiceItem[]) => {
    setIsProcessing(true);
    try {
      const isWarehouseOrder = String(id).startsWith('RKORD');
      const targetTable = isWarehouseOrder ? 'orders_warehouses' : 'invoices';
      
      if (currentUser?.role !== 'SUPER_ADMIN' && !currentUser?.permissions.editOrder) {
        throw new Error("عذراً، هذا الإجراء متاح لمدير النظام والمدققين فقط");
      }

      const inv = isWarehouseOrder ? ordersWarehouses.find(i => i.id === id) : invoices.find(i => i.id === id);
      if (!inv) throw new Error("الطلبية غير موجودة");

      // 1. Revert old items to warehouse (Return to stock)
      for (const oldItem of inv.items) {
        if (!oldItem.allocatedBatches) continue;
        for (const alloc of oldItem.allocatedBatches) {
          const { data: sourceBatch } = await supabase
            .from('products')
            .select('*')
            .eq('id', alloc.batchId)
            .maybeSingle();
          
          if (sourceBatch) {
            await supabase
              .from('products')
              .update({ quantity: Number(sourceBatch.quantity || 0) + Number(alloc.quantity) })
              .eq('id', sourceBatch.id);
          } else {
            // Re-create batch if it was deleted
            const { data: baseTemplate } = await supabase
              .from('products')
              .select('*')
              .eq('name', oldItem.productName)
              .limit(1)
              .maybeSingle();

            await supabase.from('products').insert([{
              id: alloc.batchId,
              warehouse_id: inv.warehouseId,
              name: oldItem.productName,
              code: baseTemplate?.code || oldItem.code || 'AUTO',
              points: baseTemplate?.points || oldItem.points,
              expiry: alloc.expiryDate,
              quantity: alloc.quantity,
              pieces_per_carton: baseTemplate?.pieces_per_carton || oldItem.piecesPerCarton || 1
            }]);
          }
        }
      }

      // 2. Apply new items (Deduct from stock using FEFO)
      const finalItems: InvoiceItem[] = [];
      for (const item of updatedItems) {
        const normalizedTarget = normalizeArabic(item.productName);
        
        // Fetch available batches
        const { data: fetchedBatches, error: fetchErr } = await supabase
          .from('products')
          .select('*')
          .eq('warehouse_id', inv.warehouseId)
          .gt('quantity', 0);

        if (fetchErr) throw fetchErr;

        const matchedBatches = (fetchedBatches || []).filter(p => normalizeArabic(p.name) === normalizedTarget);

        if (matchedBatches.length === 0) {
           throw new Error(`عجز كامل في مادة ${item.productName} في هذا المخزن`);
        }

        const freshBatches = matchedBatches.sort((a: any, b: any) => {
            const valA = getBatchSortValue(a.expiry);
            const valB = getBatchSortValue(b.expiry);
            if (valA !== valB) return valA.localeCompare(valB);
            return String(a.id).localeCompare(String(b.id));
        });

        const totalAvailable = freshBatches.reduce((sum, b) => sum + Number(b.quantity || 0), 0);
        const requiredQty = Number(item.quantity); 

        if (totalAvailable < requiredQty) {
           throw new Error(`عجز في مادة ${item.productName}. المطلوب: ${requiredQty}، المتوفر حالياً: ${totalAvailable}`);
        }

        let remainingToTake = requiredQty;
        const allocations: { batchId: string; expiryDate: string; quantity: number }[] = [];
        
        for (const b of freshBatches) {
          if (remainingToTake <= 0) break;
          
          const { data: currentBatchState } = await supabase
            .from('products')
            .select('quantity')
            .eq('id', b.id)
            .single();
            
          const realCurrentQty = Number(currentBatchState?.quantity || 0);
          if (realCurrentQty <= 0) continue; 

          const take = Math.min(realCurrentQty, remainingToTake);
          
          allocations.push({ 
            batchId: String(b.id), 
            expiryDate: b.expiry || '', 
            quantity: take 
          });

          remainingToTake -= take;
          
          const { error: updErr } = await supabase
            .from('products')
            .update({ quantity: realCurrentQty - take })
            .eq('id', b.id);
            
          if (updErr) throw updErr;
        }
        
        if (remainingToTake > 0) {
          throw new Error(`فشل في تأمين كامل الكمية لمادة ${item.productName}. المتبقي: ${remainingToTake}`);
        }
        
        allocations.sort((a, b) => getBatchSortValue(a.expiryDate).localeCompare(getBatchSortValue(b.expiryDate)));
        
        finalItems.push({ ...item, quantity: requiredQty, allocatedBatches: allocations });
      }

      const totalPoints = finalItems.reduce((s, i) => s + (Number(i.points || 0) * Number(i.quantity || 0)), 0);
      const totalPriceUSD = finalItems.reduce((s, i) => s + (Number(i.priceUSD || 0) * Number(i.quantity || 0)), 0);

      const { error } = await supabase
        .from(targetTable)
        .update({ 
          items: finalItems,
          total_points: totalPoints,
          total_price_usd: totalPriceUSD
        })
        .eq('id', id);

      if (error) throw error;
      await fetchData(true);
      showToast("تم تحديث الطلبية بنجاح", "success");
    } catch (err: any) {
      console.error("Update Invoice Error:", err);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteInvoice = async (id: string) => { 
    setIsProcessing(true);
    try {
      const isWarehouseOrder = String(id).startsWith('RKORD');
      const targetTable = isWarehouseOrder ? 'orders_warehouses' : 'invoices';
      await supabase.from(targetTable).delete().eq('id', id); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };

  const addApprovedInvoice = async (invoice: Invoice) => { 
    setIsProcessing(true);
    try {
      await supabase.from('approved_invoices').insert([{ 
        id: invoice.id, 
        date: invoice.date, 
        items: invoice.items, 
        warehouse_id: invoice.warehouseId, 
        agent_id: String(invoice.agentId), 
        total_points: Number(invoice.totalPoints || 0) 
      }]); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };

  const addMatchInvoice = async (invoice: MatchInvoice) => { 
    setIsProcessing(true);
    try {
      await supabase.from('match_invoices').insert([{ 
        id: invoice.id, 
        date: invoice.date, 
        items: invoice.items, 
        total_difference: Number(invoice.totalDifference || 0),
        user_id: invoice.userId,
        user_name: invoice.userName
      }]); 
      await fetchData(true); 
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Helper to safely restore deducted transfer items to the source warehouse
  const restoreTransferItemsToSource = async (transfer: any) => {
    const rawItems = transfer.items;
    let items: any[] = [];
    if (Array.isArray(rawItems)) {
      items = rawItems;
    } else if (typeof rawItems === 'string') {
      items = safeParse(rawItems, []);
    }

    const sourceWhId = String(transfer.source_warehouse_id || transfer.sourceWarehouseId || '');
    if (!sourceWhId) return;

    const { data: sourceWhProducts } = await supabase
      .from('products')
      .select('*')
      .eq('warehouse_id', sourceWhId);

    const existingSourceProducts: any[] = sourceWhProducts || [];

    for (const item of items) {
      const itemName = String(item?.productName || item?.name || item?.product_name || '').trim();
      const normItemName = normalizeArabic(itemName);
      const batches = (item?.allocatedBatches && Array.isArray(item.allocatedBatches) && item.allocatedBatches.length > 0)
        ? item.allocatedBatches
        : [{ batchId: item?.productId, expiryDate: item?.expiryDate || '', quantity: Number(item?.quantity || 0) }];

      for (const batch of batches) {
        const batchQty = Number(batch?.quantity || 0);
        if (batchQty <= 0) continue;

        const batchExp = String(batch?.expiryDate || item?.expiryDate || '').trim();
        const normBatchExp = normalizeExpiryDate(batchExp);

        let targetProduct: any = null;
        if (batch.batchId) {
          targetProduct = existingSourceProducts.find((p: any) => String(p.id) === String(batch.batchId));
        }

        if (!targetProduct) {
          targetProduct = existingSourceProducts.find((p: any) => {
            const pNameNorm = normalizeArabic(String(p.name || ''));
            const pExpNorm = normalizeExpiryDate(p.expiry || p.expiryDate || p.expiry_date || '');
            const matchName = pNameNorm === normItemName;
            const matchExp = normBatchExp ? (pExpNorm === normBatchExp) : (!pExpNorm);
            return matchName && matchExp;
          });
        }

        if (targetProduct) {
          const newQty = Number(targetProduct.quantity || 0) + batchQty;
          targetProduct.quantity = newQty;
          await supabase.from('products').update({
            quantity: newQty,
            expiry: normBatchExp || targetProduct.expiry || '',
            expiryDate: normBatchExp || targetProduct.expiryDate || ''
          }).eq('id', targetProduct.id);
        } else {
          const newRow = {
            id: crypto.randomUUID(),
            warehouse_id: sourceWhId,
            warehouseId: sourceWhId,
            name: itemName || 'منتج',
            code: item?.code || '',
            points: Number(item?.points || 0),
            expiry: normBatchExp,
            expiryDate: normBatchExp,
            quantity: batchQty,
            pieces_per_carton: Number(item?.piecesPerCarton || 1),
            piecesPerCarton: Number(item?.piecesPerCarton || 1),
            user_id: currentUser?.id,
            user_name: currentUser?.name
          };
          existingSourceProducts.push(newRow);
          await supabase.from('products').insert([newRow]);
        }
      }
    }
  };

  const addTransfer = async (transfer: Transfer) => {
    setIsProcessing(true);
    try {
      const updatedTransferItems: TransferItem[] = [];

      // Direct DB Fetch for source warehouse products
      const { data: sourceBatches, error: fetchErr } = await supabase
        .from('products')
        .select('*')
        .eq('warehouse_id', transfer.sourceWarehouseId)
        .gt('quantity', 0);

      if (fetchErr) throw fetchErr;
      const allSourceBatches: any[] = sourceBatches || [];

      for (const item of transfer.items) {
        const normalizedTarget = normalizeArabic(item.productName);
        const itemNormExpiry = normalizeExpiryDate(item.expiryDate);
        
        let matchedBatches = allSourceBatches.filter((p: any) => normalizeArabic(p.name || '') === normalizedTarget);

        if (matchedBatches.length === 0) {
           throw new Error(`عجز كامل في مادة ${item.productName} في المخزن المصدر`);
        }

        // *** STRICT EXPIRY DATE MATCHING ***
        if (itemNormExpiry) {
            const exactExpiryMatches = matchedBatches.filter((b: any) => {
              const bNormExp = normalizeExpiryDate(b.expiry || b.expiryDate || b.expiry_date || '');
              return bNormExp === itemNormExpiry;
            });

            if (exactExpiryMatches.length > 0) {
                matchedBatches = exactExpiryMatches;
            } else {
                throw new Error(`عجز في مادة ${item.productName} للصلاحية (${formatExpiryDate(item.expiryDate)}). لا يتوفر رصيد لهذا التاريخ.`);
            }
        }

        // FEFO Sorting as secondary sort
        matchedBatches.sort((a: any, b: any) => {
            const valA = getBatchSortValue(a.expiry || a.expiryDate || a.expiry_date);
            const valB = getBatchSortValue(b.expiry || b.expiryDate || b.expiry_date);
            if (valA !== valB) return valA.localeCompare(valB);
            return String(a.id).localeCompare(String(b.id));
        });

        // Pre-check Availability
        const totalAvailable = matchedBatches.reduce((sum, b) => sum + Number(b.quantity || 0), 0);
        const requiredQty = item.quantity;

        if (totalAvailable < requiredQty) {
           const expLabel = itemNormExpiry ? ` للصلاحية (${formatExpiryDate(item.expiryDate)})` : '';
           throw new Error(`عجز في مادة ${item.productName}${expLabel}. المتوفر: ${totalAvailable}، المطلوب: ${requiredQty}`);
        }

        let remainingToTransfer = requiredQty;
        const allocations: { batchId?: string; expiryDate: string; quantity: number }[] = [];

        // Deduction Loop
        for (const batch of matchedBatches) {
           if (remainingToTransfer <= 0) break;
           
           const currentBatchQty = Number(batch.quantity || 0);
           if (currentBatchQty <= 0) continue;

           const take = Math.min(currentBatchQty, remainingToTransfer);
           const batchExpNormalized = normalizeExpiryDate(batch.expiry || batch.expiryDate || batch.expiry_date) || itemNormExpiry;
           
           allocations.push({
              batchId: String(batch.id), 
              expiryDate: batchExpNormalized,
              quantity: take
           });

           const newBatchQty = currentBatchQty - take;
           batch.quantity = newBatchQty;

           await supabase
             .from('products')
             .update({ 
               quantity: newBatchQty,
               expiry: batchExpNormalized,
               expiryDate: batchExpNormalized
             })
             .eq('id', batch.id);

           remainingToTransfer -= take;
        }

        if (remainingToTransfer > 0) {
           throw new Error(`فشل استقطاع كامل كمية مادة ${item.productName}`);
        }

        updatedTransferItems.push({
           ...item,
           expiryDate: itemNormExpiry || item.expiryDate,
           quantity: requiredQty,
           allocatedBatches: allocations
        });
      }

      const { error: insertError } = await supabase.from('transfers').insert([{ 
        id: transfer.id, 
        source_warehouse_id: transfer.sourceWarehouseId, 
        destination_warehouse_id: transfer.destinationWarehouseId, 
        date: transfer.date, 
        items: updatedTransferItems, 
        status: transfer.status || 'pending', 
        notes: transfer.notes,
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }]);

      if (insertError) throw insertError;

      await fetchData(true);
    } catch (err: any) { 
      console.error("Add Transfer Critical Error:", err);
      throw err; 
    } finally {
      setIsProcessing(false);
    }
  };

  const approveTransfer = async (id: string) => {
    setIsProcessing(true);
    try {
      let transferToApprove: any = null;
      const { data: currentTransfer, error: fetchError } = await supabase
        .from('transfers')
        .select('*')
        .eq('id', id)
        .maybeSingle();
        
      if (currentTransfer) {
        transferToApprove = currentTransfer;
      } else {
        const localTransfer = transfers.find(t => String(t.id) === String(id));
        if (localTransfer) {
          transferToApprove = {
            id: localTransfer.id,
            source_warehouse_id: localTransfer.sourceWarehouseId,
            destination_warehouse_id: localTransfer.destinationWarehouseId,
            date: localTransfer.date,
            items: localTransfer.items,
            status: localTransfer.status,
            notes: localTransfer.notes
          };
        }
      }

      if (!transferToApprove) {
        throw new Error("لم يتم العثور على بيانات سند التحويل");
      }

      if (transferToApprove.status === 'approved') {
        showToast("تم اعتماد واستلام هذا السند مسبقاً", "info");
        await fetchData(true);
        return;
      }

      if (transferToApprove.status === 'cancelled') {
        throw new Error("لا يمكن تأكيد استلام سند ملغي");
      }

      const rawItems = transferToApprove.items;
      const items: any[] = Array.isArray(rawItems) ? rawItems : safeParse(rawItems, []);

      const destWhId = String(transferToApprove.destination_warehouse_id || transferToApprove.destinationWarehouseId || '');
      if (!destWhId) {
        throw new Error("لم يتم تحديد المخزن المستلم بدقة");
      }

      // Fetch all existing products in destination warehouse for accurate matching
      const { data: destWhProducts } = await supabase
        .from('products')
        .select('*')
        .eq('warehouse_id', destWhId);

      const existingDestProducts: any[] = destWhProducts || [];

      for (const item of items) {
        const itemName = String(item?.productName || item?.name || item?.product_name || '').trim();
        const normItemName = normalizeArabic(itemName);
        const itemCode = String(item?.code || item?.productCode || item?.product_code || '').trim();
        const itemPoints = Number(item?.points || 0);
        const itemPpc = Number(item?.piecesPerCarton || item?.pieces_per_carton || 1);

        let baseProductInfo: any = {};
        if (item?.productId) {
          const foundLocal = products.find(p => String(p.id) === String(item.productId));
          if (foundLocal) {
            baseProductInfo = {
              name: foundLocal.name,
              code: foundLocal.code,
              points: foundLocal.points,
              pieces_per_carton: foundLocal.piecesPerCarton,
              price_usd: foundLocal.priceUSD,
              type: foundLocal.type,
              category: foundLocal.category,
              unit: foundLocal.unit,
              image_url: foundLocal.imageUrl,
              notes: foundLocal.notes
            };
          } else {
            const { data: baseProd } = await supabase.from('products').select('*').eq('id', item.productId).limit(1);
            if (baseProd && baseProd.length > 0) baseProductInfo = baseProd[0];
          }
        }

        const batchesToProcess = (item?.allocatedBatches && Array.isArray(item.allocatedBatches) && item.allocatedBatches.length > 0)
          ? item.allocatedBatches
          : [{ batchId: item?.productId, expiryDate: item?.expiryDate || '', quantity: Number(item?.quantity || 0) }];

        for (const batch of batchesToProcess) {
          const batchQty = Number(batch?.quantity || 0);
          if (batchQty <= 0) continue;

          const batchExp = String(batch?.expiryDate || item?.expiryDate || '').trim();
          const normalizedBatchExp = normalizeExpiryDate(batchExp);

          let batchProductInfo = { ...baseProductInfo };
          if (batch?.batchId) {
            const foundLocalBatch = products.find(p => String(p.id) === String(batch.batchId));
            if (foundLocalBatch) {
              batchProductInfo = {
                name: foundLocalBatch.name,
                code: foundLocalBatch.code,
                points: foundLocalBatch.points,
                pieces_per_carton: foundLocalBatch.piecesPerCarton,
                price_usd: foundLocalBatch.priceUSD,
                type: foundLocalBatch.type,
                category: foundLocalBatch.category,
                unit: foundLocalBatch.unit,
                image_url: foundLocalBatch.imageUrl,
                notes: foundLocalBatch.notes
              };
            }
          }

          // Match product in destination warehouse strictly by normalized name and normalized expiry
          const exist = existingDestProducts.find((x: any) => {
            const xNameNorm = normalizeArabic(String(x?.name || ''));
            const xExpNorm = normalizeExpiryDate(String(x?.expiry || x?.expiryDate || x?.expiry_date || ''));
            
            const matchName = xNameNorm === normItemName;
            const matchExp = normalizedBatchExp 
              ? (xExpNorm === normalizedBatchExp)
              : (!xExpNorm);

            return matchName && matchExp;
          });

          if (exist) {
            const newQty = Number(exist.quantity || 0) + batchQty;
            exist.quantity = newQty;
            await supabase.from('products').update({
              quantity: newQty,
              expiry: normalizedBatchExp,
              expiryDate: normalizedBatchExp
            }).eq('id', exist.id);
          } else {
            const newProdRow = { 
              id: crypto.randomUUID(),
              warehouse_id: destWhId, 
              warehouseId: destWhId,
              name: itemName || batchProductInfo.name || 'منتج', 
              code: itemCode || batchProductInfo.code || '', 
              points: itemPoints || batchProductInfo.points || 0, 
              expiry: normalizedBatchExp, 
              expiryDate: normalizedBatchExp,
              quantity: batchQty, 
              pieces_per_carton: itemPpc || batchProductInfo.pieces_per_carton || 1,
              piecesPerCarton: itemPpc || batchProductInfo.pieces_per_carton || 1,
              price_usd: Number(batchProductInfo.price_usd || baseProductInfo.price_usd || 0),
              type: batchProductInfo.type || baseProductInfo.type || 'normal',
              category: batchProductInfo.category || baseProductInfo.category || '',
              unit: batchProductInfo.unit || baseProductInfo.unit || '',
              image_url: batchProductInfo.image_url || baseProductInfo.image_url || '',
              notes: batchProductInfo.notes || baseProductInfo.notes || '',
              user_id: currentUser?.id,
              user_name: currentUser?.name
            };
            existingDestProducts.push(newProdRow);
            await supabase.from('products').insert([newProdRow]);
          }
        }
      }
      
      let updateRes = await supabase.from('transfers').update({ 
        status: 'approved',
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }).eq('id', id);

      if (updateRes.error) {
        await supabase.from('transfers').update({ 
          status: 'approved' 
        }).eq('id', id);
      }
      
      await fetchData(true);
      showToast("تم تأكيد الاستلام وترحيل المواد إلى مخزن المستلم بنجاح", "success");
    } catch (err: any) { 
      console.error("Approve Transfer Error:", err);
      showToast(err?.message || "حدث خطأ أثناء تأكيد الاستلام", "error");
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const updateTransfer = async (id: string, updatedTransfer: Transfer) => {
    setIsProcessing(true);
    try {
      const { data: oldTransfer, error: fetchError } = await supabase
        .from('transfers')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError || !oldTransfer) throw new Error("لم يتم العثور على بيانات التحويل");
      if (oldTransfer.status === 'approved') throw new Error("لا يمكن تعديل سند تم اعتماده واستلامه");
      if (oldTransfer.status === 'cancelled') throw new Error("لا يمكن تعديل سند ملغي");

      const oldItems = Array.isArray(oldTransfer.items) ? oldTransfer.items : safeParse(oldTransfer.items, []);
      
      // Determine if stock-affecting fields have actually changed
      const sourceChanged = String(oldTransfer.source_warehouse_id || '') !== String(updatedTransfer.sourceWarehouseId || '');
      
      const newItems = updatedTransfer.items || [];
      const itemsMatch = oldItems.length === newItems.length && oldItems.every((oldIt: any, idx: number) => {
        const newIt = newItems[idx];
        if (!newIt) return false;
        const nameMatch = normalizeArabic(oldIt.productName || oldIt.name || '') === normalizeArabic(newIt.productName || (newIt as any).name || '');
        const expMatch = normalizeExpiryDate(oldIt.expiryDate) === normalizeExpiryDate(newIt.expiryDate);
        const qtyMatch = Number(oldIt.quantity) === Number(newIt.quantity);
        return nameMatch && expMatch && qtyMatch;
      });

      const isStockChange = sourceChanged || !itemsMatch;

      // If only status / notes changed without altering items or source warehouse
      if (!isStockChange) {
        const { error: metaUpdateErr } = await supabase
          .from('transfers')
          .update({
            status: updatedTransfer.status || oldTransfer.status,
            notes: updatedTransfer.notes,
            destination_warehouse_id: updatedTransfer.destinationWarehouseId || oldTransfer.destination_warehouse_id,
            date: updatedTransfer.date || oldTransfer.date,
            user_id: currentUser?.id,
            user_name: currentUser?.name
          })
          .eq('id', id);

        if (metaUpdateErr) throw metaUpdateErr;
        await fetchData(true);
        showToast("تم تحديث حالة وملاحظات السند بنجاح", "success");
        return;
      }

      // 1. Restore previous items to the original source warehouse
      await restoreTransferItemsToSource(oldTransfer);

      // 2. Fetch fresh stock from the updated source warehouse
      const finalItems: TransferItem[] = [];
      const { data: sourceBatches, error: fetchErr } = await supabase
        .from('products')
        .select('*')
        .eq('warehouse_id', updatedTransfer.sourceWarehouseId)
        .gt('quantity', 0);

      if (fetchErr) throw fetchErr;
      const allSourceBatches: any[] = sourceBatches || [];

      for (const item of updatedTransfer.items) {
        const normalizedTarget = normalizeArabic(item.productName);
        const itemNormExpiry = normalizeExpiryDate(item.expiryDate);
        
        let matchedBatches = allSourceBatches.filter((p: any) => normalizeArabic(p.name || '') === normalizedTarget);

        if (matchedBatches.length === 0) {
          throw new Error(`عجز كامل في مادة ${item.productName} في المخزن المصدر`);
        }

        // Strict expiry matching
        if (itemNormExpiry) {
            const exactExpiryMatches = matchedBatches.filter((b: any) => {
              const bNormExp = normalizeExpiryDate(b.expiry || b.expiryDate || b.expiry_date || '');
              return bNormExp === itemNormExpiry;
            });

            if (exactExpiryMatches.length > 0) {
              matchedBatches = exactExpiryMatches;
            } else {
              throw new Error(`عجز في مادة ${item.productName} للصلاحية (${formatExpiryDate(item.expiryDate)}). لا يتوفر رصيد كافٍ لهذا التاريخ.`);
            }
        }

        matchedBatches.sort((a: any, b: any) => {
            const valA = getBatchSortValue(a.expiry || a.expiryDate || a.expiry_date);
            const valB = getBatchSortValue(b.expiry || b.expiryDate || b.expiry_date);
            if (valA !== valB) return valA.localeCompare(valB);
            return String(a.id).localeCompare(String(b.id));
        });

        const totalAvailable = matchedBatches.reduce((sum, b) => sum + Number(b.quantity || 0), 0);
        const requiredQty = item.quantity;
        if (totalAvailable < requiredQty) {
          const expLabel = itemNormExpiry ? ` للصلاحية (${formatExpiryDate(item.expiryDate)})` : '';
          throw new Error(`عجز في مادة ${item.productName}${expLabel}. المتوفر: ${totalAvailable}، المطلوب: ${requiredQty}`);
        }

        let remainingToTransfer = requiredQty;
        const allocations: { batchId?: string; expiryDate: string; quantity: number }[] = [];

        for (const batch of matchedBatches) {
           if (remainingToTransfer <= 0) break;
           const currentBatchQty = Number(batch.quantity || 0);
           if (currentBatchQty <= 0) continue;

           const take = Math.min(currentBatchQty, remainingToTransfer);
           const batchExpNormalized = normalizeExpiryDate(batch.expiry || batch.expiryDate || batch.expiry_date) || itemNormExpiry;
           
           allocations.push({ 
             batchId: String(batch.id), 
             expiryDate: batchExpNormalized, 
             quantity: take 
           });

           const newBatchQty = currentBatchQty - take;
           batch.quantity = newBatchQty;

           await supabase.from('products').update({ 
             quantity: newBatchQty,
             expiry: batchExpNormalized,
             expiryDate: batchExpNormalized
           }).eq('id', batch.id);

           remainingToTransfer -= take;
        }

        finalItems.push({ 
          ...item, 
          expiryDate: itemNormExpiry || item.expiryDate,
          quantity: requiredQty, 
          allocatedBatches: allocations 
        });
      }

      // 3. Update transfer record
      const { error: updateError } = await supabase
        .from('transfers')
        .update({ 
          items: finalItems, 
          notes: updatedTransfer.notes, 
          source_warehouse_id: updatedTransfer.sourceWarehouseId, 
          destination_warehouse_id: updatedTransfer.destinationWarehouseId,
          status: updatedTransfer.status || oldTransfer.status || 'pending',
          date: updatedTransfer.date || oldTransfer.date,
          user_id: currentUser?.id,
          user_name: currentUser?.name
        })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchData(true);
      showToast("تم تحديث سند التحويل بنجاح", "success");
    } catch (err: any) {
      console.error("Update Transfer Error:", err);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteTransfer = async (id: string) => {
    setIsProcessing(true);
    try {
      const { data: currentTransfer, error: fetchError } = await supabase
        .from('transfers')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (fetchError || !currentTransfer) throw new Error("لم يتم العثور على السند في قاعدة البيانات");

      if (currentTransfer.status === 'approved') {
        throw new Error("لا يمكن إلغاء سند معتمد ومستلم بالفعل");
      }

      if (currentTransfer.status === 'pending' || currentTransfer.status === 'shipped') {
        await restoreTransferItemsToSource(currentTransfer);
      }
      
      let updateError = (await supabase
        .from('transfers')
        .update({ 
          status: 'cancelled',
          user_id: currentUser?.id,
          user_name: currentUser?.name
        })
        .eq('id', id)).error;

      if (updateError) {
        updateError = (await supabase
          .from('transfers')
          .update({ 
            status: 'cancelled'
          })
          .eq('id', id)).error;
      }

      if (updateError) throw updateError;
      
      await fetchData(true);
      showToast("تم إلغاء السند وإعادة كافة المواد للمخزن بنجاح", "info");
    } catch (err: any) { 
      console.error("Cancel Transfer Error:", err);
      showToast(err.message || "فشل إلغاء السند واسترجاع المواد", "error");
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteProductInvoice = async (id: string) => {
    setIsProcessing(true);
    try {
      await supabase.from('product_invoices').delete().eq('id', id);
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };
  const deleteMatchInvoice = async (id: string) => {
    setIsProcessing(true);
    try {
      await supabase.from('match_invoices').delete().eq('id', id);
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteApprovedInvoice = async (id: string) => {
    setIsProcessing(true);
    try {
      await supabase.from('approved_invoices').delete().eq('id', id);
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const generateInvoiceId = async (type: string) => {
    let prefix = 'RKNAK'; // Default cash
    if (type === 'deferred') prefix = 'RKMOA';
    else if (type === 'flexible') prefix = 'RKMRN';
    else if (type === 'commission') prefix = 'RKAMO';
    else if (type === 'offer') prefix = 'RKOFF';
    else if (type === 'order') prefix = 'RKORD';

    try {
      const targetTable = type === 'order' ? 'orders_warehouses' : 'invoices';
      let maxNumber = 0;

      // 1. Check local state first
      const listToSearch = type === 'order' ? ordersWarehousesRef.current : invoicesRef.current;
      for (const item of listToSearch) {
        if (item.id && String(item.id).startsWith(prefix)) {
           const num = parseInt(String(item.id).replace(prefix, ''), 10);
           if (!isNaN(num) && num > maxNumber) {
             maxNumber = num;
           }
        }
      }

      // 2. Fetch from DB
      const { data, error } = await supabase
        .from(targetTable)
        .select('id')
        .like('id', `${prefix}%`)
        .order('id', { ascending: false })
        .limit(100);

      if (!error && data) {
         for (const row of data) {
           const num = parseInt(String(row.id).replace(prefix, ''), 10);
           if (!isNaN(num) && num > maxNumber) {
             maxNumber = num;
           }
         }
      }
      
      const { data: latestDate } = await supabase
        .from(targetTable)
        .select('id')
        .like('id', `${prefix}%`)
        .order('date', { ascending: false })
        .limit(100);

      if (latestDate) {
        for (const row of latestDate) {
          const num = parseInt(String(row.id).replace(prefix, ''), 10);
          if (!isNaN(num) && num > maxNumber) {
            maxNumber = num;
          }
        }
      }
      
      return `${prefix}${maxNumber + 1}`;
    } catch (err) {
      console.error("Error generating invoice ID:", err);
      return `${prefix}${Date.now()}${Math.floor(Math.random() * 100)}`;
    }
  };
  
  const addSale = async (sale: Sale) => {
    setIsProcessing(true);
    let insertedId: string | null = null;
    
    try {
      const updatedItems: any[] = [];
      const productUpdates: { id: string; oldQty: number; newQty: number }[] = [];
      
      // 1. Group items by product identity
      const groupedItems: Record<string, typeof sale.items> = {};
      sale.items.forEach(item => {
        const key = item.productCode || item.productName;
        if (!groupedItems[key]) groupedItems[key] = [];
        groupedItems[key].push(item);
      });

      const { data: allBatches, error: fetchErr } = await supabase
        .from('products')
        .select('*')
        .eq('warehouse_id', sale.warehouseId)
        .gt('quantity', 0);

      if (fetchErr) throw fetchErr;
      let batchesPool = (allBatches || []).map(b => ({ ...b, currentQty: Number(b.quantity || 0), originalQty: Number(b.quantity || 0) }));

      // 2. Local Allocation
      for (const [productKey, items] of Object.entries(groupedItems)) {
        const totalRequested = items.reduce((sum, i) => sum + i.quantity, 0);
        const normalizedTarget = normalizeArabic(items[0].productName);
        const targetCode = items[0].productCode;

        const matchedBatches = batchesPool.filter(p => {
          const nameMatch = normalizeArabic(p.name) === normalizedTarget;
          const codeMatch = targetCode && p.code === targetCode;
          const typeMatch = !items[0].productType || p.type === items[0].productType;
          return (codeMatch || nameMatch) && typeMatch;
        }).sort((a, b) => getBatchSortValue(a.expiry).localeCompare(getBatchSortValue(b.expiry)));

        const totalAvailable = matchedBatches.reduce((sum, b) => sum + b.currentQty, 0);
        if (totalAvailable < totalRequested) {
          throw new Error(`عجز في مادة ${items[0].productName}`);
        }

        for (const item of items) {
          let itemRemaining = item.quantity;
          const allocations: { batchId: string; expiryDate: string; quantity: number }[] = [];
          
          for (const b of matchedBatches) {
            if (itemRemaining <= 0) break;
            if (b.currentQty <= 0) continue;
            
            const take = Math.min(b.currentQty, itemRemaining);
            allocations.push({ batchId: String(b.id), expiryDate: b.expiry || '', quantity: take });
            b.currentQty -= take;
            itemRemaining -= take;
            
            productUpdates.push({ id: String(b.id), oldQty: b.originalQty, newQty: b.currentQty });
          }
          
          updatedItems.push({ 
            productId: item.productId, 
            productName: item.productName, 
            productCode: item.productCode,
            quantity: item.quantity, 
            points: item.points, 
            priceUSD: item.priceUSD,
            piecesPerCarton: 1,
            allocatedBatches: allocations 
          });
        }
      }

      // Perform stock updates first
      const completedUpdates: typeof productUpdates = [];
      try {
        for (const update of productUpdates) {
          const { error: updErr } = await supabase.from('products').update({ quantity: update.newQty }).eq('id', update.id);
          if (updErr) throw updErr;
          completedUpdates.push(update);
        }

        // 3. Insert Invoice
        let finalId = await generateInvoiceId(sale.type || 'cash');
        let success = false;
        let attempts = 0;
        
        const isBackdated = new Date(sale.date).toDateString() !== new Date().toDateString() && new Date(sale.date) < new Date();
        const backdatedStatus = isBackdated ? 'pending' : 'approved';

        while (!success && attempts < 10) {
          const { error: insertError } = await supabase.from('invoices').insert([{ 
            id: finalId, 
            agent_id: String(sale.centerId), 
            warehouse_id: sale.warehouseId, 
            date: sale.date, 
            items: updatedItems, 
            total_points: sale.totalPoints, 
            total_price_usd: sale.totalPriceUSD,
            exchange_rate: sale.exchangeRate,
            customer_name: sale.customerName,
            customer_phone: sale.customerPhone,
            customer_membership: sale.customerMembership,
            type: sale.type || 'cash',
            offer_id: sale.offerId,
            member_id: sale.memberId,
            status: 'approved', 
            created_by: currentUser?.id,
            user_name: currentUser?.name,
            center_name: sale.centerName,
            is_backdated: isBackdated,
            backdated_status: backdatedStatus
          }]);

          if (!insertError) {
            success = true;
            insertedId = finalId;
          } else if (insertError.code === '23505' || (insertError.message && insertError.message.includes('unique constraint'))) {
            attempts++;
            await new Promise(r => setTimeout(r, Math.random() * 300 + 100));
            finalId = await generateInvoiceId(sale.type || 'cash');
          } else {
            throw insertError;
          }
        }

        if(!success) throw new Error("فشل توليد رقم فاتورة فريد");

      } catch (innerErr) {
        // Rollback stock
        for (const r of completedUpdates) {
          await supabase.from('products').update({ quantity: r.oldQty }).eq('id', r.id);
        }
        throw innerErr;
      }

      // 5. Handle Deferred if applicable
      if (sale.type === 'deferred') {
        for (const item of sale.items) {
          await supabase.from('deferred_points').insert([{
            id: crypto.randomUUID(),
            center_id: String(sale.centerId),
            member_id: String(sale.memberId || ''),
            sale_id: String(insertedId),
            product_id: item.productId,
            product_name: item.productName,
            points: item.points * item.quantity,
            quantity: item.quantity,
            release_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'pending',
            user_name: currentUser?.name,
            center_name: sale.centerName,
            member_name: sale.customerName,
            membership_number: sale.customerMembership
          }]);
        }
      }

      fetchData(true).catch(console.error);
      showToast(`تم تسجيل عملية البيع بنجاح: ${insertedId}`, "success");
    } catch (err: any) {
      console.error("[StoreContext] addSale error:", err);
      showToast(err.message || "خطأ في تسجيل البيع", "error");
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };


  const convertDeferredToCash = async (
    saleId: string, 
    conversionItems: { index: number; quantity: number }[], 
    memberInfo: { id?: string; name: string; membershipNumber: string; phone?: string }
  ) => {
    setIsProcessing(true);
    try {
      const sale = sales.find(s => s.id === saleId);
      if (!sale) throw new Error("الفاتورة غير موجودة");

      // 1. Handle Member (Create if new)
      let finalMemberId = memberInfo.id;
      if (!finalMemberId) {
        const newMemberId = crypto.randomUUID();
        const payload: any = {
          id: newMemberId,
          name: memberInfo.name,
          membership_number: memberInfo.membershipNumber,
          phone: memberInfo.phone || null,
          type: 'deferred',
          created_at: new Date().toISOString()
        };
        
        let { error: memberError } = await supabase.from('offer_members').insert([payload]);
        if (memberError && memberError.message.includes('Could not find the')) {
          console.warn("Schema mismatch detected. Falling back to base legacy columns...", memberError.message);
          const legacyPayload = {
            id: newMemberId,
            name: memberInfo.name,
            phone: memberInfo.phone || null,
            membership_number: memberInfo.membershipNumber,
            created_at: new Date().toISOString()
          };
          const fallbackResult = await supabase.from('offer_members').insert([legacyPayload]);
          memberError = fallbackResult.error;
        }

        if (memberError) {
          console.error("Error creating deferred member:", memberError);
          throw new Error("خطأ في إنشاء عضوية الآجل: " + memberError.message);
        }
        finalMemberId = newMemberId;
      }

      const itemsToConvert: any[] = [];
      const itemsToKeep: any[] = [];

      sale.items.forEach((item, idx) => {
        const conversion = conversionItems.find(c => c.index === idx);
        if (conversion) {
          // Add to conversion list
          itemsToConvert.push({
            ...item,
            quantity: conversion.quantity
          });

          // If there's a remainder, add to keep list
          if (item.quantity > conversion.quantity) {
            itemsToKeep.push({
              ...item,
              quantity: item.quantity - conversion.quantity
            });
          }
        } else {
          // Not selected for conversion, keep it all
          itemsToKeep.push(item);
        }
      });

      if (itemsToConvert.length === 0) return null;

      let newInvoiceId = saleId;

      if (itemsToKeep.length === 0) {
        // Full conversion: Update existing invoice
        await supabase.from('invoices').update({ 
          type: 'cash',
          member_id: finalMemberId,
          customer_name: memberInfo.name,
          customer_membership: memberInfo.membershipNumber,
          customer_phone: memberInfo.phone || sale.customerPhone,
          notes: (sale.notes || '') + ` (محولة بالكامل إلى نقدية للعضو ${memberInfo.name})`
        }).eq('id', saleId);
      } else {
        // Partial conversion: Split the invoice
        const newTotalPoints = itemsToKeep.reduce((sum, i) => sum + (i.points * i.quantity), 0);
        const newTotalPriceUSD = itemsToKeep.reduce((sum, i) => sum + ((i.priceUSD || 0) * i.quantity), 0);
        
        await supabase.from('invoices').update({ 
          items: itemsToKeep,
          total_points: newTotalPoints,
          total_price_usd: newTotalPriceUSD,
          notes: (sale.notes || '') + ` (تم تحويل جزء من المنتجات إلى فاتورة نقدية)`
        }).eq('id', saleId);

        // Create new cash invoice
        const cashTotalPoints = itemsToConvert.reduce((sum, i) => sum + (i.points * i.quantity), 0);
        const cashTotalPriceUSD = itemsToConvert.reduce((sum, i) => sum + ((i.priceUSD || 0) * i.quantity), 0);
        
        newInvoiceId = await generateInvoiceId('cash');
        await supabase.from('invoices').insert([{
          id: newInvoiceId,
          agent_id: String(sale.centerId),
          warehouse_id: sale.warehouseId,
          date: new Date().toISOString(),
          items: itemsToConvert,
          total_points: cashTotalPoints,
          total_price_usd: cashTotalPriceUSD,
          exchange_rate: sale.exchangeRate,
          customer_name: memberInfo.name,
          customer_phone: memberInfo.phone || sale.customerPhone,
          customer_membership: memberInfo.membershipNumber,
          type: 'cash',
          member_id: finalMemberId,
          status: 'approved',
          created_by: currentUser?.id,
          user_name: currentUser?.name,
          center_name: sale.centerName,
          notes: `محولة من فاتورة مؤجلة رقم ${saleId}`
        }]);
      }

      // 2. Update/Delete deferred points
      for (const conversion of conversionItems) {
        const item = sale.items[conversion.index];
        
        // Fetch the specific deferred point record
        const { data: dpRecords } = await supabase
          .from('deferred_points')
          .select('*')
          .eq('sale_id', saleId)
          .eq('product_id', item.productId);

        if (dpRecords && dpRecords.length > 0) {
          const dp = dpRecords[0];
          if (dp.quantity <= conversion.quantity) {
            // Delete if fully converted
            await supabase.from('deferred_points').delete().eq('id', dp.id);
          } else {
            // Update if partially converted
            const newQty = dp.quantity - conversion.quantity;
            const newPoints = (dp.points / dp.quantity) * newQty;
            await supabase.from('deferred_points').update({
              quantity: newQty,
              points: newPoints
            }).eq('id', dp.id);
          }
        }
      }

      await fetchData(true);
      showToast('تم تحويل المنتجات المختارة إلى نقدية بنجاح', 'success');
      return newInvoiceId;
    } catch (error: any) {
      console.error("Error converting deferred to cash:", error);
      showToast('خطأ في التحويل: ' + error.message, 'error');
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const requestCancellation = async (id: string, sn: string, reason: string) => {
    setIsProcessing(true);
    try {
      // We don't validate SN strictly in the backend, we just rely on UI passing it correctly.
      if (!sn || (sn.trim().toUpperCase() !== id.slice(0, 8).toUpperCase() && sn.trim().toUpperCase() !== id.toUpperCase())) {
        // Just as an example, or UI validates it. Let's assume UI passes exactly the ID.
      }
      const { error } = await supabase.from('invoices').update({ status: 'pending_cancellation', cancellation_reason: reason }).eq('id', id);
      if (error) {
        if (error.message.includes('schema cache')) {
          // If schema cache hasn't refreshed, we might need to fallback to just setting status
          const fallback = await supabase.from('invoices').update({ status: 'pending_cancellation' }).eq('id', id);
          if (fallback.error) throw fallback.error;
        } else {
          throw error;
        }
      }
      
      const newInvoices = invoicesRef.current.map(inv => 
        inv.id === id ? { ...inv, status: 'pending_cancellation' as any, cancellationReason: reason } : inv
      );
      setInvoices(newInvoices);
      invoicesRef.current = newInvoices;
      setSales(calculateSalesFromInvoices(newInvoices, warehousesRef.current));
      
      showToast('تم تحويل الفاتورة إلى قيد الإلغاء بنجاح', 'success');
      fetchData(true); // Don't await to avoid UI freeze
    } catch (error: any) {
      showToast('خطأ: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmCancellation = async (id: string) => {
    setIsProcessing(true);
    try {
      const { data: sale, error: fetchError } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      
      if (!sale) {
        console.warn(`[StoreContext] confirmCancellation: Sale with ID ${id} not found. It might be already deleted or cancelled.`);
        await fetchData(true);
        return;
      }

      const items = sale.items || [];
      for (const item of items) {
        if (item.allocatedBatches && item.allocatedBatches.length > 0) {
          for (const batch of item.allocatedBatches) {
            if (batch.batchId) {
              const { data: currentBatch } = await supabase
                .from('products')
                .select('quantity')
                .eq('id', batch.batchId)
                .single();
              
              if (currentBatch) {
                await supabase
                  .from('products')
                  .update({ quantity: Number(currentBatch.quantity || 0) + Number(batch.quantity) })
                  .eq('id', batch.batchId);
              }
            }
          }
        }
      }

      const { error: cancelError } = await supabase
        .from('invoices')
        .update({ status: 'cancelled' })
        .eq('id', id);

      if (cancelError) throw cancelError;

      await fetchData(true);
      showToast("تم تأكيد الإلغاء واسترجاع المواد بنجاح", "success");
    } catch (err: any) {
      console.error("Confirm Cancellation Error:", err);
      showToast(err.message || "خطأ في تأكيد الإلغاء", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteSale = async (id: string) => {
    if (!id) {
      showToast("معرف الفاتورة مفقود", "error");
      return;
    }

    const cleanId = String(id).trim();
    setIsProcessing(true);
    try {
      const { data: sale, error: fetchError } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', cleanId)
        .maybeSingle();

      if (fetchError) {
        console.error("Fetch sale error:", fetchError);
        throw new Error(`خطأ في استرجاع بيانات الفاتورة: ${fetchError.message}`);
      }

      if (!sale) {
        console.warn(`[StoreContext] deleteSale: Sale with ID ${cleanId} not found. Refreshing data.`);
        await fetchData(true);
        showToast("لم يتم العثور على الفاتورة، قد تكون محذوفة بالفعل", "info");
        return;
      }

      const items = sale.items || [];
      for (const item of items) {
        if (item.allocatedBatches && item.allocatedBatches.length > 0) {
          for (const batch of item.allocatedBatches) {
            if (batch.batchId) {
              const { data: currentBatch } = await supabase
                .from('products')
                .select('quantity')
                .eq('id', batch.batchId)
                .maybeSingle();
              
              if (currentBatch) {
                await supabase
                  .from('products')
                  .update({ quantity: Number(currentBatch.quantity || 0) + Number(batch.quantity) })
                  .eq('id', batch.batchId);
              }
            }
          }
        }
      }

      const { error: deleteError } = await supabase
        .from('invoices')
        .delete()
        .eq('id', cleanId);

      if (deleteError) throw deleteError;

      await fetchData(true);
      showToast("تم حذف الفاتورة واسترجاع المواد بنجاح", "success");
    } catch (err: any) {
      console.error("Delete Sale Error:", err);
      showToast(err.message || "خطأ في حذف الفاتورة", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const addCommissionWithdrawal = async (withdrawal: CommissionWithdrawal) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('agent_orders').insert([{
        id: withdrawal.id,
        date: withdrawal.date,
        agent_id: withdrawal.centerId,
        warehouse_id: withdrawal.warehouseId || null,
        receiver_name: withdrawal.receiverName, // Save to dedicated column
        items: [{
          membershipNumber: withdrawal.membershipNumber,
          memberName: withdrawal.memberName,
          receiverName: withdrawal.receiverName,
          amountUSD: withdrawal.amountUSD,
          exchangeRate: withdrawal.exchangeRate,
          authImageUrl: withdrawal.authImageUrl
        }],
        total_points: 0,
        status: 'approved',
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        center_name: withdrawal.centerName,
        notes: `COMMISSION_WITHDRAWAL: ${withdrawal.membershipNumber} ${withdrawal.memberName}${withdrawal.receiverName ? ` (المستلم: ${withdrawal.receiverName})` : ''}`
      }]);

      if (error) throw error;

      await fetchData(true, 'agent_orders');
      showToast("تم سحب العمولة بنجاح", "success");
    } catch (err: any) {
      console.error(err);
      showToast(err.message || "خطأ في سحب العمولة", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleFlexibleOffer = async (saleId: string, isFlexible: boolean) => {
    try {
      await supabase.from('invoices').update({ type: isFlexible ? 'flexible' : 'cash' }).eq('id', saleId);
      await fetchData(true);
      showToast(isFlexible ? "تمت الإضافة للعرض المرن" : "تمت الإزالة من العرض المرن", "info");
    } catch (err) {
      console.error(err);
    }
  };

  const updateCommissionWithdrawal = async (withdrawal: CommissionWithdrawal) => {
    setCommissionWithdrawals(prev => prev.map(w => w.id === withdrawal.id ? withdrawal : w));
  };

  const addDeferredPoint = async (point: DeferredPoint) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('deferred_points').insert([{
        id: point.id,
        center_id: point.centerId,
        member_id: point.memberId,
        sale_id: point.saleId,
        product_id: point.productId,
        product_name: point.productName,
        points: point.points,
        quantity: point.quantity,
        release_date: point.releaseDate,
        status: point.status,
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }]);
      if (error) throw error;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateDeferredPoint = async (point: DeferredPoint) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('deferred_points').update({
        status: point.status,
        release_date: point.releaseDate
      }).eq('id', point.id);
      if (error) throw error;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const addExpense = async (expense: Expense) => {
    setIsProcessing(true);
    // Auto-pend internal transfers that target another center
    const isInternalTransferToCenter = expense.category === 'transfer_out' && expense.description.includes('[tgt:');
    
    // Optimistic Update
    const isActuallyBackdated = new Date(expense.date).toDateString() !== new Date().toDateString() && new Date(expense.date) < new Date();
    const isBackdated = isActuallyBackdated || isInternalTransferToCenter;
    const backdatedStatus = isBackdated ? 'pending' : 'approved';
    const completeExpense = { ...expense, isBackdated, backdatedStatus };
    setExpenses(prev => [...prev, completeExpense as any]);

    try {
      const { error } = await supabase.from('expenses').insert([{
        id: expense.id,
        center_id: expense.centerId,
        center_name: expense.centerName,
        user_id: expense.userId,
        user_name: expense.userName,
        category: expense.category,
        amount: expense.amount,
        amount_iqd: expense.amountIQD,
        currency: expense.currency,
        exchange_rate: expense.exchangeRate,
        description: expense.description,
        date: expense.date,
        is_backdated: isBackdated,
        backdated_status: backdatedStatus
      }]);
      if (error) throw error;
      
      const successMsg = isInternalTransferToCenter 
        ? 'تم تسجيل الحوالة وهي بانتظار موافقة الإدارة' 
        : (isBackdated ? 'تم تسجيل المصروف (بانتظار موافقة التاريخ الرجعي)' : 'تم تسجيل العملية بنجاح');
      showToast(successMsg, 'success');
      
      fetchData(true, 'expenses').catch(console.error); // Background fetch without await
    } catch (error: any) {
      // Revert optimistic
      setExpenses(prev => prev.filter(e => e.id !== expense.id));
      showToast('خطأ في العملية: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const updateExpenseStatus = async (id: string, status: 'approved' | 'rejected') => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('expenses').update({ backdated_status: status }).eq('id', id);
      if (error) throw error;
      showToast('تم تحديث حالة المصروف بنجاح', 'success');
      
      const expense = expenses.find(e => e.id === id);
      
      if (status === 'approved' && expense?.category === 'transfer_out' && expense.description.includes('[tgt:')) {
         const tgtMatch = expense.description.match(/\[tgt:(.*?)\]/);
         if (tgtMatch && tgtMatch[1]) {
            const tgtCenterId = tgtMatch[1];
            const tgtCenter = warehouses.find(w => String(w.id) === String(tgtCenterId)) || 
                              agents.find(a => String(a.id) === String(tgtCenterId));
                              
            // Create counter-expense
            if (tgtCenterId) {
               const transferInId = crypto.randomUUID();
               await supabase.from('expenses').insert([{
                 id: transferInId,
                 center_id: tgtCenterId,
                 center_name: tgtCenter?.name || 'مركز المستلم',
                 user_id: currentUser?.id,
                 user_name: currentUser?.name || 'مدير النظام (موافقة آليه)',
                 category: 'transfer_in',
                 amount: expense.amount,
                 amount_iqd: expense.amountIQD,
                 currency: expense.currency,
                 exchange_rate: expense.exchangeRate,
                 description: `[استلام حوالة داخلية من: ${expense.centerName}] ${expense.description.replace(/\[tgt:.*?\]/g, '')}`,
                 date: new Date().toISOString(),
                 is_backdated: false,
                 backdated_status: 'approved'
               }]);
               showToast('تم إيداع الحوالة في قاصة المركز المستهدف', 'success');
            }
         }
      }
      
      await fetchData(true);
    } catch (error: any) {
      showToast('خطأ في تحديث الحالة: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const updateSaleStatus = async (id: string, status: 'approved' | 'rejected') => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('invoices').update({ backdated_status: status }).eq('id', id);
      if (error) throw error;
      showToast('تم تحديث حالة الفاتورة بنجاح');
      await fetchData(true);
    } catch (error: any) {
      showToast('خطأ في تحديث الحالة: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const addDailyClosing = async (closing: DailyClosing) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('daily_closings').insert([{
        id: closing.id,
        center_id: closing.centerId,
        center_name: closing.centerName,
        user_id: closing.userId,
        user_name: closing.userName,
        date: closing.date,
        total_cash_usd: closing.totalCashUSD,
        total_flexible_usd: closing.totalFlexibleUSD,
        total_expenses_usd: closing.totalExpensesUSD,
        total_withdrawals_usd: closing.totalWithdrawalsUSD,
        net_cash_usd: closing.netCashUSD,
        daily_safe_usd: closing.dailySafeUSD,
        total_points: closing.totalPoints,
        exchange_rate: closing.exchangeRate,
        status: closing.status,
        closed_at: closing.closedAt,
        summary: closing.summary
      }]);
      if (error) throw error;

      // Transfer to main safe (Update system settings)
      const currentMainSafeUSD = Number(systemSettings['main_safe_usd'] || 0);
      await updateSystemSetting('main_safe_usd', currentMainSafeUSD + (closing.dailySafeUSD || 0));

      await fetchData(true);
      showToast("تم إغلاق الصندوق بنجاح وترحيل الرصيد إلى القاصة المركزية", "success");
    } catch (err: any) {
      console.error("Add Daily Closing Error:", err);
      showToast(err.message || "خطأ في إغلاق الصندوق", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteDailyClosing = async (id: string) => {
    setIsProcessing(true);
    try {
      const closing = dailyClosings.find(c => c.id === id);
      if (closing) {
        const currentMainSafeUSD = Number(systemSettings['main_safe_usd'] || 0);
        await updateSystemSetting('main_safe_usd', currentMainSafeUSD - (closing.dailySafeUSD || 0));
      }
      await supabase.from('daily_closings').delete().eq('id', id);
      await fetchData(true);
      showToast("تم حذف إغلاق الصندوق وإلغاء ترحيل الرصيد", "info");
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteExpense = async (id: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('expenses').delete().eq('id', id);
      if (error) throw error;
      showToast('تم حذف المصروف بنجاح');
      await fetchData(true);
    } catch (error: any) {
      showToast('خطأ في حذف المصروف: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const addFeedback = async (feedback: Feedback) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('user_feedback').insert([mapFeedbackToDB(feedback)]);
      if (error) throw error;
      await fetchData(true, 'user_feedback');
    } catch (err: any) {
      console.error("Add Feedback Error:", err);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteFeedback = async (id: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('user_feedback').delete().eq('id', id);
      if (error) throw error;
      await fetchData(true, 'user_feedback');
      showToast("تم الحذف بنجاح");
    } catch (err: any) {
      showToast(err.message || "خطأ في الحذف", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const addCompanyLetter = async (letter: CompanyLetter) => {
    setIsProcessing(true);
    try {
      // Ensure all fields are defined or null to avoid SQL serialization issues
      const letterData = {
        id: letter.id,
        letter_number: letter.letterNumber !== undefined ? letter.letterNumber : 0,
        formatted_number: letter.formattedNumber || null,
        date: letter.date || new Date().toISOString().split('T')[0],
        content: letter.content || '',
        text_blocks_json: letter.textBlocks ? JSON.stringify(letter.textBlocks) : null,
        background_image: letter.backgroundImage || null,
        stamp_image: letter.stampImage || null,
        layout_json: letter.layout ? JSON.stringify(letter.layout) : null,
        user_id: letter.userId || null,
        user_name: letter.userName || null,
        center_id: letter.centerId || null,
        center_name: letter.centerName || null
      };

      console.log("📤 Archiving Letter:", letterData.id, letterData.formatted_number);
      
      // Calculate total size to warn if it might hit proxy limits
      const totalSize = JSON.stringify(letterData).length;
      if (totalSize > 2 * 1024 * 1024) { // 2MB is a common default limit for some PHP proxies
        console.warn(`⚠️ Large report data: ${(totalSize / 1024 / 1024).toFixed(2)} MB. This may cause 500 Internal Server Errors in some environments.`);
      }

      const { error } = await supabase.from('company_letters').insert([letterData]);
      if (error) throw error;
      await fetchData(true, 'company_letters');
      showToast("تم أرشفة الكتاب بنجاح");

      // Send notifications for corporate correspondence
      try {
        const title = "كتاب رسمي جديد 📜";
        const message = `تم إصدار وأرشفة كتاب رسمي جديد برقم ${letter.formattedNumber || letter.id} من قبل ${currentUser?.name || "الإدارة"}.`;
        
        await sendNotificationToRoleOrWarehouse({ role: ['SUPER_ADMIN', 'EDITOR'] }, title, message, 'system');
        
        const { data: allProfiles } = await supabase.from('profiles').select('id');
        if (allProfiles) {
          for (const p of allProfiles) {
            if (p.id !== currentUser?.id) {
              await sendNotificationToUser(p.id, title, message, 'system');
            }
          }
        }
      } catch (notifErr) {
        console.error("Letter notification issue:", notifErr);
      }
    } catch (err: any) {
      console.error("Add Letter Error Details:", err);
      // More descriptive error for 500 proxy issues
      const errorMsg = err.message || "خطأ مجهول في الأرشفة";
      if (errorMsg.includes("500") || errorMsg.includes("PHP API")) {
        showToast("خطأ 500: حجم البيانات كبير جداً أو انتهت مهلة الاتصال بالخادم.", "error");
      } else {
        showToast(errorMsg, "error");
      }
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteCompanyLetter = async (id: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('company_letters').delete().eq('id', id);
      if (error) throw error;
      await fetchData(true, 'company_letters');
      showToast("تم حذف الكتاب من الأرشيف");
    } catch (err: any) {
      showToast(err.message || "خطأ في الحذف", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const addFlexibleOffer = async (offer: FlexibleOffer) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('flexible_offers').insert([{
        id: offer.id,
        name: offer.name,
        description: offer.description,
        type: offer.type,
        offer_category: offer.offerCategory,
        is_global: offer.isGlobal,
        center_id: offer.centerId,
        discount_percentage: offer.discountPercentage,
        bonus_points: offer.bonusPoints,
        start_date: offer.startDate,
        end_date: offer.endDate,
        is_active: offer.isActive,
        product_ids: offer.productIds || [],
        user_id: currentUser?.id,
        user_name: currentUser?.name
      }]);
      if (error) throw error;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateFlexibleOffer = async (offer: FlexibleOffer) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('flexible_offers').update({
        name: offer.name,
        description: offer.description,
        type: offer.type,
        offer_category: offer.offerCategory,
        is_global: offer.isGlobal,
        center_id: offer.centerId,
        discount_percentage: offer.discountPercentage,
        bonus_points: offer.bonusPoints,
        start_date: offer.startDate,
        end_date: offer.endDate,
        is_active: offer.isActive,
        product_ids: offer.productIds || []
      }).eq('id', offer.id);
      if (error) throw error;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteFlexibleOffer = async (id: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('flexible_offers').delete().eq('id', id);
      if (error) throw error;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const addOfferMember = async (member: OfferMember) => {
    setIsProcessing(true);
    try {
      const payload: any = {
        id: member.id,
        offer_id: member.offerId || null,
        name: member.name,
        phone: member.phone || null,
        membership_number: member.membershipNumber,
        auth_image_url: member.authImageUrl || null,
        type: member.type || 'offer',
        center_id: member.centerId || null,
        center_name: member.centerName || null,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        created_at: member.createdAt
      };

      let { error } = await supabase.from('offer_members').insert([payload]);

      // Fallback for older schemas missing new columns
      if (error && error.message.includes('Could not find the')) {
        console.warn("Schema mismatch detected. Falling back to base legacy columns...", error.message);
        const legacyPayload = {
          id: member.id,
          name: member.name,
          phone: member.phone || null,
          membership_number: member.membershipNumber,
          created_at: member.createdAt
        };
        const fallbackResult = await supabase.from('offer_members').insert([legacyPayload]);
        error = fallbackResult.error;
      }

      if (error) {
        console.error("Supabase error (offer_members):", error);
        throw new Error(error.message);
      }
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateOfferMember = async (member: OfferMember) => {
    setIsProcessing(true);
    try {
      const payload: any = {
        offer_id: member.offerId,
        name: member.name,
        phone: member.phone,
        membership_number: member.membershipNumber,
        auth_image_url: member.authImageUrl,
        type: member.type,
        center_id: member.centerId,
        center_name: member.centerName
      };

      let { error } = await supabase.from('offer_members').update(payload).eq('id', member.id);

      // Fallback for older schemas
      if (error && error.message.includes('Could not find the')) {
        console.warn("Schema mismatch detected. Falling back to base legacy columns for update...", error.message);
        const legacyPayload = {
          name: member.name,
          phone: member.phone,
          membership_number: member.membershipNumber
        };
        const fallbackResult = await supabase.from('offer_members').update(legacyPayload).eq('id', member.id);
        error = fallbackResult.error;
      }

      if (error) throw error;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const uploadMemberAuthImage = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `auth_${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;

      const { data, error: uploadError } = await supabase.storage
        .from('members')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const actualPath = data?.path || fileName;
      const { data: { publicUrl } } = supabase.storage
        .from('members')
        .getPublicUrl(actualPath);

      return { success: true, url: normalizeImageUrl(publicUrl) };
    } catch (err: any) {
      console.error(err);
      return { success: false, error: err.message };
    }
  };

  const uploadUserAsset = async (file: File, type: 'profile' | 'id_front' | 'id_back') => {
    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const randomId = Math.random().toString(36).substring(2, 10);
      const fileName = `${type}_${Date.now()}_${randomId}.${fileExt}`;

      const { data, error: uploadError } = await supabase.storage
        .from('employees')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (uploadError) {
        console.warn("Direct storage upload failed:", uploadError);
        throw uploadError;
      }

      const actualPath = data?.path || fileName;
      const { data: { publicUrl } } = supabase.storage
        .from('employees')
        .getPublicUrl(actualPath);

      return { success: true, url: normalizeImageUrl(publicUrl) };
    } catch (err: any) {
      console.error("User asset upload error details:", err);
      return { success: false, error: "فشل رفع الملف إلى السيرفر. يرجى التحقق من الاتصال أو حجم الملف." };
    }
  };

  const deleteOfferMember = async (id: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase.from('offer_members').delete().eq('id', id);
      if (error) throw error;
      await fetchData(true);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const uploadProductImage = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `product_${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
      const { data, error: uploadError } = await supabase.storage.from('products').upload(fileName, file);
      
      if (uploadError) {
        console.error("Product storage upload error:", uploadError);
        return { success: false, error: uploadError.message || 'خطأ غير معروف في السيرفر' };
      }

      const actualPath = data?.path || fileName;
      const { data: { publicUrl } } = supabase.storage.from('products').getPublicUrl(actualPath);
      return { success: true, url: normalizeImageUrl(publicUrl) };
    } catch (err: any) { 
      console.error('Critical product image upload error:', err);
      return { success: false, error: err.message || 'خطأ فني في الرفع' }; 
    }
  };

  const uploadSystemAsset = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop() || 'png';
      const fileName = `system_${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
      
      const { data, error: uploadError } = await supabase.storage.from('system').upload(fileName, file);
      
      if (uploadError) {
        console.error("System storage upload error:", uploadError);
        return { success: false, error: uploadError.message || 'خطأ غير معروف في السيرفر' };
      }

      const actualPath = data?.path || fileName;
      const { data: { publicUrl } } = supabase.storage.from('system').getPublicUrl(actualPath);
      return { success: true, url: normalizeImageUrl(publicUrl) };
    } catch (err: any) { 
      console.error('Critical system asset upload error:', err);
      return { success: false, error: err.message || 'خطأ فني في الرفع' }; 
    }
  };

  const updateSystemSetting = async (key: string, value: any, silent = false) => {
    if (!silent) setIsProcessing(true);
    try {
      setSystemSettings(prev => ({ ...prev, [key]: value }));
      const { error } = await supabase
        .from('system_settings')
        .upsert({ key, value }, { onConflict: 'key' });
      if (error) throw error;
    } catch (err: any) {
      console.error("[StoreContext] updateSystemSetting error:", err);
      if (!silent) showToast(err.message || "خطأ في تحديث الإعدادات", "error");
    } finally {
      if (!silent) setIsProcessing(false);
    }
  };

  const uploadIntroVideo = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop() || 'mp4';
      const fileName = `intro_${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage.from('videos').upload(fileName, file);
      if (uploadError) throw uploadError;
      const actualPath = uploadData?.path || fileName;
      const { data: { publicUrl } } = supabase.storage.from('videos').getPublicUrl(actualPath);
      const normalizedUrl = normalizeImageUrl(publicUrl);
      await supabase.from('system_settings').upsert({ key: 'intro_video_url', value: normalizedUrl }, { onConflict: 'key' });
      setIntroVideo(normalizedUrl);
      showToast('تم تحديث الفيديو للجميع بنجاح');
      return { success: true, url: normalizedUrl };
    } catch (err: any) { 
      console.error("Video upload error:", err);
      return { success: false, error: err.message }; 
    }
  };

  const deleteIntroVideo = async () => {
    setIsProcessing(true);
    try { 
      await supabase.from('system_settings').delete().eq('key', 'intro_video_url'); 
      setIntroVideo(null); 
      showToast('تم حذف الفيديو التعريفي', 'info'); 
      return { success: true }; 
    }
    catch (err: any) { return { success: false, error: err.message }; }
    finally {
      setIsProcessing(false);
    }
  };

  const updateExchangeRate = async (rate: number, warehouseId?: string) => {
    setIsProcessing(true);
    try {
      if (warehouseId) {
        const { error } = await supabase
          .from('warehouses')
          .update({ exchange_rate: rate })
          .eq('id', warehouseId);
        if (error) throw error;
        setWarehouses(prev => prev.map(w => w.id === warehouseId ? { ...w, exchangeRate: rate } : w));
        showToast("تم تحديث سعر الصرف للمركز بنجاح");
      } else {
        await updateSystemSetting('global_exchange_rate', rate);
        showToast("تم تحديث سعر الصرف العالمي بنجاح");
      }
      await fetchData(true);
    } catch (err: any) {
      console.error("Update Exchange Rate Error:", err);
      showToast(err.message || "فشل في تحديث سعر الصرف", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const getExchangeRate = useCallback((centerId?: string) => {
    const globalRate = Number(systemSettings['global_exchange_rate'] || 1500);
    if (!centerId) return globalRate;
    
    const centerWarehouse = warehouses.find(w => String(w.id) === String(centerId) || String(w.agentId) === String(centerId));
    if (centerWarehouse && centerWarehouse.exchangeRate) {
      return centerWarehouse.exchangeRate;
    }
    
    return globalRate;
  }, [systemSettings, warehouses]);

  const refreshStockPredictions = async () => {
    const { getStockPredictions } = await import('../services/aiService');
    const bId = warehouses.find(w => normalizeArabic(w.name).includes('بغداد') && w.type === 'GENERAL')?.id;
    const eId = warehouses.find((w) => (normalizeArabic(w.name).includes('اربيل') || normalizeArabic(w.name).includes('أربيل')) && w.type === 'GENERAL')?.id;
    const whIds = [bId, eId].filter(id => id !== undefined) as string[];
    const predictions = await getStockPredictions(products, sales, invoices, whIds);
    setStockPredictions(predictions);
  };

  const value = useMemo(() => ({ 
    currentUser, isAuthenticated, users, loading, isSyncing, isProcessing, setIsProcessing, toast, notifications, showToast, addNotification, clearNotifications, markNotificationAsRead, markChatNotificationsAsRead, sendNotificationToUser, sendNotificationToRoleOrWarehouse, stopAlarm, viewInvoiceId, setViewInvoiceId,
    selectedDetail, setSelectedDetail,
    login, faceLogin, logout, addUser, updateUser, deleteUser, registerFace,
    agents, warehouses, products, invoices, ordersWarehouses, approvedInvoices, matchInvoices, productInvoices, transfers, sales, attendance, leaveRequests, offerMembers, commissionWithdrawals, deferredPoints, flexibleOffers, expenses, dailyClosings, feedbacks, activityLogs, memberPoints, companyLetters, introVideo, systemSettings, updateSystemSetting, roleAppearances, warehouseLocks,
    addAgent, updateAgent, deleteAgent, addWarehouse, updateWarehouse, deleteWarehouse, addProduct, addProductsBatch, updateProduct, deleteProduct,
    addInvoice, shipInvoice, receiveInvoice, cancelInvoice, updateInvoice, deleteInvoice, addApprovedInvoice, addMatchInvoice, addProductInvoice, updateRoleAppearance,
    addTransfer, updateTransfer, approveTransfer, deleteTransfer, addSale, deleteSale, requestCancellation, confirmCancellation, addAttendance, updateAttendance, deleteAttendance, addLeaveRequest, updateLeaveRequest, deleteLeaveRequest, addCommissionWithdrawal, updateCommissionWithdrawal, addDeferredPoint, updateDeferredPoint, addFlexibleOffer, updateFlexibleOffer, deleteFlexibleOffer, addOfferMember, updateOfferMember, deleteOfferMember, addExpense, deleteExpense, updateExpenseStatus, updateSaleStatus, addDailyClosing, deleteDailyClosing, addFeedback, deleteFeedback, addCompanyLetter, deleteCompanyLetter, convertDeferredToCash, uploadMemberAuthImage, toggleFlexibleOffer, deleteProductInvoice, deleteMatchInvoice, deleteApprovedInvoice, getExchangeRate,
    uploadProductImage, uploadIntroVideo, deleteIntroVideo, updateExchangeRate, acquireWarehouseLock, releaseWarehouseLock, forceReleaseLock, refreshData, fetchData, printHTML, generateInvoiceId,
    stockPredictions, refreshStockPredictions, uploadUserAsset, uploadSystemAsset,
    welcomeUser, setWelcomeUser,
    isChatOpen, setIsChatOpen,
    activeChatUserId, setActiveChatUserId,
    unreadChatCount, setUnreadChatCount
  }), [
    currentUser, isAuthenticated, users, loading, isSyncing, isProcessing, toast, notifications, viewInvoiceId, setViewInvoiceId,
    selectedDetail, setSelectedDetail,
    login, faceLogin, logout, addUser, updateUser, deleteUser, registerFace,
    agents, warehouses, products, invoices, ordersWarehouses, approvedInvoices, matchInvoices, productInvoices, transfers, sales, attendance, leaveRequests, offerMembers, commissionWithdrawals, deferredPoints, flexibleOffers, expenses, dailyClosings, feedbacks, activityLogs, memberPoints, companyLetters, introVideo, systemSettings, updateSystemSetting, roleAppearances, warehouseLocks,
    addAgent, updateAgent, deleteAgent, addWarehouse, updateWarehouse, deleteWarehouse, addProduct, addProductsBatch, updateProduct, deleteProduct,
    addInvoice, shipInvoice, receiveInvoice, cancelInvoice, updateInvoice, deleteInvoice, addApprovedInvoice, addMatchInvoice, addProductInvoice, updateRoleAppearance,
    addTransfer, updateTransfer, approveTransfer, deleteTransfer, addSale, deleteSale, requestCancellation, confirmCancellation, addAttendance, updateAttendance, deleteAttendance, addLeaveRequest, updateLeaveRequest, deleteLeaveRequest, addCommissionWithdrawal, updateCommissionWithdrawal, addDeferredPoint, updateDeferredPoint, addFlexibleOffer, updateFlexibleOffer, deleteFlexibleOffer, addOfferMember, updateOfferMember, deleteOfferMember, addExpense, deleteExpense, updateExpenseStatus, updateSaleStatus, addDailyClosing, deleteDailyClosing, addFeedback, deleteFeedback, addCompanyLetter, deleteCompanyLetter, convertDeferredToCash, uploadMemberAuthImage, toggleFlexibleOffer, deleteProductInvoice, deleteMatchInvoice, deleteApprovedInvoice, getExchangeRate,
    uploadProductImage, uploadIntroVideo, deleteIntroVideo, updateExchangeRate, acquireWarehouseLock, releaseWarehouseLock, forceReleaseLock, refreshData, fetchData, printHTML, generateInvoiceId,
    stockPredictions, refreshStockPredictions, uploadUserAsset, uploadSystemAsset,
    welcomeUser, setWelcomeUser,
    isChatOpen, setIsChatOpen,
    activeChatUserId, setActiveChatUserId,
    unreadChatCount
  ]);

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore must be used within a StoreProvider');
  return context;
};
